import React, { useState, useEffect, useRef } from 'react';
import { Home, Users, Settings, BookOpen, Calendar, BarChart3, Newspaper, Image, LogIn, LogOut, User, Menu, X } from 'lucide-react';
import { supabase } from './lib/supabase';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const isInitialLoadRef = useRef(true);
  const [classes, setClasses] = useState([]);
  const [news, setNews] = useState([]);
  const [classSessions, setClassSessions] = useState([]); // Actual sessions (from class_sessions table)
  const [weeklyTemplates, setWeeklyTemplates] = useState([]); // Weekly template (from weekly_schedule_template table)
  const [mantras, setMantras] = useState([]); // Monthly mantras
  const [asanas, setAsanas] = useState([]); // Monthly asanas  
  const [currentMantra, setCurrentMantra] = useState(null); // Current month's mantra
  const [currentAsana, setCurrentAsana] = useState(null); // Current month's asana
  const [teachers, setTeachers] = useState([]); // eslint-disable-line no-unused-vars
  const [sessionBookings, setSessionBookings] = useState({}); // Bookings per session: { sessionId: [bookings] }
  const [userBookings, setUserBookings] = useState([]); // Current user's bookings
  const [userSubscriptions, setUserSubscriptions] = useState([]); // Current user's active subscriptions
  const [allSubscriptions, setAllSubscriptions] = useState([]); // Admin: all user subscriptions
  const [subscriptionPlans, setSubscriptionPlans] = useState([]); // Admin: available subscription plans
  const [showAssignSubscriptionModal, setShowAssignSubscriptionModal] = useState(false);
  const [selectedUserForSubscription, setSelectedUserForSubscription] = useState(null);
  // Subscription filters
  const [subscriptionFilterPlan, setSubscriptionFilterPlan] = useState(''); // Filter by plan
  const [subscriptionFilterUser, setSubscriptionFilterUser] = useState(''); // Filter by user name/email
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState('');
  const [loginCredentials, setLoginCredentials] = useState({ email: '', password: '' });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showFirstTimePasswordSetup, setShowFirstTimePasswordSetup] = useState(false);
  const [firstTimePassword, setFirstTimePassword] = useState({ password: '', confirmPassword: '' });
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const [completeProfileData, setCompleteProfileData] = useState({
    full_name: '',
    phone: '',
    user_type: 'student',
    subscription_type: 'basic'
  });
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetPassword, setResetPassword] = useState({ password: '', confirmPassword: '' });
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [profileCompletionData, setProfileCompletionData] = useState({
    profile_picture: null,
    bio: ''
  });
  const [monthlyContent] = useState({
    mantra: {
      title: 'Mahá Mrityumjaya',
      lines: [
        'ÔM trayambakam yajamahê',
        'Sugandhim pushtivardhanam',
        'Urvárukamiva bandhanán',
        'Mrityôrmukshiya mámritát'
      ]
    },
    asana: {
      name: 'Paschimottanasana',
      description: 'A postura da flexão sentada para a frente - uma postura fundamental que alonga toda a coluna vertebral, isquiotibiais e músculos das costas, promovendo calma e introspecção.',
      image: '/asana-of-the-month.jpg'
    }
  });
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subscriptionType: '',
    expirationDate: '',
    profilePicture: null
  });
  const [showClassModal, setShowClassModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classDetails, setClassDetails] = useState({
    id: '',
    name: '',
    description: '',
    teacher: '',
    location: '',
    isOnline: false,
    zoomLink: '',
    bookedStudents: [],
    sessionDate: '',
    startTime: '',
    endTime: ''
  });
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState({
    id: '',
    title: '',
    content: '',
    date: '',
    image: ''
  });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [showDaySessionsModal, setShowDaySessionsModal] = useState(false); // Yogi day sessions modal
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [showEditClassModal, setShowEditClassModal] = useState(false);
  const [selectedClassToEdit, setSelectedClassToEdit] = useState(null);
  const [selectedClassFilter, setSelectedClassFilter] = useState(null); // Filter for bookings calendar
  const [newClassForm, setNewClassForm] = useState({
    name: '',
    teacher: '',
    description: ''
  });
  const [showTeacherAssignmentModal, setShowTeacherAssignmentModal] = useState(false);
  const [teacherClassAssignments, setTeacherClassAssignments] = useState([]); // All teacher-class assignments
  const [selectedClassForTeachers, setSelectedClassForTeachers] = useState(null); // Selected class type
  
  // Monthly Content modals and forms
  const [showMantraModal, setShowMantraModal] = useState(false);
  const [showAsanaModal, setShowAsanaModal] = useState(false);
  const [showMantraHistoryModal, setShowMantraHistoryModal] = useState(false);
  const [showAsanaHistoryModal, setShowAsanaHistoryModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calendarType, setCalendarType] = useState('mantra'); // 'mantra' or 'asana'
  const [isEditingMantra, setIsEditingMantra] = useState(false);
  const [isEditingAsana, setIsEditingAsana] = useState(false);
  const [mantraForm, setMantraForm] = useState({
    title: '',
    description: '',
    audioFile: null,
    audioUrl: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [asanaForm, setAsanaForm] = useState({
    title: '',
    sanskritName: '',
    description: '',
    benefits: '',
    instructions: '',
    imageFile: null,
    imageUrl: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [selectedMantra, setSelectedMantra] = useState(null);
  const [selectedAsana, setSelectedAsana] = useState(null);
  
  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);
  
  const [dashboardStats, setDashboardStats] = useState({
    totalYogis: 0,
    mostAttendedClass: { name: '', count: 0 },
    leastAttendedClass: { name: '', count: 0 },
    top3Yogis: [],
    birthdays: []
  });
  const [users, setUsers] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserOperationLoading, setIsUserOperationLoading] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    full_name: '',
    birth_date: '',
    user_type: 'student' // Default to student
  });
  const [showAddSessionModal, setShowAddSessionModal] = useState(false);
  const [showEditSessionModal, setShowEditSessionModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [activeScheduleTab, setActiveScheduleTab] = useState('template'); // 'template' or 'calendar'
  const [calendarMonth, setCalendarMonth] = useState(new Date()); // Current month being viewed
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(null); // For admin calendar day selection
  const [showDayModal, setShowDayModal] = useState(false); // Modal showing sessions for a specific day
  const [sessionForm, setSessionForm] = useState({
    class_id: '',
    teacher_id: '',
    session_date: '',
    start_time: '',
    end_time: '',
    max_participants: 10,
    is_online: false,
    location: 'Estúdio Bhāva',
    zoom_link: '',
    notes: '',
    is_template: false,
    day_of_week: 1
  });

  // Calendar utilities
  const getMonthCalendarDays = (isoDateStr) => {
    const baseDate = new Date(isoDateStr);
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth(); // 0-11

    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);

    // We want weeks to start on Monday. getDay(): 0=Sun ... 6=Sat
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7; // 0=Mon ... 6=Sun
    const daysInMonth = lastOfMonth.getDate();

    // Start date is the Monday on or before the 1st
    const startDate = new Date(firstOfMonth);
    startDate.setDate(firstOfMonth.getDate() - firstWeekday);

    // We render 6 rows * 7 days = 42 cells to keep a fixed grid
    const totalCells = 42;
    const days = [];
    for (let i = 0; i < totalCells; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      days.push(d);
    }
    return { days, month, year };
  };

  const toIsoDate = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);

  // Check if a session has already started or passed (considering date + time)
  const isSessionPast = (sessionDate, startTime) => {
    const now = new Date();
    const sessionDateTime = new Date(`${sessionDate}T${startTime}`);
    return sessionDateTime <= now;
  };

  const getIsoFromSession = (s) => (typeof s.session_date === 'string' ? s.session_date.slice(0,10) : new Date(s.session_date).toISOString().slice(0,10));

  const getSessionsForDateIso = (isoDate) => {
    return classSessions
      .filter((s) => {
        if (s.is_template) return false; // Exclude templates
        const sessionIso = getIsoFromSession(s);
        return sessionIso === isoDate;
      })
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  // Navigate calendar month
  const goToPreviousMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCalendarMonth(new Date());
  };

  // Handle clicking on a day in the calendar
  const handleDayClick = (date) => {
    if (!date) return;
    setCalendarSelectedDate(date);
    setShowDayModal(true);
  };

  // Close day modal
  const closeDayModal = () => {
    setShowDayModal(false);
    setCalendarSelectedDate(null);
  };

  // Add session for specific date from calendar
  const addSessionForDate = (date) => {
    const dateStr = toIsoDate(date);
    setSessionForm({
      class_id: classes.length > 0 ? classes[0].id : '',
      teacher_id: teachers.length > 0 ? teachers[0].id : '',
      session_date: dateStr,
      start_time: '',
      end_time: '',
      max_participants: 10,
      is_online: false,
      location: 'Estúdio Bhāva',
      zoom_link: '',
      notes: '',
      is_template: false,
      day_of_week: 1
    });
    setShowDayModal(false);
    setShowAddSessionModal(true);
  };

  // Check authentication on app load
  useEffect(() => {
    const initializeApp = async () => {
      // Check for password recovery token in URL
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      
      if (type === 'recovery') {
        console.log('🔒 Password recovery detected - showing password reset form...');
        
        // IMPORTANT: DO NOT sign out!
        // Supabase creates a temporary recovery session from the token in the URL
        // This session is needed to call updateUser({ password })
        // The recovery session is limited and can ONLY update the password, nothing else
        
        // Clear React state (but keep the recovery session active)
        setUser(null);
        setUserType(null);
        setUserBookings([]);
        setUserSubscriptions([]);
        
        // Show password reset form
        setShowPasswordReset(true);
        setLoading(false);
        
        // Mark initial load as complete (skip checkUser)
        isInitialLoadRef.current = false;
        
        console.log('🔐 Recovery session active, showing password reset form');
        return; // Don't call checkUser - we want a clean slate
      }

      // Check user session on initial load
      console.log('🚀 Initial app load - checking session...');
      await checkUser();
      
      // Mark initial load as complete
      console.log('✅ Initial load complete, enabling auth state listener');
      isInitialLoadRef.current = false;
    };

    initializeApp();

    // Listen for auth state changes (login/logout in other tabs, session expiry)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state changed:', event, '| isInitialLoad:', isInitialLoadRef.current);
      
      // Ignore SIGNED_IN during initial load (checkUser handles it)
      if (event === 'SIGNED_IN' && isInitialLoadRef.current) {
        console.log('⏭️ Ignoring SIGNED_IN during initial load (handled by checkUser)');
        return;
      }
      
      if (event === 'SIGNED_IN' && !isInitialLoadRef.current && session) {
        // Check if this is a password recovery session
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type');
        
        if (type === 'recovery') {
          console.log('🔐 Password recovery SIGNED_IN detected - showing password reset form');
          // Don't set user state or fetch profile - keep password reset modal open
          return;
        }
        
        console.log('🔄 Cross-tab login detected, restoring session...');
        // User signed in - fetch their profile
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();
        
        if (userProfile) {
          setUser({
            id: userProfile.id,
            email: session.user.email,
            name: userProfile.full_name || session.user.email,
            authenticated: true
          });
          setUserType(userProfile.user_type);
          console.log('✅ User signed in via cross-tab:', userProfile.user_type);
        }
      } else if (event === 'SIGNED_OUT') {
        // User signed out - clear state
        console.log('🔄 Sign out detected, clearing state...');
        setUser(null);
        setUserType(null);
        setUserBookings([]);
        setUserSubscriptions([]);
        console.log('✅ User signed out');
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('✅ Token refreshed - session extended');
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription?.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch users when admin views users page
  useEffect(() => {
    if (userType === 'admin' && currentView === 'users') {
      fetchUsers();
    }
  }, [userType, currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch teacher class assignments when admin views classes page
  useEffect(() => {
    if (userType === 'admin' && currentView === 'classes') {
      fetchTeacherClassAssignments();
      fetchUsers(); // Also fetch users to show teachers
      fetchTeachers(); // Fetch actual teachers from database
    }
  }, [userType, currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch teachers when admin views schedule page
  useEffect(() => {
    if (userType === 'admin' && currentView === 'schedule') {
      fetchTeachers(); // Fetch teachers for weekly template dropdown
      fetchUsers(); // Also fetch users for teacher names
      fetchTeacherClassAssignments(); // For filtering qualified teachers
    }
  }, [userType, currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch subscriptions when admin views subscriptions page
  useEffect(() => {
    if (userType === 'admin' && currentView === 'subscriptions') {
      fetchAllSubscriptions();
      fetchSubscriptionPlans();
      fetchUsers(); // Need users list for assignment
    }
  }, [userType, currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch bookings and refresh sessions when day modal opens
  useEffect(() => {
    if (showDayModal && calendarSelectedDate) {
      // Refresh session data to get latest current_participants counts
      fetchClassSessions();
      
      // Fetch detailed bookings for each session
      const sessionsForDay = getSessionsForDateIso(toIsoDate(calendarSelectedDate));
      sessionsForDay.forEach(session => {
        fetchSessionBookings(session.id);
      });
    }
  }, [showDayModal, calendarSelectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkUser = async () => {
    console.log('🔍 Starting checkUser...');
    try {
      // Check for existing Supabase auth session
      console.log('📡 Checking Supabase session...');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('❌ Session error:', sessionError);
      setLoading(false);
        return;
      }

      if (session && session.user) {
        console.log('✅ Existing session found, restoring user...', session.user.id);
        
        // Fetch user profile
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();

        if (profileError) {
          console.error('❌ Profile fetch error:', profileError);
          await supabase.auth.signOut();
          setLoading(false);
          return;
        }

        if (!userProfile) {
          console.log('⚠️ Profile not found, logging out...');
          await supabase.auth.signOut();
          setLoading(false);
          return;
        }

        console.log('✅ Profile found:', userProfile.user_type);

        // Restore user state
        setUser({
          id: userProfile.id,
          email: session.user.email,
          name: userProfile.full_name || session.user.email,
          authenticated: true
        });
        setUserType(userProfile.user_type);

        // Fetch data based on user type
        try {
          // Fetch data sequentially with timeouts to identify hanging queries
          await Promise.race([fetchClasses(), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchClasses timeout')), 5000))]);
          await Promise.race([fetchNews(), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchNews timeout')), 5000))]);
          await Promise.race([fetchMantras(), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchMantras timeout')), 5000))]);
          await Promise.race([fetchAsanas(), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchAsanas timeout')), 5000))]);
          await Promise.race([fetchClassSessions(), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchClassSessions timeout')), 5000))]);
          await Promise.race([fetchWeeklyTemplates(), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchWeeklyTemplates timeout')), 5000))]);
          await Promise.race([fetchTeachers(), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchTeachers timeout')), 5000))]);
          
          if (userProfile.user_type === 'admin') {
            await Promise.race([fetchDashboardStats(), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchDashboardStats timeout')), 5000))]);
            await Promise.race([fetchUsers(), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchUsers timeout')), 5000))]);
          }
          
          if (userProfile.user_type === 'student') {
            await Promise.race([fetchProfileFromSupabase(session.user.email), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchProfile timeout')), 5000))]);
            await Promise.race([fetchUserBookings(userProfile.id), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchBookings timeout')), 5000))]);
            await Promise.race([fetchUserSubscriptions(userProfile.id), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchSubscriptions timeout')), 5000))]);
          } else if (userProfile.user_type === 'teacher') {
            // Teachers also need to fetch their bookings (when booking as student)
            await Promise.race([fetchUserBookings(userProfile.id), new Promise((_, reject) => setTimeout(() => reject(new Error('fetchBookings timeout')), 5000))]);
          }
        } catch (fetchError) {
          console.error('⚠️ Error fetching data (non-critical):', fetchError);
          // Continue anyway - data fetch errors shouldn't block login
        }

        console.log('✅ Session restored successfully:', userProfile.user_type);
      } else {
        console.log('ℹ️ No existing session found, loading public data...');
        // Load public data
        try {
      await Promise.all([
        fetchClasses(),
        fetchNews(),
        fetchMantras(),
        fetchAsanas(),
        fetchClassSessions(),
            fetchWeeklyTemplates(),
        fetchTeachers()
      ]);
          console.log('✅ Public data loaded');
        } catch (fetchError) {
          console.error('⚠️ Error fetching public data (non-critical):', fetchError);
          // Continue anyway
        }
      }
      
      console.log('✅ checkUser complete, setting loading=false');
      setLoading(false);
    } catch (error) {
      console.error('❌ CRITICAL Error in checkUser:', error);
      console.error('Error stack:', error.stack);
      // Always set loading to false, even on error
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase.from('classes').select('*');
      if (error) throw error;
      setClasses(data || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setClasses([
        { id: 1, name: 'Yôga', description: 'Prática de yoga tradicional', max_participants: 15 },
        { id: 2, name: 'Pilates', description: 'Fortalecimento e flexibilidade', max_participants: 12 },
        { id: 3, name: 'Consciência Somática', description: 'Movimento consciente', max_participants: 10 },
        { id: 4, name: 'GAP & HIIT', description: 'Treino intensivo', max_participants: 20 }
      ]);
    }
  };

  const fetchNews = async () => {
    try {
      const { data, error } = await supabase.from('news').select('*');
      if (error) throw error;
      setNews(data || []);
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews([
        { id: 1, title: 'Nova turma de Yôga', content: 'Inscrições abertas para nova turma...', date: '2024-01-15' },
        { id: 2, title: 'Workshop de Meditação', content: 'Workshop especial no próximo sábado...', date: '2024-01-10' }
      ]);
    }
  };

  const fetchMantras = async () => {
    try {
      const { data, error} = await supabase
        .from('monthly_mantras')
        .select('*')
        .eq('is_active', true)
        .order('year', { ascending: false })
        .order('month', { ascending: false });
      if (error) throw error;
      setMantras(data || []);
      
      // Set current month's mantra
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const current = data?.find(m => m.month === currentMonth && m.year === currentYear);
      setCurrentMantra(current || null);
    } catch (error) {
      console.error('Error fetching mantras:', error);
      setMantras([]);
      setCurrentMantra(null);
    }
  };

  const fetchAsanas = async () => {
    try {
      const { data, error } = await supabase
        .from('monthly_asanas')
        .select('*')
        .eq('is_active', true)
        .order('year', { ascending: false })
        .order('month', { ascending: false });
      if (error) throw error;
      setAsanas(data || []);
      
      // Set current month's asana
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const current = data?.find(a => a.month === currentMonth && a.year === currentYear);
      setCurrentAsana(current || null);
    } catch (error) {
      console.error('Error fetching asanas:', error);
      setAsanas([]);
      setCurrentAsana(null);
    }
  };

  // Helper function for month names
  const getMonthName = (month) => {
    const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    return months[month - 1] || '';
  };

  // ================== MANTRA MANAGEMENT ==================

  const openAddMantraModal = () => {
    setIsEditingMantra(false);
    setSelectedMantra(null);
    setMantraForm({
      title: '',
      description: '',
      audioFile: null,
      audioUrl: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    });
    setShowMantraModal(true);
  };

  const openEditMantraModal = (mantra) => {
    setIsEditingMantra(true);
    setSelectedMantra(mantra);
    setMantraForm({
      title: mantra.title,
      description: mantra.description || '',
      audioFile: null,
      audioUrl: mantra.audio_url || '',
      month: mantra.month,
      year: mantra.year
    });
    setShowMantraModal(true);
  };

  const closeMantraModal = () => {
    // Stop recording if active
    if (isRecording && mediaRecorder) {
      mediaRecorder.stop();
    }
    // Reset states
    setShowMantraModal(false);
    setIsEditingMantra(false);
    setSelectedMantra(null);
    setIsRecording(false);
    setMediaRecorder(null);
    setRecordedAudio(null);
    setMantraForm({
      title: '',
      description: '',
      audioFile: null,
      audioUrl: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    });
  };

  const handleMantraFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/m4a'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a)$/i)) {
        alert('Por favor, selecione um arquivo de áudio válido (MP3, WAV, M4A)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('O arquivo é muito grande. Tamanho máximo: 10MB');
        return;
      }
      setMantraForm({ ...mantraForm, audioFile: file });
    }
  };

  const handleSaveMantra = async () => {
    if (!mantraForm.title.trim()) {
      alert('Por favor, preencha o título do mantra');
      return;
    }

    try {
      let audioUrl = mantraForm.audioUrl;

      if (mantraForm.audioFile) {
        const reader = new FileReader();
        audioUrl = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(mantraForm.audioFile);
        });
      }

      const mantraData = {
        title: mantraForm.title,
        description: mantraForm.description,
        audio_url: audioUrl,
        month: mantraForm.month,
        year: mantraForm.year,
        created_by: user?.id || null,
        updated_at: new Date().toISOString()
      };

      if (isEditingMantra && selectedMantra) {
        const { error } = await supabase
          .from('monthly_mantras')
          .update(mantraData)
          .eq('id', selectedMantra.id);

        if (error) throw error;
        alert('Mantra atualizado com sucesso!');
      } else {
        const { error } = await supabase
          .from('monthly_mantras')
          .insert([mantraData]);

        if (error) {
          if (error.code === '23505') {
            alert('Já existe um mantra para este mês. Por favor, edite o existente ou escolha outro mês.');
          } else {
            throw error;
          }
          return;
        }
        alert('Mantra adicionado com sucesso!');
      }

      closeMantraModal();
      await fetchMantras();
    } catch (error) {
      console.error('Error saving mantra:', error);
      alert('Erro ao salvar mantra: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleDeleteMantra = async (mantra) => {
    if (!window.confirm(`Tem certeza que deseja remover o mantra "${mantra.title}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('monthly_mantras')
        .delete()
        .eq('id', mantra.id);

      if (error) throw error;

      alert('Mantra removido com sucesso!');
      await fetchMantras();
    } catch (error) {
      console.error('Error deleting mantra:', error);
      alert('Erro ao remover mantra: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // ================== AUDIO RECORDING ==================

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const audioChunks = [];

      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], `recording-${Date.now()}.webm`, { type: 'audio/webm' });
        setRecordedAudio(URL.createObjectURL(audioBlob));
        setMantraForm({ ...mantraForm, audioFile: audioFile });
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Erro ao acessar microfone. Verifique as permissões do navegador.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const clearRecording = () => {
    setRecordedAudio(null);
    setMantraForm({ ...mantraForm, audioFile: null });
  };

  // ================== ASANA MANAGEMENT ==================

  const openAddAsanaModal = () => {
    setIsEditingAsana(false);
    setSelectedAsana(null);
    setAsanaForm({
      title: '',
      sanskritName: '',
      description: '',
      benefits: '',
      instructions: '',
      imageFile: null,
      imageUrl: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    });
    setShowAsanaModal(true);
  };

  const openEditAsanaModal = (asana) => {
    setIsEditingAsana(true);
    setSelectedAsana(asana);
    setAsanaForm({
      title: asana.title,
      sanskritName: asana.sanskrit_name || '',
      description: asana.description || '',
      benefits: asana.benefits || '',
      instructions: asana.instructions || '',
      imageFile: null,
      imageUrl: asana.image_url || '',
      month: asana.month,
      year: asana.year
    });
    setShowAsanaModal(true);
  };

  const closeAsanaModal = () => {
    setShowAsanaModal(false);
    setIsEditingAsana(false);
    setSelectedAsana(null);
    setAsanaForm({
      title: '',
      sanskritName: '',
      description: '',
      benefits: '',
      instructions: '',
      imageFile: null,
      imageUrl: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    });
  };

  const handleAsanaFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Por favor, selecione uma imagem válida (JPG, PNG, WEBP)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('A imagem é muito grande. Tamanho máximo: 5MB');
        return;
      }
      setAsanaForm({ ...asanaForm, imageFile: file });
    }
  };

  const handleSaveAsana = async () => {
    if (!asanaForm.title.trim()) {
      alert('Por favor, preencha o título do asana');
      return;
    }

    try {
      let imageUrl = asanaForm.imageUrl;

      if (asanaForm.imageFile) {
        const reader = new FileReader();
        imageUrl = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(asanaForm.imageFile);
        });
      }

      const asanaData = {
        title: asanaForm.title,
        sanskrit_name: asanaForm.sanskritName,
        description: asanaForm.description,
        benefits: asanaForm.benefits,
        instructions: asanaForm.instructions,
        image_url: imageUrl,
        month: asanaForm.month,
        year: asanaForm.year,
        created_by: user?.id || null,
        updated_at: new Date().toISOString()
      };

      if (isEditingAsana && selectedAsana) {
        const { error } = await supabase
          .from('monthly_asanas')
          .update(asanaData)
          .eq('id', selectedAsana.id);

        if (error) throw error;
        alert('Asana atualizado com sucesso!');
      } else {
        const { error } = await supabase
          .from('monthly_asanas')
          .insert([asanaData]);

        if (error) {
          if (error.code === '23505') {
            alert('Já existe um asana para este mês. Por favor, edite o existente ou escolha outro mês.');
          } else {
            throw error;
          }
          return;
        }
        alert('Asana adicionado com sucesso!');
      }

      closeAsanaModal();
      await fetchAsanas();
    } catch (error) {
      console.error('Error saving asana:', error);
      alert('Erro ao salvar asana: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleDeleteAsana = async (asana) => {
    if (!window.confirm(`Tem certeza que deseja remover o asana "${asana.title}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('monthly_asanas')
        .delete()
        .eq('id', asana.id);

      if (error) throw error;

      alert('Asana removido com sucesso!');
      await fetchAsanas();
    } catch (error) {
      console.error('Error deleting asana:', error);
      alert('Erro ao remover asana: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const fetchClassSessions = async () => {
    try {
      const { data, error } = await supabase.from('class_sessions').select(`
        *,
        current_participants,
        max_participants,
        classes(name, description, duration_minutes),
        teachers(bio, specialties)
      `);
      if (error) throw error;
      console.log('📊 Fetched sessions with participant counts:', data?.slice(0, 2)); // Log first 2 for debugging
      setClassSessions(data || []);
    } catch (error) {
      console.error('Error fetching class sessions:', error);
      // Demo data based on the real schedule
      setClassSessions([
        // Monday
        { id: 1, session_date: '2024-01-15', start_time: '09:30:00', end_time: '10:30:00', is_online: false, notes: 'Inês Carreira - Presencial', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 60 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 2, session_date: '2024-01-15', start_time: '09:30:00', end_time: '10:30:00', is_online: true, notes: 'Inês Carreira - Online', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 60 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 3, session_date: '2024-01-15', start_time: '19:15:00', end_time: '20:15:00', is_online: true, notes: 'Paulo Anjos - EXCLUSIVO ONLINE', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 60 }, teachers: { bio: 'Professor de Yoga Online', specialties: ['Yoga Online'] } },
        // Tuesday
        { id: 4, session_date: '2024-01-16', start_time: '07:00:00', end_time: '08:00:00', is_online: false, notes: 'Inês Carreira - Presencial', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 60 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 5, session_date: '2024-01-16', start_time: '07:00:00', end_time: '08:00:00', is_online: true, notes: 'Inês Carreira - Online', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 60 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 6, session_date: '2024-01-16', start_time: '08:15:00', end_time: '08:45:00', is_online: false, notes: 'Inês Carreira - Presencial', max_participants: 20, classes: { name: 'MEDITAÇÃO', description: 'Prática de meditação e mindfulness', duration_minutes: 30 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 7, session_date: '2024-01-16', start_time: '08:15:00', end_time: '08:45:00', is_online: true, notes: 'Inês Carreira - Online', max_participants: 20, classes: { name: 'MEDITAÇÃO', description: 'Prática de meditação e mindfulness', duration_minutes: 30 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 8, session_date: '2024-01-16', start_time: '09:30:00', end_time: '10:30:00', is_online: false, notes: 'Vanessa Marsland', max_participants: 12, classes: { name: 'GAP', description: 'Glúteos, Abdominais e Pernas', duration_minutes: 60 }, teachers: { bio: 'Instrutora de Fitness', specialties: ['GAP', 'HITT'] } },
        { id: 9, session_date: '2024-01-16', start_time: '13:00:00', end_time: '13:45:00', is_online: false, notes: 'Joana Moreira - NEW', max_participants: 12, classes: { name: 'PILATES', description: 'Fortalecimento e flexibilidade', duration_minutes: 45 }, teachers: { bio: 'Instrutora de Pilates', specialties: ['Pilates'] } },
        { id: 10, session_date: '2024-01-16', start_time: '19:15:00', end_time: '20:30:00', is_online: false, notes: 'Inês Carreira - Presencial - 75 min', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 75 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 11, session_date: '2024-01-16', start_time: '19:15:00', end_time: '20:30:00', is_online: true, notes: 'Inês Carreira - Online - 75 min', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 75 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        // Wednesday
        { id: 12, session_date: '2024-01-17', start_time: '09:30:00', end_time: '10:15:00', is_online: false, notes: 'Joana Moreira - NEW', max_participants: 12, classes: { name: 'PILATES', description: 'Fortalecimento e flexibilidade', duration_minutes: 45 }, teachers: { bio: 'Instrutora de Pilates', specialties: ['Pilates'] } },
        { id: 13, session_date: '2024-01-17', start_time: '19:15:00', end_time: '20:15:00', is_online: true, notes: 'Paulo Anjos - EXCLUSIVO ONLINE', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 60 }, teachers: { bio: 'Professor de Yoga Online', specialties: ['Yoga Online'] } },
        // Thursday
        { id: 14, session_date: '2024-01-18', start_time: '08:00:00', end_time: '09:00:00', is_online: false, notes: 'Vanessa Marsland', max_participants: 15, classes: { name: 'HITT', description: 'High Intensity Interval Training', duration_minutes: 60 }, teachers: { bio: 'Instrutora de Fitness', specialties: ['GAP', 'HITT'] } },
        { id: 15, session_date: '2024-01-18', start_time: '10:15:00', end_time: '11:00:00', is_online: false, notes: 'Joana Moreira - NEW', max_participants: 12, classes: { name: 'PILATES', description: 'Fortalecimento e flexibilidade', duration_minutes: 45 }, teachers: { bio: 'Instrutora de Pilates', specialties: ['Pilates'] } },
        { id: 16, session_date: '2024-01-18', start_time: '19:15:00', end_time: '20:30:00', is_online: false, notes: 'Inês Carreira - Presencial - 75 min', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 75 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 17, session_date: '2024-01-18', start_time: '19:15:00', end_time: '20:30:00', is_online: true, notes: 'Inês Carreira - Online - 75 min', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 75 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        // Friday
        { id: 18, session_date: '2024-01-19', start_time: '07:00:00', end_time: '08:00:00', is_online: false, notes: 'Inês Carreira - Presencial', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 60 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 19, session_date: '2024-01-19', start_time: '07:00:00', end_time: '08:00:00', is_online: true, notes: 'Inês Carreira - Online', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 60 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 20, session_date: '2024-01-19', start_time: '08:15:00', end_time: '08:45:00', is_online: false, notes: 'Inês Carreira - Presencial', max_participants: 20, classes: { name: 'MEDITAÇÃO', description: 'Prática de meditação e mindfulness', duration_minutes: 30 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 21, session_date: '2024-01-19', start_time: '08:15:00', end_time: '08:45:00', is_online: true, notes: 'Inês Carreira - Online', max_participants: 20, classes: { name: 'MEDITAÇÃO', description: 'Prática de meditação e mindfulness', duration_minutes: 30 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 22, session_date: '2024-01-19', start_time: '09:00:00', end_time: '10:00:00', is_online: false, notes: 'Inês Carreira - Presencial', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 60 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 23, session_date: '2024-01-19', start_time: '09:00:00', end_time: '10:00:00', is_online: true, notes: 'Inês Carreira - Online', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 60 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 24, session_date: '2024-01-19', start_time: '17:00:00', end_time: '17:45:00', is_online: false, notes: 'Joana Moreira - NEW', max_participants: 12, classes: { name: 'PILATES', description: 'Fortalecimento e flexibilidade', duration_minutes: 45 }, teachers: { bio: 'Instrutora de Pilates', specialties: ['Pilates'] } },
        // Saturday
        { id: 25, session_date: '2024-01-20', start_time: '10:30:00', end_time: '12:00:00', is_online: false, notes: 'Inês Carreira - Presencial - 90 min', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 90 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } },
        { id: 26, session_date: '2024-01-20', start_time: '10:30:00', end_time: '12:00:00', is_online: true, notes: 'Inês Carreira - Online - 90 min', max_participants: 15, classes: { name: 'YOGA', description: 'Prática de yoga tradicional', duration_minutes: 90 }, teachers: { bio: 'Professora de Yoga e Meditação', specialties: ['Yoga', 'Meditação'] } }
      ]);
    }
  };

  const fetchWeeklyTemplates = async () => {
    try {
      const { data, error } = await supabase.from('weekly_schedule_template').select(`
        *,
        classes(name, description, duration_minutes),
        teachers(
          id,
          bio,
          specialties,
          user_profile:user_profiles!user_id(
            id,
            email,
            full_name
          )
        )
      `).eq('is_active', true);
      if (error) throw error;
      setWeeklyTemplates(data || []);
    } catch (error) {
      console.error('Error fetching weekly templates:', error);
      setWeeklyTemplates([]);
    }
  };

  const fetchSessionBookings = async (sessionId) => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          user_profiles(full_name, email)
        `)
        .eq('class_session_id', sessionId)
        .in('status', ['confirmed', 'completed']);
      
      if (error) throw error;
      
      setSessionBookings(prev => ({
        ...prev,
        [sessionId]: data || []
      }));
      
      return data || [];
    } catch (error) {
      console.error('Error fetching session bookings:', error);
      return [];
    }
  };

  const fetchUserBookings = async (userId = null) => {
    try {
      const targetUserId = userId || user?.id;
      
      if (!targetUserId) {
        setUserBookings([]);
        return;
      }

      const { data, error } = await supabase
        .from('bookings')
        .select('id, class_session_id, status')
        .eq('user_id', targetUserId)
        .in('status', ['confirmed', 'completed']);
      
      if (error) throw error;
      
      // Store as array of session IDs for easy lookup
      setUserBookings(data || []);
      console.log('✅ User bookings fetched:', data?.length || 0, 'for user:', targetUserId);
    } catch (error) {
      console.error('Error fetching user bookings:', error);
      setUserBookings([]);
    }
  };

  const fetchUserSubscriptions = async (userId = null) => {
    try {
      const targetUserId = userId || user?.id;
      
      if (!targetUserId) {
        setUserSubscriptions([]);
        return;
      }

      // Call the database function to get subscription summary
      const { data, error } = await supabase
        .rpc('get_user_subscription_summary', { 
          p_user_id: targetUserId 
        });
      
      if (error) throw error;
      
      setUserSubscriptions(data || []);
      console.log('✅ User subscriptions fetched:', data?.length || 0, 'for user:', targetUserId);
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      setUserSubscriptions([]);
    }
  };

  const fetchAllSubscriptions = async () => {
    try {
      // Get all user subscriptions with user info
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select(`
          *,
          user_profiles!inner(full_name, email),
          subscription_plans!inner(display_name, plan_group, class_type, weekly_limit, total_credits)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setAllSubscriptions(data || []);
      console.log('✅ All subscriptions fetched:', data?.length || 0);
    } catch (error) {
      console.error('Error fetching all subscriptions:', error);
      setAllSubscriptions([]);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      
      if (error) throw error;
      
      setSubscriptionPlans(data || []);
      console.log('✅ Subscription plans fetched:', data?.length || 0);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      setSubscriptionPlans([]);
    }
  };

  const fetchTeachers = async () => {
    try {
      // Fetch teachers with their user profile data
      // Using explicit foreign key syntax: user_profiles!user_id
      const { data, error } = await supabase
        .from('teachers')
        .select(`
          *,
          user_profile:user_profiles!user_id(
            id,
            email,
            full_name
          )
        `)
        .eq('is_active', true);
      
      if (error) throw error;
      setTeachers(data || []);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      // Fallback to empty array instead of hardcoded data
      setTeachers([]);
    }
  };

  // Fetch bookings for a specific session (for admin view)
  const fetchBookingsForSession = async (sessionId) => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          user_id,
          status,
          booking_date,
          user_profiles:user_id (
            id,
            full_name,
            email,
            profile_picture
          )
        `)
        .eq('class_session_id', sessionId)
        .eq('status', 'confirmed');
      
      if (error) throw error;
      
      // Transform the data to match the expected format
      return (data || []).map(booking => {
        const profile = booking.user_profiles;
        const nameParts = profile?.full_name?.split(' ') || [];
        const initials = nameParts.length >= 2 
          ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
          : profile?.full_name?.[0]?.toUpperCase() || '?';
        
        return {
          name: profile?.full_name || 'Utilizador desconhecido',
          initials: initials,
          picture: profile?.profile_picture || null,
          email: profile?.email || '',
          bookingId: booking.id
        };
      });
    } catch (error) {
      console.error('Error fetching bookings for session:', error);
      // Return mock data for demo
      return [
        { name: 'Emma Silva', initials: 'ES', picture: null },
        { name: 'João Costa', initials: 'JC', picture: null },
        { name: 'Maria Brás', initials: 'MB', picture: null }
      ];
    }
  };

  const fetchDashboardStats = async () => {
    try {
      // Fetch total yogis count
      const { count: yogisCount } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('user_type', 'student');
      
      // Fetch birthdays from user_profiles
      const today = new Date();
      const todayMonth = today.getMonth() + 1; // JavaScript months are 0-indexed
      const todayDay = today.getDate();
      
      // Get dates for next 3 days
      const targetDates = [today];
      for (let i = 1; i <= 3; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        targetDates.push(date);
      }

      // Fetch all students with birth_date
      const { data: students, error: studentsError } = await supabase
        .from('user_profiles')
        .select('full_name, birth_date')
        .eq('user_type', 'student')
        .not('birth_date', 'is', null);

      let birthdays = [];
      
      if (!studentsError && students && students.length > 0) {
        // Filter birthdays for today and next 3 days
        birthdays = students
          .map(student => {
            if (!student.birth_date) return null;
            
            const birthDate = new Date(student.birth_date);
            const birthMonth = birthDate.getMonth() + 1;
            const birthDay = birthDate.getDate();
            
            // Find matching date in target dates
            const matchingDate = targetDates.find(date => {
              const dateMonth = date.getMonth() + 1;
              const dateDay = date.getDate();
              return dateMonth === birthMonth && dateDay === birthDay;
            });
            
            if (matchingDate) {
              return {
                name: student.full_name,
                date: matchingDate,
                isToday: matchingDate.getTime() === today.getTime()
              };
            }
            return null;
          })
          .filter(b => b !== null)
          .sort((a, b) => {
            // Sort by date, today first
            if (a.isToday) return -1;
            if (b.isToday) return 1;
            return a.date.getTime() - b.date.getTime();
          });
      }

      // TODO: Fetch most/least attended classes from bookings data
      // TODO: Fetch top 3 most assiduous yogis from bookings data

      setDashboardStats({
        totalYogis: yogisCount || 120,
        mostAttendedClass: { name: 'Yôga', count: 45 },
        leastAttendedClass: { name: 'GAP & HIIT', count: 12 },
        top3Yogis: [
          { name: 'Emma Silva', count: 28 },
          { name: 'Pedro Costa', count: 25 },
          { name: 'Ana Santos', count: 23 }
        ],
        birthdays: birthdays
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Fallback to mock data
      const today = new Date();
      const next3Days = [];
      for (let i = 1; i <= 3; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        next3Days.push(date);
      }

      setDashboardStats({
        totalYogis: 120,
        mostAttendedClass: { name: 'Yôga', count: 45 },
        leastAttendedClass: { name: 'GAP & HIIT', count: 12 },
        top3Yogis: [
          { name: 'Emma Silva', count: 28 },
          { name: 'Pedro Costa', count: 25 },
          { name: 'Ana Santos', count: 23 }
        ],
        birthdays: [
          { name: 'Maria Silva', date: today, isToday: true },
          { name: 'João Santos', date: next3Days[0], isToday: false },
          { name: 'Ana Costa', date: next3Days[1], isToday: false }
        ]
      });
    }
  };

  // Handle first time password setup
  const handleFirstTimePasswordSetup = async (email, password) => {
    try {
      // Try to use the SQL function first, but fallback to password reset if it doesn't exist
      let passwordSet = false;
      
      try {
        const { data: functionResult, error: functionError } = await supabase.rpc(
          'set_user_password',
          {
            user_email: email,
            new_password: password
          }
        );

        if (!functionError) {
          passwordSet = true;
        } else if (functionError?.message?.includes('function') || functionError?.message?.includes('does not exist')) {
          // Function doesn't exist, use fallback method
          throw new Error('FUNCTION_NOT_FOUND');
        } else {
          throw functionError;
        }
      } catch (funcError) {
        // Fallback: Use password reset flow
        // Request a password reset, which will send an email
        // Note: This requires the user to click the link in the email
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`
        });

        if (resetError) {
          // If reset doesn't work, try to sign up again with new password
          // This might work if user was created but not confirmed
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: email,
            password: password,
            options: {
              emailRedirectTo: undefined
            }
          });

          if (signUpError) {
            // Last resort: Show instructions
            alert('⚠️ Não foi possível definir a senha automaticamente.\n\n' +
                  'Por favor, execute o script set-user-password-function.sql no Supabase SQL Editor.\n\n' +
                  'Alternativamente, use o fluxo de recuperação de senha:\n' +
                  '1. Vá para "Esqueceu a senha?"\n' +
                  '2. Digite seu email\n' +
                  '3. Verifique seu email e clique no link\n' +
                  '4. Defina uma nova senha');
            return;
          }

          // If signUp succeeds, user was created/updated
          // Now try to sign in
          passwordSet = true;
        } else {
          // Password reset email sent
          alert('✅ Email de recuperação de senha enviado!\n\n' +
                'Por favor, verifique seu email e clique no link para definir sua senha.\n\n' +
                'Depois de definir a senha, você poderá fazer login.');
          setShowFirstTimePasswordSetup(false);
          setFirstTimePassword({ password: '', confirmPassword: '' });
          return;
        }
      }

      if (passwordSet) {
        // Try to sign in with the new password
        // Note: This might not work immediately if using signUp fallback
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: email,
          password: password
        });

        if (signInError) {
          // If sign in fails, it might be because user needs to confirm email
          // or password wasn't set yet
          alert('⚠️ Senha pode ter sido definida, mas não foi possível fazer login automaticamente.\n\n' +
                'Por favor, tente fazer login novamente com o email e a senha que definiu.\n\n' +
                'Se ainda não funcionar, use o fluxo de recuperação de senha.');
          setShowFirstTimePasswordSetup(false);
          setFirstTimePassword({ password: '', confirmPassword: '' });
          return;
        }

        // Get user profile to determine user type
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('email', email)
          .single();

        if (profile) {
          setUser({ id: profile.id, email, name: profile.full_name, authenticated: true });
          setUserType(profile.user_type);
          setShowLoginForm(false);
          setShowFirstTimePasswordSetup(false);
          setFirstTimePassword({ password: '', confirmPassword: '' });
          
          await Promise.all([
            fetchClasses(),
            fetchNews(),
            fetchClassSessions(),
            fetchWeeklyTemplates(),
            fetchTeachers(),
            ...(profile.user_type === 'admin' ? [fetchDashboardStats(), fetchUsers()] : []),
            ...(profile.user_type === 'student' ? [fetchProfileFromSupabase(email)] : [])
          ]);
        }
      }
    } catch (error) {
      console.error('Error setting up password:', error);
      alert('Erro ao definir senha: ' + (error.message || 'Erro desconhecido') + 
            '\n\n💡 Dica: Execute o script set-user-password-function.sql no Supabase SQL Editor para habilitar a definição de senha sem email.');
    }
  };

  const handleLogin = async (email, password, type) => {
    try {
      // Validate input
      if (!email || !password) {
        alert('Por favor, preencha email e senha.');
        return;
      }

      // Try to authenticate with Supabase (encrypted passwords)
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      if (authError) {
        console.error('Auth error:', authError);
        
        // Wrong credentials or user doesn't exist
        alert('❌ Credenciais inválidas!\n\nVerifique email e senha.');
        return;
      }

      // Successful authentication - fetch user profile by ID (not email, for RLS)
      const userId = authData.user.id;
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      // If profile doesn't exist, show "Complete Profile" form
      if (profileError || !userProfile) {
        console.log('📝 First time login - profile not found. Showing complete profile form.');
        setShowLoginForm(false);
        setShowCompleteProfile(true);
        setCompleteProfileData({
          full_name: authData.user.user_metadata?.full_name || '',
          phone: '',
          user_type: 'student',
          subscription_type: 'basic'
        });
        // Store auth data temporarily for profile creation
        window.tempAuthUserId = userId;
        window.tempAuthEmail = email;
        return;
      }

      // Set user state with ID and user type
      setUser({
        id: userProfile.id,
        email: email,
        name: userProfile.full_name || email,
        authenticated: true
      });
      setUserType(userProfile.user_type);
        setShowLoginForm(false);
      setShowFirstTimePasswordSetup(false);

      // Fetch data based on user type
        await Promise.all([
          fetchClasses(),
          fetchNews(),
          fetchClassSessions(),
        fetchTeachers(),
        ...(userProfile.user_type === 'admin' ? [fetchDashboardStats(), fetchUsers()] : []),
        ...(userProfile.user_type === 'student' ? [fetchProfileFromSupabase(email), fetchUserBookings(userProfile.id), fetchUserSubscriptions(userProfile.id)] : []),
        ...(userProfile.user_type === 'teacher' ? [fetchUserBookings(userProfile.id)] : [])
      ]);

      console.log('✅ Login successful:', userProfile.user_type);
    } catch (error) {
      console.error('Login error:', error);
      alert('❌ Erro no login: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Forgot Password Handler - SECURE VERSION using database function
  const handleForgotPassword = async () => {
    try {
      if (!forgotPasswordEmail || !forgotPasswordEmail.trim()) {
        alert('Por favor, insira o seu email.');
        return;
      }

      const emailInput = forgotPasswordEmail.trim();
      console.log('🔍 [STEP 1] Checking if email can recover password:', emailInput);

      // Call secure database function that only returns boolean
      // This prevents email enumeration and doesn't expose user data
      const { data: canRecover, error: checkError } = await supabase
        .rpc('can_recover_password', { email_input: emailInput });

      console.log('🔐 [STEP 2] Can recover result:', canRecover);
      console.log('🔐 [STEP 2] Check error:', checkError);

      if (checkError) {
        console.error('❌ Error checking email:', checkError);
        throw checkError;
      }

      // If function returns FALSE, email doesn't exist OR is admin
      if (!canRecover) {
        console.log('❌ [STEP 3] Email cannot recover (not found or admin)');
        alert('❌ Email não encontrado ou não autorizado para recuperação.\n\nSe é administrador, contacte o suporte técnico.\nCaso contrário, verifique se o email está correto.');
        return;
      }

      console.log('✅ [STEP 3] Email can recover - sending reset email');

      // Send password reset email
      // Note: We use the user's input email (normalized)
      const { data: resetData, error: resetError } = await supabase.auth.resetPasswordForEmail(
        emailInput,
        {
          redirectTo: `${window.location.origin}/#reset-password`,
        }
      );

      console.log('📧 [STEP 4] Reset response data:', resetData);
      console.log('📧 [STEP 4] Reset response error:', resetError);

      if (resetError) {
        console.error('❌ [STEP 5] Reset error:', resetError);
        throw resetError;
      }

      console.log('✅ [STEP 5] Password reset email sent successfully!');
      alert('✅ Email de recuperação enviado!\n\nVerifique a sua caixa de entrada e siga as instruções para redefinir a sua senha.');
      
      // Close modal and reset form
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      console.error('💥 [ERROR] Forgot password error:', error);
      alert('❌ Erro ao enviar email de recuperação: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    
    try {
      const userId = window.tempAuthUserId;
      const email = window.tempAuthEmail;
      
      if (!userId || !email) {
        alert('❌ Erro: Sessão expirada. Por favor, faça login novamente.');
        setShowCompleteProfile(false);
        setShowLoginForm(true);
        return;
      }

      if (!completeProfileData.full_name.trim()) {
        alert('❌ Por favor, preencha o nome completo.');
        return;
      }

      // Create profile in user_profiles table
      const { error: insertError } = await supabase
        .from('user_profiles')
        .insert({
          id: userId,
          email: email,
          full_name: completeProfileData.full_name.trim(),
          phone: completeProfileData.phone.trim() || null,
          user_type: completeProfileData.user_type,
          subscription_type: completeProfileData.subscription_type
        });

      if (insertError) {
        console.error('Profile creation error:', insertError);
        alert('❌ Erro ao criar perfil: ' + (insertError.message || 'Erro desconhecido'));
        return;
      }

      console.log('✅ Profile created successfully!');
      
      // Clean up temp data
      delete window.tempAuthUserId;
      delete window.tempAuthEmail;
      
      // Set user state
      setUser({
        id: userId,
        email: email,
        name: completeProfileData.full_name,
        authenticated: true
      });
      setUserType(completeProfileData.user_type);
      setShowCompleteProfile(false);

      // Fetch data based on user type
        await Promise.all([
          fetchClasses(),
          fetchNews(),
          fetchClassSessions(),
          fetchTeachers(),
        ...(completeProfileData.user_type === 'admin' ? [fetchDashboardStats(), fetchUsers()] : []),
        ...(completeProfileData.user_type === 'student' ? [fetchProfileFromSupabase(email), fetchUserBookings(userId), fetchUserSubscriptions(userId)] : [])
      ]);

      alert('✅ Perfil criado com sucesso!\n\nBem-vindo ao Bhāva! 🙏');
    } catch (error) {
      console.error('Complete profile error:', error);
      alert('❌ Erro ao completar perfil: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleProfileCompletion = async (e) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('Sessão expirada. Por favor, faça login novamente.');
        setShowProfileCompletion(false);
        setShowLoginForm(true);
              return;
            }

      // Validate bio
      if (!profileCompletionData.bio || profileCompletionData.bio.trim().length < 10) {
        alert('Por favor, escreva uma biografia com pelo menos 10 caracteres.');
        return;
      }

      // For now, we'll allow profile_picture to be optional (they can add it later)
      // In a future version, you can integrate Supabase Storage for actual file uploads
      
      // Update user profile
      const { error } = await supabase
        .from('user_profiles')
        .update({
          bio: profileCompletionData.bio.trim(),
          profile_picture: profileCompletionData.profile_picture || null
        })
        .eq('id', user.id);

      if (error) throw error;

      console.log('✅ Profile completed successfully');
      alert('✅ Perfil completo!\n\nPode agora fazer login.');
      
      setShowProfileCompletion(false);
      setProfileCompletionData({ profile_picture: null, bio: '' });
      setShowLoginForm(true);
    } catch (error) {
      console.error('Profile completion error:', error);
      alert('❌ Erro ao completar perfil: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    try {
      if (!resetPassword.password || !resetPassword.confirmPassword) {
        alert('Por favor, preencha todos os campos.');
          return;
        }

      if (resetPassword.password.length < 6) {
        alert('A senha deve ter pelo menos 6 caracteres.');
        return;
      }
      
      if (resetPassword.password !== resetPassword.confirmPassword) {
        alert('As senhas não coincidem. Por favor, verifique.');
        return;
      }

      console.log('🔐 Updating password for reset user...');
      
      // Update password using Supabase
      const { error, data } = await supabase.auth.updateUser({
        password: resetPassword.password
      });

      if (error) throw error;

      console.log('✅ Password updated successfully for user:', data.user.id);
      console.log('📧 User email:', data.user.email);
      
      // CRITICAL: Ensure we're using the NEW user's session
      // Get fresh session after password update
      const { data: { session: newSession } } = await supabase.auth.getSession();
      
      if (!newSession || newSession.user.id !== data.user.id) {
        console.error('⚠️ Session mismatch detected!');
        alert('⚠️ Erro de sessão. Por favor, faça login manualmente com a nova senha.');
        setShowPasswordReset(false);
        setResetPassword({ password: '', confirmPassword: '' });
        window.location.hash = '';
        return;
      }
      
      console.log('✅ Session verified for correct user');
      
      // Check if user profile needs completion (missing photo/bio)
      const userId = data.user.id;
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('profile_picture, bio, email, full_name')
        .eq('id', userId)
        .maybeSingle();

      // If profile exists but missing photo/bio, this is a first-time user
      const isFirstTimeUser = userProfile && (!userProfile.profile_picture || !userProfile.bio);

      if (isFirstTimeUser) {
        console.log('🎉 First-time user detected, sending welcome email...');
        
        // Send welcome email (don't wait for it, continue flow)
        try {
          await fetch('/api/send-welcome-email', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userEmail: userProfile.email || data.user.email,
              userName: userProfile.full_name || 'Yogi'
            })
          });
          console.log('✅ Welcome email sent successfully');
        } catch (emailError) {
          // Don't block user flow if email fails
          console.error('⚠️ Failed to send welcome email (non-critical):', emailError);
        }

        setShowPasswordReset(false);
        setResetPassword({ password: '', confirmPassword: '' });
        window.location.hash = '';
        
        // Show profile completion modal
        setShowProfileCompletion(true);
        alert('✅ Senha alterada com sucesso!\n\n' +
              '📧 Email de boas-vindas enviado!\n\n' +
              '📝 Por favor, complete o seu perfil com uma foto e uma breve biografia.');
      } else {
        // Profile complete (returning user), show login
        alert('✅ Senha alterada com sucesso!\n\nPode agora fazer login com a nova senha.');
        
        // Clear URL hash and close modal
        window.location.hash = '';
        setShowPasswordReset(false);
        setResetPassword({ password: '', confirmPassword: '' });
        
        // Show login form
        setShowLoginForm(true);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      alert('❌ Erro ao alterar senha: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Fetch profile data from Supabase (user_profiles + active subscription)
  const fetchProfileFromSupabase = async (email) => {
    try {
      if (!email) return;

      // Get user profile by email
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, email, full_name')
        .eq('email', email)
        .maybeSingle();

      if (profileError) throw profileError;

      let firstName = '';
      let lastName = '';
      if (profile?.full_name) {
        const parts = profile.full_name.trim().split(/\s+/);
        firstName = parts.shift() || '';
        lastName = parts.join(' ');
      }

      // Get active subscription for this user
      let subscriptionType = '';
      let expirationDate = '';
      if (profile?.id) {
        const { data: activeSub, error: subErr } = await supabase
          .from('user_subscriptions')
          .select('id, subscription_plan_id, end_date, is_active')  // ✅ Fixed: subscription_plan_id
          .eq('user_id', profile.id)
          .eq('is_active', true)
          .order('end_date', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (subErr) {
          console.error('Error fetching user subscription:', subErr);
          // Don't throw - just skip subscription data
        } else if (activeSub?.subscription_plan_id) {
          const { data: sub, error: planErr } = await supabase
            .from('subscription_plans')  // ✅ Fixed: subscription_plans
            .select('display_name')  // ✅ Fixed: display_name instead of name
            .eq('id', activeSub.subscription_plan_id)
            .maybeSingle();
          if (!planErr && sub) {
            subscriptionType = sub.display_name || '';
            expirationDate = activeSub.end_date || '';
          }
        }
      }

      setProfileData((prev) => ({
        ...prev,
        firstName: firstName || prev.firstName,
        lastName: lastName || prev.lastName,
        email: profile?.email || prev.email || email,
        subscriptionType: subscriptionType || prev.subscriptionType,
        expirationDate: expirationDate || prev.expirationDate,
      }));
    } catch (err) {
      console.error('Erro ao carregar o perfil da Supabase:', err);
    }
  };

  const handleLogout = async () => {
    try {
      // Sign out from Supabase (clears session from localStorage)
      await supabase.auth.signOut();
      
      // Clear React state
    setUser(null);
    setUserType(null);
      setUserBookings([]);
      setUserSubscriptions([]);
      
      console.log('✅ Logout successful');
    } catch (error) {
      console.error('Logout error:', error);
      // Clear state anyway
      setUser(null);
      setUserType(null);
    }
  };

  const openLoginForm = (userType) => {
    setSelectedUserType(userType);
    setShowLoginForm(true);
    // Clear credentials for security
    setLoginCredentials({ email: '', password: '' });
  };

  const submitLogin = async () => {
    // If password is empty, check if user exists and show first time setup
    if (!loginCredentials.password || loginCredentials.password.trim() === '') {
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('email', loginCredentials.email)
        .maybeSingle();

      if (userProfile) {
        // User exists, show first time password setup
        setShowFirstTimePasswordSetup(true);
        return;
      } else {
        alert('Por favor, insira uma senha ou verifique se o email está correto.');
        return;
      }
    }
    
    handleLogin(loginCredentials.email, loginCredentials.password, selectedUserType);
  };

  // Profile handling functions
  const handleProfilePictureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileData(prev => ({
          ...prev,
          profilePicture: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileUpdate = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('Nenhum utilizador autenticado.');
        return;
      }

      // Compose full_name from editable first/last name
      const fullName = `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim();

      // Update user_profiles by user ID
      const { error } = await supabase
        .from('user_profiles')
        .update({ 
          full_name: fullName,
          profile_picture: profileData.profilePicture // Include profile picture
        })
        .eq('id', user.id);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      alert('Perfil atualizado com sucesso!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(`Erro ao salvar perfil: ${error.message || 'Erro desconhecido'}`);
    }
  };

  // Class booking modal handler
  const openClassModal = async (sessionId, sessionName, sessionDetails) => {
    // If admin, fetch all bookings for this session
    let bookedStudents = sessionDetails.bookedStudents || [];
    if (userType === 'admin') {
      bookedStudents = await fetchBookingsForSession(sessionId);
    }
    
    setClassDetails({
      id: sessionId,
      name: sessionName,
      description: sessionDetails.description || 'Uma aula completa e revigorante de Yôga',
      teacher: sessionDetails.teacher || 'Prof. Inês Carreira',
      location: sessionDetails.location || 'Sala 1',
      isOnline: sessionDetails.isOnline || false,
      zoomLink: sessionDetails.zoomLink || '',
      bookedStudents: bookedStudents,
      sessionDate: sessionDetails.sessionDate || '',
      startTime: sessionDetails.startTime || '',
      endTime: sessionDetails.endTime || ''
    });
    setShowClassModal(true);
  };

  const closeClassModal = () => {
    setShowClassModal(false);
    setSelectedClass(null);
  };

  // Helper function to check if user has booked a session
  const isSessionBooked = (sessionId) => {
    return userBookings.some(booking => 
      booking.class_session_id === sessionId && 
      (booking.status === 'confirmed' || booking.status === 'completed')
    );
  };

  // Cancel a booking
  const handleCancelBooking = async () => {
    if (!user) {
      alert('Por favor, faça login.');
      return;
    }

    if (!classDetails.id) {
      alert('Erro: Sessão inválida.');
      return;
    }

    const confirmCancel = window.confirm('Tem certeza que deseja cancelar esta reserva?');
    if (!confirmCancel) return;

    try {
      // Find the user's booking for this session
      const { data: existingBooking, error: findError } = await supabase
        .from('bookings')
        .select('id')
        .eq('class_session_id', classDetails.id)
        .eq('user_id', user.id)
        .eq('status', 'confirmed')
        .maybeSingle();

      if (findError) throw findError;

      if (!existingBooking) {
        alert('❌ Reserva não encontrada.');
        return;
      }

      // Update booking status to cancelled
      const { error: cancelError } = await supabase
        .from('bookings')
        .update({ 
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', existingBooking.id);

      if (cancelError) throw cancelError;

      console.log('✅ Booking cancelled successfully');

      // Wait for database trigger to complete (1.5 seconds for reliability)
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Refresh data
      await Promise.all([
        fetchClassSessions(),
        fetchUserBookings(user.id),
        ...(userType !== 'teacher' ? [fetchUserSubscriptions(user.id)] : []) // Refresh subscriptions (credits refunded)
      ]);

      console.log('✅ Data refreshed after cancellation');

      alert('✅ Reserva cancelada com sucesso!');
      closeClassModal();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Erro ao cancelar a reserva: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Send booking confirmation email with calendar attachment
  const sendBookingEmail = async () => {
    try {
      // Call Vercel Serverless Function (deployed with your app)
      const functionUrl = '/api/send-booking-email';
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: user.email,
          userName: user.name,
          className: classDetails.name,
          sessionDate: classDetails.sessionDate,
          startTime: classDetails.startTime,
          endTime: classDetails.endTime,
          location: classDetails.location,
          isOnline: classDetails.isOnline,
          zoomLink: classDetails.zoomLink,
          teacher: classDetails.teacher
        })
      });

      if (!response.ok) {
        console.error('Email sending failed, but booking is confirmed');
        return;
      }

      const data = await response.json();
      console.log('✅ Confirmation email sent with calendar attachment:', data.emailId);
    } catch (error) {
      // Don't throw - email is nice-to-have, booking is primary
      console.error('Error sending confirmation email:', error);
    }
  };

  // Book a class session
  const validateBooking = async (sessionId) => {
    try {
      if (!user?.id) {
        return {
          can_book: false,
          reason: 'Por favor, faça login para reservar uma aula.'
        };
      }

      // Call the validation function from the database
      const { data, error } = await supabase
        .rpc('can_user_book_session', {
          p_user_id: user.id,
          p_class_session_id: sessionId
        });

      if (error) throw error;

      // The function returns an array with one result
      const result = data && data.length > 0 ? data[0] : null;
      
      if (!result) {
        return {
          can_book: false,
          reason: 'Erro ao validar reserva. Por favor, tente novamente.'
        };
      }

      return result;
    } catch (error) {
      console.error('Error validating booking:', error);
      return {
        can_book: false,
        reason: 'Erro ao validar reserva: ' + (error.message || 'Erro desconhecido')
      };
    }
  };

  const handleBookSession = async () => {
    if (!user) {
      alert('Por favor, faça login para reservar uma aula.');
      return;
    }

    if (!classDetails.id) {
      alert('Erro: Sessão inválida.');
      return;
    }

    try {
      // Check if user already has a booking for this session
      const { data: existingBookings, error: checkError } = await supabase
        .from('bookings')
        .select('id')
        .eq('class_session_id', classDetails.id)
        .eq('user_id', user.id)
        .eq('status', 'confirmed');

      if (checkError) throw checkError;

      if (existingBookings && existingBookings.length > 0) {
        alert('Já tem uma reserva para esta aula!');
        return;
      }

      // Validate booking against subscription limits (SKIP for teachers)
      if (userType !== 'teacher') {
        console.log('🔍 Validating booking for student...');
      const validation = await validateBooking(classDetails.id);
      
      if (!validation.can_book) {
        // Show user-friendly error message
        alert('❌ Não é possível reservar esta aula\n\n' + validation.reason + '\n\n💡 Dica: Verifique as suas subscrições ativas no perfil.');
        return;
      }

      console.log('✅ Validation passed:', validation.reason);
      } else {
        console.log('✅ Teacher booking - subscription validation skipped');
      }

      // Create new booking
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          user_id: user.id,
          class_session_id: classDetails.id,
          status: 'confirmed',
          payment_status: 'pending'
        })
        .select()
        .single();

      if (bookingError) throw bookingError;

      console.log('✅ Booking created successfully:', booking);

      // Wait for database trigger to complete (1.5 seconds for reliability)
      // The trigger auto-updates current_participants
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Refresh data to show updated bookings and participant counts
      await Promise.all([
        fetchClassSessions(),  // Refresh sessions (with updated current_participants)
        fetchUserBookings(user.id),    // Refresh user's bookings (to show booked indicator)
        ...(userType !== 'teacher' ? [fetchUserSubscriptions(user.id)] : []) // Refresh subscriptions (only for students)
      ]);

      console.log('✅ Data refreshed after booking');

      // Send confirmation email with calendar attachment (async, don't wait)
      sendBookingEmail().catch(err => console.log('Email not sent:', err));

      alert('✅ Reserva confirmada com sucesso!\n\nVerifique o seu email para o ficheiro de calendário (.ics)');
      closeClassModal();
    } catch (error) {
      console.error('Error booking session:', error);
      alert('Erro ao reservar a aula: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Subscription management handlers
  const handleAssignSubscription = async (userId, planId, startDate, endDate = null, credits = null) => {
    try {
      if (!userId || !planId) {
        alert('Por favor, selecione um utilizador e um plano.');
        return;
      }

      const plan = subscriptionPlans.find(p => p.id === planId);
      if (!plan) {
        alert('Plano não encontrado.');
        return;
      }

      // Prepare subscription data
      const subscriptionData = {
        user_id: userId,
        subscription_plan_id: planId,
        start_date: startDate || new Date().toISOString().split('T')[0],
        is_active: true
      };

      // Add end_date for pack plans
      if (plan.plan_group === 'pack' && plan.credits_expire_days) {
        const expiry = new Date(subscriptionData.start_date);
        expiry.setDate(expiry.getDate() + plan.credits_expire_days);
        subscriptionData.end_date = expiry.toISOString().split('T')[0];
      } else if (endDate) {
        subscriptionData.end_date = endDate;
      }

      // Add credits for pack plans
      if (plan.total_credits) {
        subscriptionData.total_credits = plan.total_credits;
        subscriptionData.remaining_credits = credits || plan.total_credits;
      }

      // Insert subscription
      const { data, error } = await supabase
        .from('user_subscriptions')
        .insert([subscriptionData])
        .select();

      if (error) throw error;

      console.log('✅ Subscription assigned:', data);
      alert('✅ Subscrição atribuída com sucesso!');
      
      // Refresh data
      await fetchAllSubscriptions();
      setShowAssignSubscriptionModal(false);
      setSelectedUserForSubscription(null);
    } catch (error) {
      console.error('Error assigning subscription:', error);
      alert('❌ Erro ao atribuir subscrição: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!subscriptionId) return;

    const confirmCancel = window.confirm(
      'Tem certeza que deseja cancelar esta subscrição?\n\n' +
      'Esta ação não pode ser desfeita.'
    );

    if (!confirmCancel) return;

    try {
      const { error } = await supabase
        .from('user_subscriptions')
        .update({
          is_active: false,
          cancelled_at: new Date().toISOString()
        })
        .eq('id', subscriptionId);

      if (error) throw error;

      console.log('✅ Subscription cancelled');
      alert('✅ Subscrição cancelada com sucesso!');
      
      // Refresh data
      await fetchAllSubscriptions();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('❌ Erro ao cancelar subscrição: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleToggleSubscriptionStatus = async (subscriptionId, currentStatus) => {
    if (!subscriptionId) return;

    const action = currentStatus ? 'desativar' : 'ativar';
    const confirmToggle = window.confirm(
      `Tem certeza que deseja ${action} esta subscrição?`
    );

    if (!confirmToggle) return;

    try {
      const { error } = await supabase
        .from('user_subscriptions')
        .update({
          is_active: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', subscriptionId);

      if (error) throw error;

      console.log(`✅ Subscription ${action}d`);
      alert(`✅ Subscrição ${action}da com sucesso!`);
      
      // Refresh data
      await fetchAllSubscriptions();
    } catch (error) {
      console.error(`Error ${action}ing subscription:`, error);
      alert(`❌ Erro ao ${action} subscrição: ` + (error.message || 'Erro desconhecido'));
    }
  };

  // News modal handlers
  const openNewsModal = (newsItem) => {
    setSelectedNews({
      id: newsItem.id,
      title: newsItem.title,
      content: newsItem.content,
      date: newsItem.date,
      image: newsItem.image || '/api/placeholder/600/300'
    });
    setShowNewsModal(true);
  };

  const closeNewsModal = () => {
    setShowNewsModal(false);
    setSelectedNews({
      id: '',
      title: '',
      content: '',
      date: '',
      image: ''
    });
  };

  const openAddClassModal = () => {
    setNewClassForm({
      name: '',
      teacher: '',
      description: ''
    });
    setShowAddClassModal(true);
  };

  const closeAddClassModal = () => {
    setShowAddClassModal(false);
    setNewClassForm({
      name: '',
      teacher: '',
      description: ''
    });
  };

  const openEditClassModal = (classItem) => {
    setSelectedClassToEdit(classItem);
    setNewClassForm({
      name: classItem.name,
      teacher: classItem.teacher || '',
      description: classItem.description
    });
    setShowEditClassModal(true);
  };

  const closeEditClassModal = () => {
    setShowEditClassModal(false);
    setSelectedClassToEdit(null);
    setNewClassForm({
      name: '',
      teacher: '',
      description: ''
    });
  };

  const handleEditClass = async () => {
    if (!newClassForm.name || !newClassForm.description) {
      alert('Por favor, preencha todos os campos obrigatórios (Nome e Descrição).');
      return;
    }

    try {
      const { error } = await supabase
        .from('classes')
        .update({
          name: newClassForm.name,
          description: newClassForm.description,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedClassToEdit.id);

      if (error) throw error;

      alert('Aula atualizada com sucesso!');
      closeEditClassModal();
      await fetchClasses();
    } catch (error) {
      console.error('Error updating class:', error);
      alert('Erro ao atualizar aula: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleDeleteClass = async (classItem) => {
    if (!window.confirm(`Tem certeza que deseja remover a aula "${classItem.name}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('classes')
        .delete()
        .eq('id', classItem.id);

      if (error) throw error;

      alert('Aula removida com sucesso!');
      await fetchClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      alert('Erro ao remover aula: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleAddClass = async () => {
    if (!newClassForm.name || !newClassForm.description) {
      alert('Por favor, preencha todos os campos obrigatórios (Nome e Descrição).');
      return;
    }

    try {
      // Check if Supabase URL and key are configured
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        throw new Error('Supabase não está configurado. Verifique as variáveis de ambiente.');
      }

      // Check authentication status
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        console.log('No authenticated user found. RLS policies may block this operation.');
        console.log('Tip: You may need to authenticate with Supabase or adjust RLS policies.');
      } else {
        console.log('Authenticated as:', currentUser.email);
      }

      // Try to save to Supabase
      const { data, error } = await supabase
        .from('classes')
        .insert([
          {
            name: newClassForm.name,
            description: newClassForm.description,
            max_participants: 20, // default value
            is_active: true
          }
        ])
        .select();

      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }

      // Add to local state
      if (data && data.length > 0) {
        const newClassWithTeacher = {
          ...data[0],
          teacher: newClassForm.teacher || null
        };
        setClasses([...classes, newClassWithTeacher]);
        alert('Aula adicionada com sucesso!');
        closeAddClassModal();
        // Refresh the classes list
        await fetchClasses();
      } else {
        throw new Error('Nenhum dado retornado do banco de dados');
      }
    } catch (error) {
      console.error('Error adding class:', error);
      
      // Show detailed error message
      let errorMessage = 'Erro ao adicionar aula ao banco de dados.';
      if (error.message) {
        errorMessage += `\n\nErro: ${error.message}`;
      }
      if (error.code) {
        errorMessage += `\nCódigo: ${error.code}`;
      }
      if (error.details) {
        errorMessage += `\nDetalhes: ${error.details}`;
      }
      
      // Check if it's an RLS/permission error
      if (error.code === '42501' || error.message?.includes('permission') || error.message?.includes('policy')) {
        errorMessage += '\n\n💡 Possíveis soluções:';
        errorMessage += '\n1. Verifique as políticas RLS no Supabase';
        errorMessage += '\n2. Certifique-se de que está autenticado como admin';
        errorMessage += '\n3. Ou adicione a aula localmente (não será salva no banco)';
      }
      
      // Fallback: add to local state only
      const shouldSaveLocally = window.confirm(
        errorMessage + 
        '\n\nDeseja adicionar localmente mesmo assim? (A aula não será salva no banco de dados)'
      );
      
      if (shouldSaveLocally) {
        const newClass = {
          id: Date.now(), // temporary ID
          name: newClassForm.name,
          description: newClassForm.description,
          teacher: newClassForm.teacher,
          max_participants: 20
        };
        setClasses([...classes, newClass]);
        alert('Aula adicionada localmente (não foi salva no banco de dados).');
        closeAddClassModal();
      }
    }
  };

  // Fetch users from Supabase
  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback to empty array
      setUsers([]);
    }
  };

  // Fetch teacher-class assignments
  const fetchTeacherClassAssignments = async () => {
    try {
      const { data, error } = await supabase
        .from('teacher_class_types')
        .select(`
          *,
          teacher:teachers(
            id,
            user_id,
            bio,
            user_profile:user_profiles!user_id(
              id,
              full_name,
              email
            )
          ),
          class:classes(name, category)
        `)
        .eq('is_active', true);
      
      if (error) throw error;
      setTeacherClassAssignments(data || []);
    } catch (error) {
      console.error('Error fetching teacher class assignments:', error);
      setTeacherClassAssignments([]);
    }
  };

  // Assign teacher to a class type
  const handleAssignTeacherToClass = async (teacherId, classId) => {
    try {
      const { error } = await supabase
        .from('teacher_class_types')
        .insert({
          teacher_id: teacherId,
          class_id: classId,
          assigned_by: user.id,
          is_active: true
        });
      
      if (error) {
        if (error.code === '23505') { // Duplicate key error
          alert('Este professor já está atribuído a este tipo de aula.');
        } else {
          throw error;
        }
        return;
      }
      
      alert('Professor atribuído com sucesso!');
      await fetchTeacherClassAssignments();
    } catch (error) {
      console.error('Error assigning teacher to class:', error);
      alert('Erro ao atribuir professor: ' + error.message);
    }
  };

  // Remove teacher from a class type
  const handleRemoveTeacherFromClass = async (assignmentId) => {
    if (!window.confirm('Tem certeza que deseja remover esta atribuição?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('teacher_class_types')
        .delete()
        .eq('id', assignmentId);
      
      if (error) throw error;
      
      alert('Atribuição removida com sucesso!');
      await fetchTeacherClassAssignments();
    } catch (error) {
      console.error('Error removing teacher assignment:', error);
      alert('Erro ao remover atribuição: ' + error.message);
    }
  };

  // Open teacher assignment modal for a specific class
  const openTeacherAssignmentModal = (classItem) => {
    setSelectedClassForTeachers(classItem);
    setShowTeacherAssignmentModal(true);
  };

  const closeTeacherAssignmentModal = () => {
    setSelectedClassForTeachers(null);
    setShowTeacherAssignmentModal(false);
  };

  // Open/close user modals
  const openAddUserModal = () => {
    setNewUserForm({
      email: '',
      full_name: '',
      birth_date: ''
    });
    setShowAddUserModal(true);
  };

  const closeAddUserModal = () => {
    // Prevent closing while operation is in progress
    if (isUserOperationLoading) {
      console.log('⚠️ Cannot close modal while operation is in progress');
      return;
    }
    setShowAddUserModal(false);
    setNewUserForm({
      email: '',
      full_name: '',
      birth_date: '',
      user_type: 'student' // Reset to default
    });
  };

  const openEditUserModal = (user) => {
    setSelectedUser(user);
    setNewUserForm({
      email: user.email || '',
      full_name: user.full_name || '',
      birth_date: user.birth_date || '',
      user_type: user.user_type || 'student'
    });
    setShowEditUserModal(true);
  };

  const closeEditUserModal = () => {
    // Prevent closing while operation is in progress
    if (isUserOperationLoading) {
      console.log('⚠️ Cannot close modal while operation is in progress');
      return;
    }
    setShowEditUserModal(false);
    setSelectedUser(null);
    setNewUserForm({
      email: '',
      full_name: '',
      birth_date: ''
    });
  };

  // Handle add user
  const handleAddUser = async () => {
    if (!newUserForm.email || !newUserForm.full_name) {
      alert('Por favor, preencha todos os campos obrigatórios (Email e Nome).');
      return;
    }

    // Prevent multiple submissions
    if (isUserOperationLoading) {
      console.log('⚠️ Operation already in progress, ignoring click');
      return;
    }

    setIsUserOperationLoading(true);
    
    try {
      // Try to use the SQL function first, but fallback to signUp if it doesn't exist
      let userCreated = false;
      
      try {
        console.log('🔧 Calling create_confirmed_user_no_password with params:', {
          user_email: newUserForm.email,
          user_full_name: newUserForm.full_name,
          user_birth_date: newUserForm.birth_date || null,
          user_subscription_type: 'basic',
          user_type: newUserForm.user_type || 'student'
        });
        
        const { data: functionResult, error: functionError } = await supabase.rpc(
          'create_confirmed_user_no_password',
          {
            user_email: newUserForm.email,
            user_full_name: newUserForm.full_name,
            user_birth_date: newUserForm.birth_date || null,
            user_subscription_type: 'basic',
            user_type: newUserForm.user_type || 'student' // Pass user_type to database
          }
        );

        console.log('📊 RPC Response:', { data: functionResult, error: functionError });
        if (functionError) {
          console.error('🔴 DETAILED ERROR:', JSON.stringify(functionError, null, 2));
        }

        if (!functionError && functionResult) {
          userCreated = true;
          console.log('✅ User created via SQL function, UUID:', functionResult);
        } else if (functionError?.message?.includes('function') || functionError?.message?.includes('does not exist')) {
          // Function doesn't exist - inform admin
          console.error('❌ SQL function not found');
          throw new Error('SQL function "create_confirmed_user_no_password" not found. Please run database/create-confirmed-user-function.sql');
        } else {
          console.error('❌ SQL function error:', functionError);
          console.error('Error details:', {
            message: functionError?.message,
            details: functionError?.details,
            hint: functionError?.hint,
            code: functionError?.code
          });
          throw functionError;
        }
      } catch (funcError) {
        // If function error, show clear message and stop
        if (funcError.message?.includes('create_confirmed_user_no_password')) {
          alert('⚠️ Função SQL não encontrada!\n\n' +
                'Por favor, execute o script:\n' +
                'database/create-confirmed-user-function.sql\n\n' +
                'no Supabase SQL Editor.');
          setIsUserOperationLoading(false);
          closeAddUserModal();
          return;
        }
        
        // Fallback: Create user via signUp with a temporary password
        // CRITICAL: Store admin session BEFORE signUp (signUp auto-logs in the new user!)
        console.warn('⚠️ SQL function failed, using fallback signUp method');
        const { data: { session: adminSessionBeforeSignUp } } = await supabase.auth.getSession();
        const adminIdBeforeSignUp = adminSessionBeforeSignUp?.user?.id;
        console.log('🔐 Storing admin session before signUp, Admin ID:', adminIdBeforeSignUp);
        
        const tempPassword = Math.random().toString(36).slice(-12) + 'A1!@#$';
        
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: newUserForm.email,
          password: tempPassword,
          options: {
            emailRedirectTo: undefined,
            data: {
              full_name: newUserForm.full_name,
              user_type: newUserForm.user_type || 'student' // Pass user_type from form
            }
          }
        });
        
        // CRITICAL: Restore admin session immediately after signUp
        if (adminSessionBeforeSignUp && adminIdBeforeSignUp) {
          console.log('🔄 Restoring admin session after signUp...');
          const { error: sessionRestoreError } = await supabase.auth.setSession({
            access_token: adminSessionBeforeSignUp.access_token,
            refresh_token: adminSessionBeforeSignUp.refresh_token
          });
          
          if (sessionRestoreError) {
            console.error('❌ Failed to restore admin session:', sessionRestoreError);
            alert('⚠️ Utilizador criado mas a sessão de admin foi perdida.\n\n' +
                  'Por favor, faça login novamente como admin.');
            await supabase.auth.signOut();
            setUser(null);
            setUserType(null);
            setIsUserOperationLoading(false);
            closeAddUserModal();
            return;
          }
          
          console.log('✅ Admin session restored successfully');
        }

        if (signUpError) {
          console.error('🔴 SIGNUP ERROR DETAILS:', JSON.stringify(signUpError, null, 2));
          if (signUpError.message?.includes('already registered') || signUpError.message?.includes('User already registered')) {
            alert('⚠️ Utilizador já existe no Supabase Auth.\n\n' +
                  'O perfil será atualizado se existir.');
            
            // Try to get the existing user and create/update profile
            const { data: existingUsers } = await supabase
              .from('user_profiles')
              .select('id')
              .eq('email', newUserForm.email)
              .maybeSingle();

            if (!existingUsers) {
              alert('⚠️ Utilizador existe no Auth mas não tem perfil.\n\n' +
                    'Por favor, execute este SQL no Supabase SQL Editor:\n\n' +
                    `-- First, get the user ID from auth.users:\n` +
                    `SELECT id FROM auth.users WHERE email = '${newUserForm.email}';\n\n` +
                    `-- Then insert the profile (replace USER_ID with the ID above):\n` +
                    `INSERT INTO user_profiles (id, email, full_name, birth_date, subscription_type, user_type)\n` +
                    `VALUES (\n` +
                    `  'USER_ID',\n` +
                    `  '${newUserForm.email}',\n` +
                    `  '${newUserForm.full_name}',\n` +
                    `  ${newUserForm.birth_date ? `'${newUserForm.birth_date}'` : 'NULL'},\n` +
                    `  '${newUserForm.subscription_type}',\n` +
                    `  'student'\n` +
                    `)\n` +
                    `ON CONFLICT (id) DO UPDATE SET\n` +
                    `  email = EXCLUDED.email,\n` +
                    `  full_name = EXCLUDED.full_name,\n` +
                    `  birth_date = EXCLUDED.birth_date,\n` +
                    `  subscription_type = EXCLUDED.subscription_type;`);
              setIsUserOperationLoading(false);
              closeAddUserModal();
              return;
            }
          } else {
            throw signUpError;
          }
        }

        if (signUpData?.user) {
          // User created, now create/update profile
          const newUserId = signUpData.user.id;
          const { error: profileError } = await supabase
            .from('user_profiles')
            .insert([
              {
                id: newUserId,
                email: newUserForm.email,
                full_name: newUserForm.full_name,
                birth_date: newUserForm.birth_date || null,
                subscription_type: 'basic', // Default value
                user_type: newUserForm.user_type || 'student' // ✅ FIXED: Use form value
              }
            ])
            .select();

          if (profileError) {
            if (profileError.message?.includes('row-level security') || profileError.code === '42501') {
              throw new Error('Erro de política RLS. Por favor, execute o script fix-admin-insert-user-profiles.sql no Supabase SQL Editor.');
            }
            throw profileError;
          }

          // ✅ NEW: Create teacher entry if user_type is 'teacher'
          if (newUserForm.user_type === 'teacher') {
            console.log('👨‍🏫 Creating teacher entry for:', newUserId);
            const { error: teacherError } = await supabase
              .from('teachers')
              .insert([
                {
                  user_id: newUserId,
                  bio: '',
                  is_active: true
                }
              ]);

            if (teacherError) {
              console.error('⚠️ Failed to create teacher entry:', teacherError);
              // Don't fail the entire operation, just log the error
              // Admin can manually create teacher entry later if needed
            } else {
              console.log('✅ Teacher entry created successfully');
            }
          }

          userCreated = true;
        }
      }

      if (userCreated) {
        // CRITICAL: Store current admin session before sending password reset
        // This prevents session switching when resetPasswordForEmail is called
        console.log('🔒 Storing current admin session before password reset...');
        const { data: { session: currentAdminSession } } = await supabase.auth.getSession();
        const currentAdminId = currentAdminSession?.user?.id;
        console.log('Admin ID before reset:', currentAdminId);
        
        // Send password reset email automatically
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(
          newUserForm.email,
          {
            redirectTo: 'https://mauhinity.digital'
          }
        );

        // CRITICAL: Verify session didn't change
        const { data: { session: afterResetSession } } = await supabase.auth.getSession();
        const afterResetId = afterResetSession?.user?.id;
        console.log('User ID after reset:', afterResetId);
        
        if (currentAdminId && afterResetId && currentAdminId !== afterResetId) {
          console.error('⚠️ SESSION SWITCHED! Restoring admin session...');
          // Session was compromised - this shouldn't happen but let's detect it
          alert('⚠️ Detectado problema de sessão. Por favor, faça login novamente.');
          // Force logout to prevent security issue
          await supabase.auth.signOut();
          setUser(null);
          setUserType(null);
          setIsUserOperationLoading(false);
          return;
        }

        if (resetError) {
          console.error('Error sending password reset email:', resetError);
          console.error('Error details:', {
            message: resetError.message,
            status: resetError.status,
            name: resetError.name
          });
          
          // Provide more helpful error message based on error type
          let errorMessage = '⚠️ Utilizador criado com sucesso!\n\n' +
                '⚠️ No entanto, o email de definição de senha falhou.\n\n' +
                '📋 Possíveis causas:\n' +
                '• Serviço de email não configurado no Supabase\n' +
                '• URL de redirecionamento não autorizado\n' +
                '• Limite de taxa de emails excedido\n\n' +
                '✅ Soluções:\n' +
                '1. O utilizador pode usar "Esqueci a senha" no login\n' +
                '2. Configure o serviço de email no Supabase Dashboard\n' +
                '3. Adicione "https://mauhinity.digital" aos Redirect URLs\n\n' +
                '📧 Email do utilizador: ' + newUserForm.email;
          
          alert(errorMessage);
        } else {
          console.log('✅ Password reset email sent, admin session preserved');
          alert('✅ Utilizador adicionado com sucesso!\n\n' +
                '📧 Email de definição de senha enviado para: ' + newUserForm.email + '\n\n' +
                'O utilizador deve:\n' +
                '1. Verificar o email (incluindo spam)\n' +
                '2. Clicar no link para definir senha\n' +
                '3. Completar o perfil (foto e bio)');
        }
        
        closeAddUserModal();
        await fetchUsers();
        setIsUserOperationLoading(false);
      }
    } catch (error) {
      console.error('Error adding user:', error);
      setIsUserOperationLoading(false);
      alert('Erro ao adicionar utilizador:\n\n' + 
            (error.message || 'Erro desconhecido') + 
            '\n\n💡 Dica: Certifique-se de que executou o script create-confirmed-user-function.sql no Supabase SQL Editor.');
    }
  };

  // Handle edit user
  const handleEditUser = async () => {
    if (!selectedUser || !newUserForm.email || !newUserForm.full_name) {
      alert('Por favor, preencha todos os campos obrigatórios (Email e Nome).');
      return;
    }

    // Prevent multiple submissions
    if (isUserOperationLoading) {
      console.log('⚠️ Operation already in progress, ignoring click');
      return;
    }

    setIsUserOperationLoading(true);

    try {
      const oldUserType = selectedUser.user_type;
      const newUserType = newUserForm.user_type || 'student';

      // Update user profile
      const { error } = await supabase
        .from('user_profiles')
        .update({
          email: newUserForm.email,
          full_name: newUserForm.full_name,
          birth_date: newUserForm.birth_date || null,
          subscription_type: newUserForm.subscription_type,
          user_type: newUserType,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedUser.id);

      if (error) throw error;

      // Handle user_type change
      if (oldUserType !== newUserType) {
        if (newUserType === 'teacher') {
          // Changed to teacher: create teacher entry if doesn't exist
          const { error: teacherError } = await supabase
            .from('teachers')
            .insert({
              user_id: selectedUser.id,
              bio: '',
              specialties: [],
              hourly_rate: 0,
              is_active: true
            })
            .select()
            .single();
          
          // Ignore error if teacher already exists (23505 = unique violation)
          if (teacherError && teacherError.code !== '23505') {
            console.warn('Error creating teacher entry:', teacherError);
          }
        } else if (oldUserType === 'teacher' && newUserType === 'student') {
          // Changed from teacher to student: optionally deactivate teacher entry
          // We don't delete to preserve history, just mark as inactive
          await supabase
            .from('teachers')
            .update({ is_active: false })
            .eq('user_id', selectedUser.id);
        }
      }

      alert('Utilizador atualizado com sucesso!');
      closeEditUserModal();
      await fetchUsers();
      setIsUserOperationLoading(false);
    } catch (error) {
      console.error('Error updating user:', error);
      setIsUserOperationLoading(false);
      alert('Erro ao atualizar utilizador: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userToDelete) => {
    if (!window.confirm(`Tem certeza que deseja remover ${userToDelete.full_name || userToDelete.email}?\n\n` +
                        'Esta ação é PERMANENTE e não pode ser desfeita!\n\n' +
                        'Isto irá remover:\n' +
                        '• Perfil do utilizador\n' +
                        '• Conta de autenticação\n' +
                        '• Todas as subscrições\n' +
                        '• Todas as reservas')) {
      return;
    }

    try {
      console.log('Deleting user:', userToDelete.id, userToDelete.email);
      
      // Call SQL function to delete from both user_profiles AND auth.users
      const { data, error } = await supabase.rpc('delete_user_completely', {
        user_id_to_delete: userToDelete.id
      });

      if (error) {
        console.error('RPC error:', error);
        throw error;
      }

      console.log('Delete result:', data);
      
      alert('✅ Utilizador removido com sucesso!\n\n' +
            '🗑️ Perfil e conta de autenticação eliminados.\n\n' +
            `📧 Email removido: ${userToDelete.email}`);
      
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      
      // More helpful error messages
      let errorMessage = 'Erro ao remover utilizador:\n\n';
      
      if (error.message?.includes('does not exist')) {
        errorMessage += '⚠️ A função SQL não existe.\n\n' +
                       'Por favor, execute o script:\n' +
                       'database/delete-user-completely.sql\n\n' +
                       'no Supabase SQL Editor.';
      } else if (error.message?.includes('Only admins')) {
        errorMessage += '🚫 Apenas administradores podem remover utilizadores.';
      } else {
        errorMessage += error.message || 'Erro desconhecido';
      }
      
      alert(errorMessage);
    }
  };

  // ========== SESSION MANAGEMENT FUNCTIONS ==========
  
  // Open/close session modals
  const openAddSessionModal = async (isTemplate = false) => {
    // Refresh teacher data and assignments to ensure we have the latest
    await Promise.all([
      fetchTeachers(),
      fetchTeacherClassAssignments()
    ]);
    
    setSessionForm({
      class_id: classes.length > 0 ? classes[0].id : '',
      teacher_id: teachers.length > 0 ? teachers[0].id : '',
      session_date: isTemplate ? '2024-01-01' : '', // Dummy date for templates
      start_time: '',
      end_time: '',
      max_participants: 15,
      is_online: false,
      location: 'Estúdio Bhāva',
      zoom_link: '',
      notes: '',
      is_template: isTemplate,
      day_of_week: 1 // Monday by default
    });
    setShowAddSessionModal(true);
  };

  const closeAddSessionModal = () => {
    setShowAddSessionModal(false);
  };

  const openEditSessionModal = async (session) => {
    // Determine if this is a template by checking if it exists in weeklyTemplates
    const isTemplate = weeklyTemplates.some(t => t.id === session.id);
    
    // Refresh teacher data and assignments to ensure we have the latest
    await Promise.all([
      fetchTeachers(),
      fetchTeacherClassAssignments()
    ]);
    
    setSelectedSession(session);
    setSessionForm({
      class_id: session.class_id,
      teacher_id: session.teacher_id,
      session_date: session.session_date || '',
      start_time: session.start_time,
      end_time: session.end_time,
      max_participants: session.max_participants,
      is_online: session.is_online || false,
      location: session.location || 'Estúdio Bhāva',
      zoom_link: session.zoom_link || '',
      notes: session.notes || '',
      is_template: isTemplate,
      day_of_week: session.day_of_week || 1
    });
    setShowEditSessionModal(true);
  };

  const closeEditSessionModal = () => {
    setShowEditSessionModal(false);
    setSelectedSession(null);
  };

  // Handle add session (works for both templates and regular sessions)
  const handleAddSession = async () => {
    try {
      if (!sessionForm.class_id || !sessionForm.teacher_id || 
          !sessionForm.start_time || !sessionForm.end_time) {
        alert('Por favor, preencha todos os campos obrigatórios.');
        return;
      }

      // For templates, validate day_of_week; for regular sessions, validate date
      if (sessionForm.is_template && !sessionForm.day_of_week) {
        alert('Por favor, selecione o dia da semana.');
        return;
      }
      if (!sessionForm.is_template && !sessionForm.session_date) {
        alert('Por favor, selecione a data.');
        return;
      }

      if (sessionForm.is_template) {
        // Insert into weekly_schedule_template table
        const { error } = await supabase
          .from('weekly_schedule_template')
          .insert([{
            class_id: sessionForm.class_id,
            teacher_id: sessionForm.teacher_id,
            day_of_week: sessionForm.day_of_week,
            start_time: sessionForm.start_time,
            end_time: sessionForm.end_time,
            max_participants: sessionForm.max_participants,
            is_online: sessionForm.is_online,
            location: sessionForm.location,
            zoom_link: sessionForm.zoom_link,
            notes: sessionForm.notes,
            is_active: true
          }]);

        if (error) throw error;
      } else {
        // Insert into class_sessions table
        const { error } = await supabase
          .from('class_sessions')
          .insert([{
            class_id: sessionForm.class_id,
            teacher_id: sessionForm.teacher_id,
            session_date: sessionForm.session_date,
            start_time: sessionForm.start_time,
            end_time: sessionForm.end_time,
            max_participants: sessionForm.max_participants,
            is_online: sessionForm.is_online,
            location: sessionForm.location,
            zoom_link: sessionForm.zoom_link,
            notes: sessionForm.notes,
            status: 'scheduled'
          }]);

        if (error) throw error;
      }

      alert(sessionForm.is_template ? 'Template adicionado com sucesso!' : 'Horário adicionado com sucesso!');
      closeAddSessionModal();
      await fetchClassSessions();
      await fetchWeeklyTemplates();
    } catch (error) {
      console.error('Error adding session:', error);
      alert('Erro ao adicionar horário: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Handle update session
  const handleUpdateSession = async () => {
    try {
      if (!sessionForm.class_id || !sessionForm.teacher_id || 
          !sessionForm.start_time || !sessionForm.end_time) {
        alert('Por favor, preencha todos os campos obrigatórios.');
        return;
      }

      if (sessionForm.is_template) {
        // Update in weekly_schedule_template table
        const { error } = await supabase
          .from('weekly_schedule_template')
          .update({
            class_id: sessionForm.class_id,
            teacher_id: sessionForm.teacher_id,
            day_of_week: sessionForm.day_of_week,
            start_time: sessionForm.start_time,
            end_time: sessionForm.end_time,
            max_participants: sessionForm.max_participants,
            is_online: sessionForm.is_online,
            location: sessionForm.location,
            zoom_link: sessionForm.zoom_link,
            notes: sessionForm.notes,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedSession.id);

        if (error) throw error;
      } else {
        // Update in class_sessions table
        const { error } = await supabase
          .from('class_sessions')
          .update({
            class_id: sessionForm.class_id,
            teacher_id: sessionForm.teacher_id,
            session_date: sessionForm.session_date,
            start_time: sessionForm.start_time,
            end_time: sessionForm.end_time,
            max_participants: sessionForm.max_participants,
            is_online: sessionForm.is_online,
            location: sessionForm.location,
            zoom_link: sessionForm.zoom_link,
            notes: sessionForm.notes,
            is_modified: true, // Mark as modified
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedSession.id);

        if (error) throw error;
      }

      alert(sessionForm.is_template ? 'Template atualizado com sucesso!' : 'Horário atualizado com sucesso!');
      closeEditSessionModal();
      await fetchClassSessions();
      await fetchWeeklyTemplates();
    } catch (error) {
      console.error('Error updating session:', error);
      alert('Erro ao atualizar horário: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Handle delete session
  const handleDeleteSession = async (session) => {
    // Check if this is a template by looking at which state it comes from
    // Templates come from weeklyTemplates, sessions from classSessions
    const isTemplate = weeklyTemplates.some(t => t.id === session.id);
    const action = isTemplate ? 'remover este template' : 'remover este horário';
    
    if (!window.confirm(`Tem certeza que deseja ${action}? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const tableName = isTemplate ? 'weekly_schedule_template' : 'class_sessions';
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', session.id);

      if (error) throw error;

      alert(isTemplate ? 'Template removido com sucesso!' : 'Horário removido com sucesso!');
      await fetchClassSessions();
      await fetchWeeklyTemplates();
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Erro ao remover: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Handle cancel session (for calendar - marks as cancelled)
  const handleCancelSession = async (session) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta aula? Ela será mantida no calendário mas marcada como cancelada.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('class_sessions')
        .update({
          is_cancelled: true,
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', session.id);

      if (error) throw error;

      alert('Aula cancelada com sucesso!');
      await fetchClassSessions();
      await fetchWeeklyTemplates();
    } catch (error) {
      console.error('Error cancelling session:', error);
      alert('Erro ao cancelar aula: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Handle reactivate session (uncancel)
  const handleReactivateSession = async (session) => {
    try {
      const { error } = await supabase
        .from('class_sessions')
        .update({
          is_cancelled: false,
          status: 'scheduled',
          updated_at: new Date().toISOString()
        })
        .eq('id', session.id);

      if (error) throw error;

      alert('Aula reativada com sucesso!');
      await fetchClassSessions();
      await fetchWeeklyTemplates();
    } catch (error) {
      console.error('Error reactivating session:', error);
      alert('Erro ao reativar aula: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Subscription type options
  const subscriptionTypes = [
    { value: 'presencial_yoga_1x_semana', label: 'Presencial Yôga 1x semana' },
    { value: 'presencial_yoga_2x_semana', label: 'Presencial Yôga 2x semana' },
    { value: 'presencial_yoga_livre_transito', label: 'Presencial Yôga Livre Trânsito' },
    { value: 'presencial_yoga_aulas_avulso', label: 'Presencial Yôga Aulas Avulso' },
    { value: 'online_yoga_2x_semana', label: 'Online Yôga 2x semana' },
    { value: 'online_yoga_livre_transito', label: 'Online Yôga Livre Trânsito' },
    { value: 'online_yoga_aulas_avulso', label: 'Online Yôga Aulas Avulso' },
    { value: 'misto_yoga_1x_presencial_1x_online', label: 'Misto Yôga 1x presencial + 1x online' },
    { value: 'pack_5_aulas_presencial', label: 'Pack 5 aulas presencial' },
    { value: 'pack_10_aulas_presencial', label: 'Pack 10 aulas presencial' },
    { value: 'pack_5_aulas_online', label: 'Pack 5 aulas online' },
    { value: 'pack_10_aulas_online', label: 'Pack 10 aulas online' },
    { value: 'gravacoes_2x_semana', label: 'Gravações 2x semana' },
    { value: 'gravacoes_3x_semana', label: 'Gravações 3x semana' },
    { value: 'gravacoes_5x_semana', label: 'Gravações 5x semana' }
  ];

  // Get navigation items based on user type
  const getNavigationItems = () => {
    switch (userType) {
      case 'student':
        return [
          { id: 'home', label: 'O Bhava', icon: Home },
          { id: 'classes', label: 'Aulas Disponíveis', icon: BookOpen },
          { id: 'bookings', label: 'Marcação de aulas', icon: Calendar },
          { id: 'profile', label: 'O meu perfil', icon: User },
          { id: 'news', label: 'Novidades', icon: Newspaper }
        ];
      case 'teacher':
        return [
          { id: 'home', label: 'O meu Bhava', icon: Home },
          { id: 'my-classes', label: 'As Minhas Aulas', icon: BookOpen },
          { id: 'book-classes', label: 'Marcar Aulas', icon: Calendar },
          { id: 'profile', label: 'O meu perfil', icon: User },
          { id: 'news', label: 'Novidades', icon: Newspaper }
        ];
      case 'admin':
        return [
          { id: 'home', label: 'O meu Bhāva', icon: Home },
          { id: 'classes', label: 'Gerir Aulas', icon: BookOpen },
          { id: 'schedule', label: 'Horários e Calendário', icon: Calendar },
          { id: 'users', label: 'Utilizadores', icon: Users },
          { id: 'subscriptions', label: 'Subscrições', icon: Settings },
          { id: 'content', label: 'Conteúdo', icon: Image },
          { id: 'news', label: 'Notícias', icon: Newspaper },
          { id: 'reports', label: 'Relatórios', icon: BarChart3 }
        ];
      default:
        return [];
    }
  };

  // Loading Screen (while checking session)
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <svg xmlns="http://www.w3.org/2000/svg" width="200" height="60" viewBox="0 0 750 250" className="logo-svg">
            <g fill="#81524f">
              <g transform="translate(0.00000265, 248.06043)">
                <path d="M 6.53125 -235.765625 C 6.53125 -238.285156 7.21875 -240.285156 8.59375 -241.765625 C 9.96875 -243.253906 11.914062 -244 14.4375 -244 C 16.957031 -244 18.960938 -243.253906 20.453125 -241.765625 C 21.941406 -240.285156 22.6875 -238.285156 22.6875 -235.765625 L 22.6875 -160.84375 C 28.875 -168.164062 36.375 -173.945312 45.1875 -178.1875 C 54.007812 -182.425781 63.804688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 L 142.625 -64.609375 C 142.625 -55.210938 140.847656 -46.390625 137.296875 -38.140625 C 133.742188 -29.898438 128.875 -22.6875 122.6875 -16.5 C 116.5 -10.3125 109.28125 -5.441406 101.03125 -1.890625 C 92.789062 1.660156 83.972656 3.4375 74.578125 3.4375 C 65.179688 3.4375 56.359375 1.660156 48.109375 -1.890625 C 39.859375 -5.441406 32.640625 -10.3125 26.453125 -16.5 C 20.273438 -22.6875 15.410156 -29.898438 11.859375 -38.140625 C 8.304688 -46.390625 6.53125 -55.210938 6.53125 -64.609375 Z M 22.6875 -64.609375 C 22.6875 -57.503906 24.0625 -50.800781 26.8125 -44.5 C 29.5625 -38.195312 33.28125 -32.695312 37.96875 -28 C 42.664062 -23.3125 48.164062 -19.59375 54.46875 -16.84375 C 60.769531 -14.09375 67.472656 -12.71875 74.578125 -12.71875 C 81.679688 -12.71875 88.378906 -14.09375 94.671875 -16.84375 C 100.972656 -19.59375 106.472656 -23.3125 111.171875 -28 C 115.867188 -32.695312 119.59375 -38.195312 122.34375 -44.5 C 125.09375 -50.800781 126.46875 -57.503906 126.46875 -64.609375 L 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.703125 -168.40625 61.171875 -167.085938 54.984375 -164.453125 C 48.796875 -161.816406 43.351562 -158.265625 38.65625 -153.796875 C 33.96875 -149.328125 30.191406 -144.054688 27.328125 -137.984375 C 24.460938 -131.910156 22.914062 -125.4375 22.6875 -118.5625 Z M 22.6875 -64.609375 "/>
              </g>
              <g transform="translate(149.137219, 248.06043)">
                <path d="M 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.703125 -168.40625 61.171875 -167.085938 54.984375 -164.453125 C 48.796875 -161.816406 43.351562 -158.265625 38.65625 -153.796875 C 33.96875 -149.328125 30.191406 -144.054688 27.328125 -137.984375 C 24.460938 -131.910156 22.914062 -125.4375 22.6875 -118.5625 L 22.6875 -4.46875 C 22.6875 -1.945312 21.941406 0 20.453125 1.375 C 18.960938 2.75 16.957031 3.4375 14.4375 3.4375 C 9.164062 3.4375 6.53125 0.800781 6.53125 -4.46875 L 6.53125 -235.765625 C 6.53125 -238.285156 7.21875 -240.285156 8.59375 -241.765625 C 9.96875 -243.253906 11.914062 -244 14.4375 -244 C 16.957031 -244 18.960938 -243.253906 20.453125 -241.765625 C 21.941406 -240.285156 22.6875 -238.285156 22.6875 -235.765625 L 22.6875 -160.84375 C 28.875 -168.164062 36.375 -173.945312 45.1875 -178.1875 C 54.007812 -182.425781 63.804688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 L 142.625 -4.46875 C 142.625 -1.945312 141.878906 0 140.390625 1.375 C 138.898438 2.75 136.894531 3.4375 134.375 3.4375 C 129.101562 3.4375 126.46875 0.800781 126.46875 -4.46875 Z M 126.46875 -116.5 "/>
              </g>
              <g transform="translate(298.274435, 248.06043)">
                <path d="M 142.625 -64.609375 C 142.625 -55.210938 140.847656 -46.390625 137.296875 -38.140625 C 133.742188 -29.898438 128.875 -22.6875 122.6875 -16.5 C 116.5 -10.3125 109.28125 -5.441406 101.03125 -1.890625 C 92.789062 1.660156 83.972656 3.4375 74.578125 3.4375 C 65.179688 3.4375 56.359375 1.660156 48.109375 -1.890625 C 39.859375 -5.441406 32.640625 -10.3125 26.453125 -16.5 C 20.273438 -22.6875 15.410156 -29.898438 11.859375 -38.140625 C 8.304688 -46.390625 6.53125 -55.210938 6.53125 -64.609375 L 6.53125 -68.390625 C 6.53125 -78.015625 8.304688 -85.921875 11.859375 -92.109375 C 15.410156 -98.296875 20.273438 -103.101562 26.453125 -106.53125 C 32.640625 -109.96875 39.859375 -112.375 48.109375 -113.75 C 56.359375 -115.125 65.179688 -115.8125 74.578125 -115.8125 C 84.890625 -115.8125 94.566406 -114.953125 103.609375 -113.234375 C 112.660156 -111.515625 120.28125 -108.363281 126.46875 -103.78125 L 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.472656 -168.40625 60.769531 -167.03125 54.46875 -164.28125 C 48.164062 -161.53125 42.664062 -157.804688 37.96875 -153.109375 C 33.28125 -148.410156 29.5625 -142.851562 26.8125 -136.4375 C 24.0625 -130.019531 22.6875 -123.257812 22.6875 -116.15625 C 22.6875 -113.632812 21.941406 -111.6875 20.453125 -110.3125 C 18.960938 -108.9375 16.957031 -108.25 14.4375 -108.25 C 9.164062 -108.25 6.53125 -111.113281 6.53125 -116.84375 C 6.53125 -126.238281 8.304688 -135.0625 11.859375 -143.3125 C 15.410156 -151.5625 20.273438 -158.71875 26.453125 -164.78125 C 32.640625 -170.851562 39.859375 -175.664062 48.109375 -179.21875 C 56.359375 -182.769531 65.179688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 Z M 22.6875 -64.609375 C 22.6875 -57.503906 24.0625 -50.800781 26.8125 -44.5 C 29.5625 -38.195312 33.28125 -32.695312 37.96875 -28 C 42.664062 -23.3125 48.164062 -19.59375 54.46875 -16.84375 C 60.769531 -14.09375 67.472656 -12.71875 74.578125 -12.71875 C 81.679688 -12.71875 88.378906 -14.09375 94.671875 -16.84375 C 100.972656 -19.59375 106.472656 -23.3125 111.171875 -28 C 115.867188 -32.695312 119.59375 -38.195312 122.34375 -44.5 C 125.09375 -50.800781 126.46875 -57.503906 126.46875 -64.609375 L 126.46875 -70.453125 C 126.238281 -76.867188 124.691406 -81.96875 121.828125 -85.75 C 118.960938 -89.53125 115.238281 -92.453125 110.65625 -94.515625 C 106.070312 -96.578125 100.6875 -97.953125 94.5 -98.640625 C 88.320312 -99.328125 81.679688 -99.671875 74.578125 -99.671875 C 67.472656 -99.671875 60.769531 -99.328125 54.46875 -98.640625 C 48.164062 -97.953125 42.664062 -96.519531 37.96875 -94.34375 C 33.28125 -92.164062 29.5625 -89.015625 26.8125 -84.890625 C 24.0625 -80.765625 22.6875 -75.265625 22.6875 -68.390625 Z M 29.90625 -226.828125 C 27.382812 -226.828125 25.378906 -227.515625 23.890625 -228.890625 C 22.398438 -230.265625 21.65625 -232.210938 21.65625 -234.734375 C 21.65625 -237.253906 22.398438 -239.253906 23.890625 -240.734375 C 25.378906 -242.222656 27.382812 -242.96875 29.90625 -242.96875 L 119.25 -242.96875 C 121.769531 -242.96875 123.71875 -242.222656 125.09375 -240.734375 C 126.46875 -239.253906 127.15625 -237.253906 127.15625 -234.734375 C 127.15625 -229.460938 124.519531 -226.828125 119.25 -226.828125 Z M 29.90625 -226.828125 "/>
              </g>
              <g transform="translate(447.411651, 248.06043)">
                <path d="M 130.59375 -180.078125 C 131.28125 -182.597656 132.539062 -184.429688 134.375 -185.578125 C 136.207031 -186.722656 138.15625 -187.066406 140.21875 -186.609375 C 142.738281 -185.460938 144.628906 -183.859375 145.890625 -181.796875 C 147.148438 -179.734375 147.09375 -177.554688 145.71875 -175.265625 L 145.71875 -175.609375 C 134.488281 -146.742188 123.375 -118.046875 112.375 -89.515625 C 101.382812 -60.992188 90.273438 -32.300781 79.046875 -3.4375 C 78.816406 -1.601562 78.070312 -0.113281 76.8125 1.03125 C 75.550781 2.175781 74.234375 2.976562 72.859375 3.4375 C 67.816406 4.582031 64.609375 2.519531 63.234375 -2.75 L 63.234375 -2.40625 C 53.835938 -31.726562 44.5 -60.9375 35.21875 -90.03125 C 25.945312 -119.132812 16.613281 -148.347656 7.21875 -177.671875 C 6.53125 -180.191406 6.703125 -182.425781 7.734375 -184.375 C 8.765625 -186.320312 10.425781 -187.523438 12.71875 -187.984375 C 15.238281 -188.441406 17.472656 -188.097656 19.421875 -186.953125 C 21.367188 -185.804688 22.570312 -184.085938 23.03125 -181.796875 C 31.269531 -155.679688 39.34375 -130.304688 47.25 -105.671875 C 55.15625 -81.046875 63.347656 -55.5625 71.828125 -29.21875 C 81.679688 -54.644531 91.585938 -79.898438 101.546875 -104.984375 C 111.515625 -130.078125 121.3125 -155.335938 130.9375 -180.765625 Z M 130.59375 -180.078125 "/>
              </g>
              <g transform="translate(600.672433, 248.06043)">
                <path d="M 142.625 -64.609375 C 142.625 -55.210938 140.847656 -46.390625 137.296875 -38.140625 C 133.742188 -29.898438 128.875 -22.6875 122.6875 -16.5 C 116.5 -10.3125 109.28125 -5.441406 101.03125 -1.890625 C 92.789062 1.660156 83.972656 3.4375 74.578125 3.4375 C 65.179688 3.4375 56.359375 1.660156 48.109375 -1.890625 C 39.859375 -5.441406 32.640625 -10.3125 26.453125 -16.5 C 20.273438 -22.6875 15.410156 -29.898438 11.859375 -38.140625 C 8.304688 -46.390625 6.53125 -55.210938 6.53125 -64.609375 L 6.53125 -68.390625 C 6.53125 -78.015625 8.304688 -85.921875 11.859375 -92.109375 C 15.410156 -98.296875 20.273438 -103.101562 26.453125 -106.53125 C 32.640625 -109.96875 39.859375 -112.375 48.109375 -113.75 C 56.359375 -115.125 65.179688 -115.8125 74.578125 -115.8125 C 84.890625 -115.8125 94.566406 -114.953125 103.609375 -113.234375 C 112.660156 -111.515625 120.28125 -108.363281 126.46875 -103.78125 L 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.472656 -168.40625 60.769531 -167.03125 54.46875 -164.28125 C 48.164062 -161.53125 42.664062 -157.804688 37.96875 -153.109375 C 33.28125 -148.410156 29.5625 -142.851562 26.8125 -136.4375 C 24.0625 -130.019531 22.6875 -123.257812 22.6875 -116.15625 C 22.6875 -113.632812 21.941406 -111.6875 20.453125 -110.3125 C 18.960938 -108.9375 16.957031 -108.25 14.4375 -108.25 C 9.164062 -108.25 6.53125 -111.113281 6.53125 -116.84375 C 6.53125 -126.238281 8.304688 -135.0625 11.859375 -143.3125 C 15.410156 -151.5625 20.273438 -158.71875 26.453125 -164.78125 C 32.640625 -170.851562 39.859375 -175.664062 48.109375 -179.21875 C 56.359375 -182.769531 65.179688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 Z M 22.6875 -64.609375 C 22.6875 -57.503906 24.0625 -50.800781 26.8125 -44.5 C 29.5625 -38.195312 33.28125 -32.695312 37.96875 -28 C 42.664062 -23.3125 48.164062 -19.59375 54.46875 -16.84375 C 60.769531 -14.09375 67.472656 -12.71875 74.578125 -12.71875 C 81.679688 -12.71875 88.378906 -14.09375 94.671875 -16.84375 C 100.972656 -19.59375 106.472656 -23.3125 111.171875 -28 C 115.867188 -32.695312 119.59375 -38.195312 122.34375 -44.5 C 125.09375 -50.800781 126.46875 -57.503906 126.46875 -64.609375 L 126.46875 -70.453125 C 126.238281 -76.867188 124.691406 -81.96875 121.828125 -85.75 C 118.960938 -89.53125 115.238281 -92.453125 110.65625 -94.515625 C 106.070312 -96.578125 100.6875 -97.953125 94.5 -98.640625 C 88.320312 -99.328125 81.679688 -99.671875 74.578125 -99.671875 C 67.472656 -99.671875 60.769531 -99.328125 54.46875 -98.640625 C 48.164062 -97.953125 42.664062 -96.519531 37.96875 -94.34375 C 33.28125 -92.164062 29.5625 -89.015625 26.8125 -84.890625 C 24.0625 -80.765625 22.6875 -75.265625 22.6875 -68.390625 Z M 22.6875 -64.609375 "/>
              </g>
            </g>
          </svg>
          <div className="loading-spinner"></div>
          <p>A verificar sessão...</p>
        </div>
      </div>
    );
  }

  // Login Component
  if (!user) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="logo-container">
          <svg xmlns="http://www.w3.org/2000/svg" width="200" height="60" viewBox="0 0 750 250" className="logo-svg">
            <g fill="#81524f">
              <g transform="translate(0.00000265, 248.06043)">
                <path d="M 6.53125 -235.765625 C 6.53125 -238.285156 7.21875 -240.285156 8.59375 -241.765625 C 9.96875 -243.253906 11.914062 -244 14.4375 -244 C 16.957031 -244 18.960938 -243.253906 20.453125 -241.765625 C 21.941406 -240.285156 22.6875 -238.285156 22.6875 -235.765625 L 22.6875 -160.84375 C 28.875 -168.164062 36.375 -173.945312 45.1875 -178.1875 C 54.007812 -182.425781 63.804688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 L 142.625 -64.609375 C 142.625 -55.210938 140.847656 -46.390625 137.296875 -38.140625 C 133.742188 -29.898438 128.875 -22.6875 122.6875 -16.5 C 116.5 -10.3125 109.28125 -5.441406 101.03125 -1.890625 C 92.789062 1.660156 83.972656 3.4375 74.578125 3.4375 C 65.179688 3.4375 56.359375 1.660156 48.109375 -1.890625 C 39.859375 -5.441406 32.640625 -10.3125 26.453125 -16.5 C 20.273438 -22.6875 15.410156 -29.898438 11.859375 -38.140625 C 8.304688 -46.390625 6.53125 -55.210938 6.53125 -64.609375 Z M 22.6875 -64.609375 C 22.6875 -57.503906 24.0625 -50.800781 26.8125 -44.5 C 29.5625 -38.195312 33.28125 -32.695312 37.96875 -28 C 42.664062 -23.3125 48.164062 -19.59375 54.46875 -16.84375 C 60.769531 -14.09375 67.472656 -12.71875 74.578125 -12.71875 C 81.679688 -12.71875 88.378906 -14.09375 94.671875 -16.84375 C 100.972656 -19.59375 106.472656 -23.3125 111.171875 -28 C 115.867188 -32.695312 119.59375 -38.195312 122.34375 -44.5 C 125.09375 -50.800781 126.46875 -57.503906 126.46875 -64.609375 L 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.703125 -168.40625 61.171875 -167.085938 54.984375 -164.453125 C 48.796875 -161.816406 43.351562 -158.265625 38.65625 -153.796875 C 33.96875 -149.328125 30.191406 -144.054688 27.328125 -137.984375 C 24.460938 -131.910156 22.914062 -125.4375 22.6875 -118.5625 Z M 22.6875 -64.609375 "/>
              </g>
              <g transform="translate(149.137219, 248.06043)">
                <path d="M 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.703125 -168.40625 61.171875 -167.085938 54.984375 -164.453125 C 48.796875 -161.816406 43.351562 -158.265625 38.65625 -153.796875 C 33.96875 -149.328125 30.191406 -144.054688 27.328125 -137.984375 C 24.460938 -131.910156 22.914062 -125.4375 22.6875 -118.5625 L 22.6875 -4.46875 C 22.6875 -1.945312 21.941406 0 20.453125 1.375 C 18.960938 2.75 16.957031 3.4375 14.4375 3.4375 C 9.164062 3.4375 6.53125 0.800781 6.53125 -4.46875 L 6.53125 -235.765625 C 6.53125 -238.285156 7.21875 -240.285156 8.59375 -241.765625 C 9.96875 -243.253906 11.914062 -244 14.4375 -244 C 16.957031 -244 18.960938 -243.253906 20.453125 -241.765625 C 21.941406 -240.285156 22.6875 -238.285156 22.6875 -235.765625 L 22.6875 -160.84375 C 28.875 -168.164062 36.375 -173.945312 45.1875 -178.1875 C 54.007812 -182.425781 63.804688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 L 142.625 -4.46875 C 142.625 -1.945312 141.878906 0 140.390625 1.375 C 138.898438 2.75 136.894531 3.4375 134.375 3.4375 C 129.101562 3.4375 126.46875 0.800781 126.46875 -4.46875 Z M 126.46875 -116.5 "/>
              </g>
              <g transform="translate(298.274435, 248.06043)">
                <path d="M 142.625 -64.609375 C 142.625 -55.210938 140.847656 -46.390625 137.296875 -38.140625 C 133.742188 -29.898438 128.875 -22.6875 122.6875 -16.5 C 116.5 -10.3125 109.28125 -5.441406 101.03125 -1.890625 C 92.789062 1.660156 83.972656 3.4375 74.578125 3.4375 C 65.179688 3.4375 56.359375 1.660156 48.109375 -1.890625 C 39.859375 -5.441406 32.640625 -10.3125 26.453125 -16.5 C 20.273438 -22.6875 15.410156 -29.898438 11.859375 -38.140625 C 8.304688 -46.390625 6.53125 -55.210938 6.53125 -64.609375 L 6.53125 -68.390625 C 6.53125 -78.015625 8.304688 -85.921875 11.859375 -92.109375 C 15.410156 -98.296875 20.273438 -103.101562 26.453125 -106.53125 C 32.640625 -109.96875 39.859375 -112.375 48.109375 -113.75 C 56.359375 -115.125 65.179688 -115.8125 74.578125 -115.8125 C 84.890625 -115.8125 94.566406 -114.953125 103.609375 -113.234375 C 112.660156 -111.515625 120.28125 -108.363281 126.46875 -103.78125 L 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.472656 -168.40625 60.769531 -167.03125 54.46875 -164.28125 C 48.164062 -161.53125 42.664062 -157.804688 37.96875 -153.109375 C 33.28125 -148.410156 29.5625 -142.851562 26.8125 -136.4375 C 24.0625 -130.019531 22.6875 -123.257812 22.6875 -116.15625 C 22.6875 -113.632812 21.941406 -111.6875 20.453125 -110.3125 C 18.960938 -108.9375 16.957031 -108.25 14.4375 -108.25 C 9.164062 -108.25 6.53125 -111.113281 6.53125 -116.84375 C 6.53125 -126.238281 8.304688 -135.0625 11.859375 -143.3125 C 15.410156 -151.5625 20.273438 -158.71875 26.453125 -164.78125 C 32.640625 -170.851562 39.859375 -175.664062 48.109375 -179.21875 C 56.359375 -182.769531 65.179688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 Z M 22.6875 -64.609375 C 22.6875 -57.503906 24.0625 -50.800781 26.8125 -44.5 C 29.5625 -38.195312 33.28125 -32.695312 37.96875 -28 C 42.664062 -23.3125 48.164062 -19.59375 54.46875 -16.84375 C 60.769531 -14.09375 67.472656 -12.71875 74.578125 -12.71875 C 81.679688 -12.71875 88.378906 -14.09375 94.671875 -16.84375 C 100.972656 -19.59375 106.472656 -23.3125 111.171875 -28 C 115.867188 -32.695312 119.59375 -38.195312 122.34375 -44.5 C 125.09375 -50.800781 126.46875 -57.503906 126.46875 -64.609375 L 126.46875 -70.453125 C 126.238281 -76.867188 124.691406 -81.96875 121.828125 -85.75 C 118.960938 -89.53125 115.238281 -92.453125 110.65625 -94.515625 C 106.070312 -96.578125 100.6875 -97.953125 94.5 -98.640625 C 88.320312 -99.328125 81.679688 -99.671875 74.578125 -99.671875 C 67.472656 -99.671875 60.769531 -99.328125 54.46875 -98.640625 C 48.164062 -97.953125 42.664062 -96.519531 37.96875 -94.34375 C 33.28125 -92.164062 29.5625 -89.015625 26.8125 -84.890625 C 24.0625 -80.765625 22.6875 -75.265625 22.6875 -68.390625 Z M 29.90625 -226.828125 C 27.382812 -226.828125 25.378906 -227.515625 23.890625 -228.890625 C 22.398438 -230.265625 21.65625 -232.210938 21.65625 -234.734375 C 21.65625 -237.253906 22.398438 -239.253906 23.890625 -240.734375 C 25.378906 -242.222656 27.382812 -242.96875 29.90625 -242.96875 L 119.25 -242.96875 C 121.769531 -242.96875 123.71875 -242.222656 125.09375 -240.734375 C 126.46875 -239.253906 127.15625 -237.253906 127.15625 -234.734375 C 127.15625 -229.460938 124.519531 -226.828125 119.25 -226.828125 Z M 29.90625 -226.828125 "/>
              </g>
              <g transform="translate(447.411651, 248.06043)">
                <path d="M 130.59375 -180.078125 C 131.28125 -182.597656 132.539062 -184.429688 134.375 -185.578125 C 136.207031 -186.722656 138.15625 -187.066406 140.21875 -186.609375 C 142.738281 -185.460938 144.628906 -183.859375 145.890625 -181.796875 C 147.148438 -179.734375 147.09375 -177.554688 145.71875 -175.265625 L 145.71875 -175.609375 C 134.488281 -146.742188 123.375 -118.046875 112.375 -89.515625 C 101.382812 -60.992188 90.273438 -32.300781 79.046875 -3.4375 C 78.816406 -1.601562 78.070312 -0.113281 76.8125 1.03125 C 75.550781 2.175781 74.234375 2.976562 72.859375 3.4375 C 67.816406 4.582031 64.609375 2.519531 63.234375 -2.75 L 63.234375 -2.40625 C 53.835938 -31.726562 44.5 -60.9375 35.21875 -90.03125 C 25.945312 -119.132812 16.613281 -148.347656 7.21875 -177.671875 C 6.53125 -180.191406 6.703125 -182.425781 7.734375 -184.375 C 8.765625 -186.320312 10.425781 -187.523438 12.71875 -187.984375 C 15.238281 -188.441406 17.472656 -188.097656 19.421875 -186.953125 C 21.367188 -185.804688 22.570312 -184.085938 23.03125 -181.796875 C 31.269531 -155.679688 39.34375 -130.304688 47.25 -105.671875 C 55.15625 -81.046875 63.347656 -55.5625 71.828125 -29.21875 C 81.679688 -54.644531 91.585938 -79.898438 101.546875 -104.984375 C 111.515625 -130.078125 121.3125 -155.335938 130.9375 -180.765625 Z M 130.59375 -180.078125 "/>
              </g>
              <g transform="translate(600.672433, 248.06043)">
                <path d="M 142.625 -64.609375 C 142.625 -55.210938 140.847656 -46.390625 137.296875 -38.140625 C 133.742188 -29.898438 128.875 -22.6875 122.6875 -16.5 C 116.5 -10.3125 109.28125 -5.441406 101.03125 -1.890625 C 92.789062 1.660156 83.972656 3.4375 74.578125 3.4375 C 65.179688 3.4375 56.359375 1.660156 48.109375 -1.890625 C 39.859375 -5.441406 32.640625 -10.3125 26.453125 -16.5 C 20.273438 -22.6875 15.410156 -29.898438 11.859375 -38.140625 C 8.304688 -46.390625 6.53125 -55.210938 6.53125 -64.609375 L 6.53125 -68.390625 C 6.53125 -78.015625 8.304688 -85.921875 11.859375 -92.109375 C 15.410156 -98.296875 20.273438 -103.101562 26.453125 -106.53125 C 32.640625 -109.96875 39.859375 -112.375 48.109375 -113.75 C 56.359375 -115.125 65.179688 -115.8125 74.578125 -115.8125 C 84.890625 -115.8125 94.566406 -114.953125 103.609375 -113.234375 C 112.660156 -111.515625 120.28125 -108.363281 126.46875 -103.78125 L 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.472656 -168.40625 60.769531 -167.03125 54.46875 -164.28125 C 48.164062 -161.53125 42.664062 -157.804688 37.96875 -153.109375 C 33.28125 -148.410156 29.5625 -142.851562 26.8125 -136.4375 C 24.0625 -130.019531 22.6875 -123.257812 22.6875 -116.15625 C 22.6875 -113.632812 21.941406 -111.6875 20.453125 -110.3125 C 18.960938 -108.9375 16.957031 -108.25 14.4375 -108.25 C 9.164062 -108.25 6.53125 -111.113281 6.53125 -116.84375 C 6.53125 -126.238281 8.304688 -135.0625 11.859375 -143.3125 C 15.410156 -151.5625 20.273438 -158.71875 26.453125 -164.78125 C 32.640625 -170.851562 39.859375 -175.664062 48.109375 -179.21875 C 56.359375 -182.769531 65.179688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 Z M 22.6875 -64.609375 C 22.6875 -57.503906 24.0625 -50.800781 26.8125 -44.5 C 29.5625 -38.195312 33.28125 -32.695312 37.96875 -28 C 42.664062 -23.3125 48.164062 -19.59375 54.46875 -16.84375 C 60.769531 -14.09375 67.472656 -12.71875 74.578125 -12.71875 C 81.679688 -12.71875 88.378906 -14.09375 94.671875 -16.84375 C 100.972656 -19.59375 106.472656 -23.3125 111.171875 -28 C 115.867188 -32.695312 119.59375 -38.195312 122.34375 -44.5 C 125.09375 -50.800781 126.46875 -57.503906 126.46875 -64.609375 L 126.46875 -70.453125 C 126.238281 -76.867188 124.691406 -81.96875 121.828125 -85.75 C 118.960938 -89.53125 115.238281 -92.453125 110.65625 -94.515625 C 106.070312 -96.578125 100.6875 -97.953125 94.5 -98.640625 C 88.320312 -99.328125 81.679688 -99.671875 74.578125 -99.671875 C 67.472656 -99.671875 60.769531 -99.328125 54.46875 -98.640625 C 48.164062 -97.953125 42.664062 -96.519531 37.96875 -94.34375 C 33.28125 -92.164062 29.5625 -89.015625 26.8125 -84.890625 C 24.0625 -80.765625 22.6875 -75.265625 22.6875 -68.390625 Z M 22.6875 -64.609375 "/>
              </g>
            </g>
          </svg>
        </div>
          <h2>Escolha o seu portal</h2>
          
          <div className="login-options">
            <button 
              className="login-option"
              onClick={() => openLoginForm('student')}
            >
              <Users size={24} />
              <span>Portal do Yogi</span>
            </button>
            
            <button 
              className="login-option"
              onClick={() => openLoginForm('teacher')}
            >
              <BookOpen size={24} />
              <span>Portal do Professor</span>
            </button>
            
            <button 
              className="login-option"
              onClick={() => openLoginForm('admin')}
            >
              <Settings size={24} />
              <span>Portal do Administrador</span>
            </button>
          </div>
        </div>

        {/* Login Form Modal */}
        {showLoginForm && !showFirstTimePasswordSetup && (
          <div className="login-modal">
            <div className="login-form">
              <h3>Login - {selectedUserType === 'student' ? 'Yogi' : selectedUserType === 'teacher' ? 'Professor' : 'Administrador'}</h3>
              
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={loginCredentials.email}
                  onChange={(e) => setLoginCredentials({...loginCredentials, email: e.target.value})}
                  placeholder="Digite seu email"
                />
              </div>
              
              <div className="form-group">
                <label>Senha:</label>
                <input
                  type="password"
                  value={loginCredentials.password}
                  onChange={(e) => setLoginCredentials({...loginCredentials, password: e.target.value})}
                  placeholder="Digite sua senha"
                  onKeyPress={(e) => e.key === 'Enter' && submitLogin()}
                />
              </div>

              {/* Forgot Password Link (not for admin) */}
              {selectedUserType !== 'admin' && (
                <div style={{ textAlign: 'right', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                  <button 
                    onClick={() => {
                      setShowLoginForm(false);
                      setShowForgotPassword(true);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#81524f',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Esqueceu a senha?
                  </button>
                </div>
              )}
              
              <div className="form-actions">
                <button className="login-btn" onClick={submitLogin}>
                  <LogIn size={20} />
                  Entrar
                </button>
                <button className="cancel-btn" onClick={() => setShowLoginForm(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="login-modal">
            <div className="login-form">
              <h3>Recuperar Senha</h3>
              <p style={{ color: '#6c757d', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                Insira o seu email e receberá um link para redefinir a sua senha.
              </p>
              
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="Digite seu email"
                  onKeyPress={(e) => e.key === 'Enter' && handleForgotPassword()}
                />
              </div>
              
              <div className="form-actions">
                <button className="login-btn" onClick={handleForgotPassword}>
                  Enviar Link
                </button>
                <button 
                  className="cancel-btn" 
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* First Time Password Setup Modal */}
        {showFirstTimePasswordSetup && (
          <div className="login-modal">
            <div className="login-form">
              <h3>Primeiro Acesso - Definir Senha</h3>
              <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
                Bem-vindo! Como é o seu primeiro acesso, por favor defina a sua senha.
              </p>
              
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={loginCredentials.email}
                  disabled
                  style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                />
              </div>
              
              <div className="form-group">
                <label>Nova Senha *</label>
                <input
                  type="password"
                  value={firstTimePassword.password}
                  onChange={(e) => setFirstTimePassword({...firstTimePassword, password: e.target.value})}
                  placeholder="Mínimo 6 caracteres"
                  minLength="6"
                />
              </div>

              <div className="form-group">
                <label>Confirmar Senha *</label>
                <input
                  type="password"
                  value={firstTimePassword.confirmPassword}
                  onChange={(e) => setFirstTimePassword({...firstTimePassword, confirmPassword: e.target.value})}
                  placeholder="Digite a senha novamente"
                  minLength="6"
                />
              </div>
              
              <div className="form-actions">
                <button 
                  className="login-btn" 
                  onClick={async () => {
                    if (!firstTimePassword.password || !firstTimePassword.confirmPassword) {
                      alert('Por favor, preencha todos os campos.');
                      return;
                    }
                    if (firstTimePassword.password.length < 6) {
                      alert('A senha deve ter pelo menos 6 caracteres.');
                      return;
                    }
                    if (firstTimePassword.password !== firstTimePassword.confirmPassword) {
                      alert('As senhas não coincidem. Por favor, verifique.');
                      return;
                    }
                    await handleFirstTimePasswordSetup(loginCredentials.email, firstTimePassword.password);
                  }}
                >
                  Definir Senha e Entrar
                </button>
                <button 
                  className="cancel-btn" 
                  onClick={() => {
                    setShowFirstTimePasswordSetup(false);
                    setFirstTimePassword({ password: '', confirmPassword: '' });
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Complete Profile Modal */}
        {showCompleteProfile && (
          <div className="login-modal">
            <div className="login-form">
              <h3>🙏 Complete Seu Perfil</h3>
              <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
                Bem-vindo ao Bhāva! Para continuar, por favor complete o seu perfil.
              </p>
              
              <form onSubmit={handleCompleteProfile}>
                <div className="form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    value={window.tempAuthEmail || ''}
                    disabled
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                  <small style={{ color: '#6c757d' }}>O email vem da sua conta de autenticação</small>
      </div>

                <div className="form-group">
                  <label>Nome Completo *</label>
                  <input
                    type="text"
                    value={completeProfileData.full_name}
                    onChange={(e) => setCompleteProfileData({...completeProfileData, full_name: e.target.value})}
                    placeholder="Digite seu nome completo"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Telefone</label>
                  <input
                    type="tel"
                    value={completeProfileData.phone}
                    onChange={(e) => setCompleteProfileData({...completeProfileData, phone: e.target.value})}
                    placeholder="+351 912 345 678 (opcional)"
                  />
                </div>

                <div className="form-group">
                  <label>Tipo de Utilizador *</label>
                  <select
                    value={completeProfileData.user_type}
                    onChange={(e) => setCompleteProfileData({...completeProfileData, user_type: e.target.value})}
                    required
                  >
                    <option value="student">Yogi (Aluno)</option>
                    <option value="teacher">Professor</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                {completeProfileData.user_type === 'student' && (
                  <div className="form-group">
                    <label>Tipo de Subscrição *</label>
                    <select
                      value={completeProfileData.subscription_type}
                      onChange={(e) => setCompleteProfileData({...completeProfileData, subscription_type: e.target.value})}
                      required
                    >
                      <option value="basic">Básica</option>
                      <option value="premium">Premium</option>
                      <option value="trial">Período Experimental</option>
                    </select>
                  </div>
                )}
                
                <div className="form-actions">
                  <button type="submit" className="login-btn">
                    <User size={20} />
                    Criar Perfil
                  </button>
                  <button 
                    type="button"
                    className="cancel-btn" 
                    onClick={() => {
                      setShowCompleteProfile(false);
                      setShowLoginForm(true);
                      delete window.tempAuthUserId;
                      delete window.tempAuthEmail;
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordReset && (
          <div className="login-modal">
            <div className="login-form">
              <h3>🔒 Redefinir Senha</h3>
              <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
                Por favor, crie uma nova senha para a sua conta.
              </p>
              
              <form onSubmit={handlePasswordReset}>
                <div className="form-group">
                  <label>Nova Senha *</label>
                  <input
                    type="password"
                    value={resetPassword.password}
                    onChange={(e) => setResetPassword({...resetPassword, password: e.target.value})}
                    placeholder="Mínimo 6 caracteres"
                    minLength="6"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Confirmar Senha *</label>
                  <input
                    type="password"
                    value={resetPassword.confirmPassword}
                    onChange={(e) => setResetPassword({...resetPassword, confirmPassword: e.target.value})}
                    placeholder="Digite a senha novamente"
                    minLength="6"
                    required
                  />
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="login-btn">
                    ✅ Alterar Senha
                  </button>
                  <button 
                    type="button"
                    className="cancel-btn" 
                    onClick={() => {
                      setShowPasswordReset(false);
                      setResetPassword({ password: '', confirmPassword: '' });
                      window.location.hash = '';
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Profile Completion Modal */}
        {showProfileCompletion && (
          <div className="login-modal">
            <div className="login-form">
              <h3>📝 Complete o Seu Perfil</h3>
              <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
                Quase lá! Por favor, adicione uma breve biografia para completar o seu perfil.
              </p>
              
              <form onSubmit={handleProfileCompletion}>
                <div className="form-group">
                  <label>URL da Foto de Perfil</label>
                  <input
                    type="url"
                    value={profileCompletionData.profile_picture || ''}
                    onChange={(e) => setProfileCompletionData({...profileCompletionData, profile_picture: e.target.value})}
                    placeholder="https://exemplo.com/foto.jpg (opcional)"
                  />
                  <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                    💡 Pode adicionar uma foto mais tarde. Deixe em branco por agora.
                  </small>
                </div>

                <div className="form-group">
                  <label>Biografia *</label>
                  <textarea
                    value={profileCompletionData.bio}
                    onChange={(e) => setProfileCompletionData({...profileCompletionData, bio: e.target.value})}
                    placeholder="Conte-nos um pouco sobre si... (mínimo 10 caracteres)"
                    rows="5"
                    minLength="10"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontFamily: 'inherit',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                  <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                    {profileCompletionData.bio.length}/10 caracteres mínimo
                  </small>
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="login-btn">
                    ✅ Completar Perfil
                  </button>
                  <button 
                    type="button"
                    className="cancel-btn" 
                    onClick={() => {
                      const confirmSkip = window.confirm(
                        'Tem certeza que quer saltar esta etapa?\n\n' +
                        'Pode completar o perfil mais tarde nas configurações.'
                      );
                      if (confirmSkip) {
                        setShowProfileCompletion(false);
                        setProfileCompletionData({ profile_picture: null, bio: '' });
                        setShowLoginForm(true);
                      }
                    }}
                  >
                    Saltar por Agora
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  const navigationItems = getNavigationItems();

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <button 
            className="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div className="header-logo-container">
          <svg xmlns="http://www.w3.org/2000/svg" width="120" height="40" viewBox="0 0 750 250" className="header-logo-svg">
            <g fill="#81524f">
              <g transform="translate(0.00000265, 248.06043)">
                <path d="M 6.53125 -235.765625 C 6.53125 -238.285156 7.21875 -240.285156 8.59375 -241.765625 C 9.96875 -243.253906 11.914062 -244 14.4375 -244 C 16.957031 -244 18.960938 -243.253906 20.453125 -241.765625 C 21.941406 -240.285156 22.6875 -238.285156 22.6875 -235.765625 L 22.6875 -160.84375 C 28.875 -168.164062 36.375 -173.945312 45.1875 -178.1875 C 54.007812 -182.425781 63.804688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 L 142.625 -64.609375 C 142.625 -55.210938 140.847656 -46.390625 137.296875 -38.140625 C 133.742188 -29.898438 128.875 -22.6875 122.6875 -16.5 C 116.5 -10.3125 109.28125 -5.441406 101.03125 -1.890625 C 92.789062 1.660156 83.972656 3.4375 74.578125 3.4375 C 65.179688 3.4375 56.359375 1.660156 48.109375 -1.890625 C 39.859375 -5.441406 32.640625 -10.3125 26.453125 -16.5 C 20.273438 -22.6875 15.410156 -29.898438 11.859375 -38.140625 C 8.304688 -46.390625 6.53125 -55.210938 6.53125 -64.609375 Z M 22.6875 -64.609375 C 22.6875 -57.503906 24.0625 -50.800781 26.8125 -44.5 C 29.5625 -38.195312 33.28125 -32.695312 37.96875 -28 C 42.664062 -23.3125 48.164062 -19.59375 54.46875 -16.84375 C 60.769531 -14.09375 67.472656 -12.71875 74.578125 -12.71875 C 81.679688 -12.71875 88.378906 -14.09375 94.671875 -16.84375 C 100.972656 -19.59375 106.472656 -23.3125 111.171875 -28 C 115.867188 -32.695312 119.59375 -38.195312 122.34375 -44.5 C 125.09375 -50.800781 126.46875 -57.503906 126.46875 -64.609375 L 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.703125 -168.40625 61.171875 -167.085938 54.984375 -164.453125 C 48.796875 -161.816406 43.351562 -158.265625 38.65625 -153.796875 C 33.96875 -149.328125 30.191406 -144.054688 27.328125 -137.984375 C 24.460938 -131.910156 22.914062 -125.4375 22.6875 -118.5625 Z M 22.6875 -64.609375 "/>
              </g>
              <g transform="translate(149.137219, 248.06043)">
                <path d="M 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.703125 -168.40625 61.171875 -167.085938 54.984375 -164.453125 C 48.796875 -161.816406 43.351562 -158.265625 38.65625 -153.796875 C 33.96875 -149.328125 30.191406 -144.054688 27.328125 -137.984375 C 24.460938 -131.910156 22.914062 -125.4375 22.6875 -118.5625 L 22.6875 -4.46875 C 22.6875 -1.945312 21.941406 0 20.453125 1.375 C 18.960938 2.75 16.957031 3.4375 14.4375 3.4375 C 9.164062 3.4375 6.53125 0.800781 6.53125 -4.46875 L 6.53125 -235.765625 C 6.53125 -238.285156 7.21875 -240.285156 8.59375 -241.765625 C 9.96875 -243.253906 11.914062 -244 14.4375 -244 C 16.957031 -244 18.960938 -243.253906 20.453125 -241.765625 C 21.941406 -240.285156 22.6875 -238.285156 22.6875 -235.765625 L 22.6875 -160.84375 C 28.875 -168.164062 36.375 -173.945312 45.1875 -178.1875 C 54.007812 -182.425781 63.804688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 L 142.625 -4.46875 C 142.625 -1.945312 141.878906 0 140.390625 1.375 C 138.898438 2.75 136.894531 3.4375 134.375 3.4375 C 129.101562 3.4375 126.46875 0.800781 126.46875 -4.46875 Z M 126.46875 -116.5 "/>
              </g>
              <g transform="translate(298.274435, 248.06043)">
                <path d="M 142.625 -64.609375 C 142.625 -55.210938 140.847656 -46.390625 137.296875 -38.140625 C 133.742188 -29.898438 128.875 -22.6875 122.6875 -16.5 C 116.5 -10.3125 109.28125 -5.441406 101.03125 -1.890625 C 92.789062 1.660156 83.972656 3.4375 74.578125 3.4375 C 65.179688 3.4375 56.359375 1.660156 48.109375 -1.890625 C 39.859375 -5.441406 32.640625 -10.3125 26.453125 -16.5 C 20.273438 -22.6875 15.410156 -29.898438 11.859375 -38.140625 C 8.304688 -46.390625 6.53125 -55.210938 6.53125 -64.609375 L 6.53125 -68.390625 C 6.53125 -78.015625 8.304688 -85.921875 11.859375 -92.109375 C 15.410156 -98.296875 20.273438 -103.101562 26.453125 -106.53125 C 32.640625 -109.96875 39.859375 -112.375 48.109375 -113.75 C 56.359375 -115.125 65.179688 -115.8125 74.578125 -115.8125 C 84.890625 -115.8125 94.566406 -114.953125 103.609375 -113.234375 C 112.660156 -111.515625 120.28125 -108.363281 126.46875 -103.78125 L 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.472656 -168.40625 60.769531 -167.03125 54.46875 -164.28125 C 48.164062 -161.53125 42.664062 -157.804688 37.96875 -153.109375 C 33.28125 -148.410156 29.5625 -142.851562 26.8125 -136.4375 C 24.0625 -130.019531 22.6875 -123.257812 22.6875 -116.15625 C 22.6875 -113.632812 21.941406 -111.6875 20.453125 -110.3125 C 18.960938 -108.9375 16.957031 -108.25 14.4375 -108.25 C 9.164062 -108.25 6.53125 -111.113281 6.53125 -116.84375 C 6.53125 -126.238281 8.304688 -135.0625 11.859375 -143.3125 C 15.410156 -151.5625 20.273438 -158.71875 26.453125 -164.78125 C 32.640625 -170.851562 39.859375 -175.664062 48.109375 -179.21875 C 56.359375 -182.769531 65.179688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 Z M 22.6875 -64.609375 C 22.6875 -57.503906 24.0625 -50.800781 26.8125 -44.5 C 29.5625 -38.195312 33.28125 -32.695312 37.96875 -28 C 42.664062 -23.3125 48.164062 -19.59375 54.46875 -16.84375 C 60.769531 -14.09375 67.472656 -12.71875 74.578125 -12.71875 C 81.679688 -12.71875 88.378906 -14.09375 94.671875 -16.84375 C 100.972656 -19.59375 106.472656 -23.3125 111.171875 -28 C 115.867188 -32.695312 119.59375 -38.195312 122.34375 -44.5 C 125.09375 -50.800781 126.46875 -57.503906 126.46875 -64.609375 L 126.46875 -70.453125 C 126.238281 -76.867188 124.691406 -81.96875 121.828125 -85.75 C 118.960938 -89.53125 115.238281 -92.453125 110.65625 -94.515625 C 106.070312 -96.578125 100.6875 -97.953125 94.5 -98.640625 C 88.320312 -99.328125 81.679688 -99.671875 74.578125 -99.671875 C 67.472656 -99.671875 60.769531 -99.328125 54.46875 -98.640625 C 48.164062 -97.953125 42.664062 -96.519531 37.96875 -94.34375 C 33.28125 -92.164062 29.5625 -89.015625 26.8125 -84.890625 C 24.0625 -80.765625 22.6875 -75.265625 22.6875 -68.390625 Z M 29.90625 -226.828125 C 27.382812 -226.828125 25.378906 -227.515625 23.890625 -228.890625 C 22.398438 -230.265625 21.65625 -232.210938 21.65625 -234.734375 C 21.65625 -237.253906 22.398438 -239.253906 23.890625 -240.734375 C 25.378906 -242.222656 27.382812 -242.96875 29.90625 -242.96875 L 119.25 -242.96875 C 121.769531 -242.96875 123.71875 -242.222656 125.09375 -240.734375 C 126.46875 -239.253906 127.15625 -237.253906 127.15625 -234.734375 C 127.15625 -229.460938 124.519531 -226.828125 119.25 -226.828125 Z M 29.90625 -226.828125 "/>
              </g>
              <g transform="translate(447.411651, 248.06043)">
                <path d="M 130.59375 -180.078125 C 131.28125 -182.597656 132.539062 -184.429688 134.375 -185.578125 C 136.207031 -186.722656 138.15625 -187.066406 140.21875 -186.609375 C 142.738281 -185.460938 144.628906 -183.859375 145.890625 -181.796875 C 147.148438 -179.734375 147.09375 -177.554688 145.71875 -175.265625 L 145.71875 -175.609375 C 134.488281 -146.742188 123.375 -118.046875 112.375 -89.515625 C 101.382812 -60.992188 90.273438 -32.300781 79.046875 -3.4375 C 78.816406 -1.601562 78.070312 -0.113281 76.8125 1.03125 C 75.550781 2.175781 74.234375 2.976562 72.859375 3.4375 C 67.816406 4.582031 64.609375 2.519531 63.234375 -2.75 L 63.234375 -2.40625 C 53.835938 -31.726562 44.5 -60.9375 35.21875 -90.03125 C 25.945312 -119.132812 16.613281 -148.347656 7.21875 -177.671875 C 6.53125 -180.191406 6.703125 -182.425781 7.734375 -184.375 C 8.765625 -186.320312 10.425781 -187.523438 12.71875 -187.984375 C 15.238281 -188.441406 17.472656 -188.097656 19.421875 -186.953125 C 21.367188 -185.804688 22.570312 -184.085938 23.03125 -181.796875 C 31.269531 -155.679688 39.34375 -130.304688 47.25 -105.671875 C 55.15625 -81.046875 63.347656 -55.5625 71.828125 -29.21875 C 81.679688 -54.644531 91.585938 -79.898438 101.546875 -104.984375 C 111.515625 -130.078125 121.3125 -155.335938 130.9375 -180.765625 Z M 130.59375 -180.078125 "/>
              </g>
              <g transform="translate(600.672433, 248.06043)">
                <path d="M 142.625 -64.609375 C 142.625 -55.210938 140.847656 -46.390625 137.296875 -38.140625 C 133.742188 -29.898438 128.875 -22.6875 122.6875 -16.5 C 116.5 -10.3125 109.28125 -5.441406 101.03125 -1.890625 C 92.789062 1.660156 83.972656 3.4375 74.578125 3.4375 C 65.179688 3.4375 56.359375 1.660156 48.109375 -1.890625 C 39.859375 -5.441406 32.640625 -10.3125 26.453125 -16.5 C 20.273438 -22.6875 15.410156 -29.898438 11.859375 -38.140625 C 8.304688 -46.390625 6.53125 -55.210938 6.53125 -64.609375 L 6.53125 -68.390625 C 6.53125 -78.015625 8.304688 -85.921875 11.859375 -92.109375 C 15.410156 -98.296875 20.273438 -103.101562 26.453125 -106.53125 C 32.640625 -109.96875 39.859375 -112.375 48.109375 -113.75 C 56.359375 -115.125 65.179688 -115.8125 74.578125 -115.8125 C 84.890625 -115.8125 94.566406 -114.953125 103.609375 -113.234375 C 112.660156 -111.515625 120.28125 -108.363281 126.46875 -103.78125 L 126.46875 -116.5 C 126.46875 -123.601562 125.09375 -130.304688 122.34375 -136.609375 C 119.59375 -142.910156 115.867188 -148.410156 111.171875 -153.109375 C 106.472656 -157.804688 100.972656 -161.53125 94.671875 -164.28125 C 88.378906 -167.03125 81.679688 -168.40625 74.578125 -168.40625 C 67.472656 -168.40625 60.769531 -167.03125 54.46875 -164.28125 C 48.164062 -161.53125 42.664062 -157.804688 37.96875 -153.109375 C 33.28125 -148.410156 29.5625 -142.851562 26.8125 -136.4375 C 24.0625 -130.019531 22.6875 -123.257812 22.6875 -116.15625 C 22.6875 -113.632812 21.941406 -111.6875 20.453125 -110.3125 C 18.960938 -108.9375 16.957031 -108.25 14.4375 -108.25 C 9.164062 -108.25 6.53125 -111.113281 6.53125 -116.84375 C 6.53125 -126.238281 8.304688 -135.0625 11.859375 -143.3125 C 15.410156 -151.5625 20.273438 -158.71875 26.453125 -164.78125 C 32.640625 -170.851562 39.859375 -175.664062 48.109375 -179.21875 C 56.359375 -182.769531 65.179688 -184.546875 74.578125 -184.546875 C 83.972656 -184.546875 92.789062 -182.769531 101.03125 -179.21875 C 109.28125 -175.664062 116.5 -170.796875 122.6875 -164.609375 C 128.875 -158.429688 133.742188 -151.21875 137.296875 -142.96875 C 140.847656 -134.71875 142.625 -125.894531 142.625 -116.5 Z M 22.6875 -64.609375 C 22.6875 -57.503906 24.0625 -50.800781 26.8125 -44.5 C 29.5625 -38.195312 33.28125 -32.695312 37.96875 -28 C 42.664062 -23.3125 48.164062 -19.59375 54.46875 -16.84375 C 60.769531 -14.09375 67.472656 -12.71875 74.578125 -12.71875 C 81.679688 -12.71875 88.378906 -14.09375 94.671875 -16.84375 C 100.972656 -19.59375 106.472656 -23.3125 111.171875 -28 C 115.867188 -32.695312 119.59375 -38.195312 122.34375 -44.5 C 125.09375 -50.800781 126.46875 -57.503906 126.46875 -64.609375 L 126.46875 -70.453125 C 126.238281 -76.867188 124.691406 -81.96875 121.828125 -85.75 C 118.960938 -89.53125 115.238281 -92.453125 110.65625 -94.515625 C 106.070312 -96.578125 100.6875 -97.953125 94.5 -98.640625 C 88.320312 -99.328125 81.679688 -99.671875 74.578125 -99.671875 C 67.472656 -99.671875 60.769531 -99.328125 54.46875 -98.640625 C 48.164062 -97.953125 42.664062 -96.519531 37.96875 -94.34375 C 33.28125 -92.164062 29.5625 -89.015625 26.8125 -84.890625 C 24.0625 -80.765625 22.6875 -75.265625 22.6875 -68.390625 Z M 22.6875 -64.609375 "/>
              </g>
            </g>
          </svg>
        </div>
        </div>
        <div className="header-right">
          <span className="user-type">{userType === 'student' ? 'Yogi' : userType === 'teacher' ? 'Professor' : 'Administrador'}</span>
          <div className="user-menu-container">
            <button 
              className="user-button" 
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="User menu"
            >
            {user.email?.charAt(0).toUpperCase()}
          </button>
            
            {showUserMenu && (
              <>
                <div className="user-menu-overlay" onClick={() => setShowUserMenu(false)} />
                <div className="user-menu-dropdown">
                  <div className="user-menu-header">
                    <div className="user-menu-avatar">
                      {user.email?.charAt(0).toUpperCase()}
                    </div>
                    <div className="user-menu-info">
                      <strong>{user.name || 'Utilizador'}</strong>
                      <small>{user.email}</small>
                      <span className="user-menu-role">
                        {userType === 'student' ? '👤 Yogi' : userType === 'teacher' ? '📚 Professor' : '⚙️ Administrador'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="user-menu-divider" />
                  
                  <div className="user-menu-items">
                    <button 
                      className="user-menu-item"
                      onClick={() => {
                        setShowUserMenu(false);
                        setCurrentView('profile');
                      }}
                    >
                      <User size={18} />
                      <span>Ver Perfil</span>
                    </button>
                    
                    <button 
                      className="user-menu-item logout-item"
                      onClick={() => {
                        setShowUserMenu(false);
                        handleLogout();
                      }}
                    >
                      <LogOut size={18} />
                      <span>Terminar Sessão</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="main-container">
        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <aside className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <nav className="sidebar-nav">
            {navigationItems.map((item) => (
              <button 
                key={item.id}
                className={`nav-item ${currentView === item.id ? 'active' : ''}`}
                onClick={() => {
                  setCurrentView(item.id);
                  setIsMobileMenuOpen(false); // Close menu on mobile when item is clicked
                }}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          {/* Student Portal Views */}
          {userType === 'student' && (
            <>
              {currentView === 'home' && (
                <div className="homepage">
                  <div className="welcome-section">
                    {profileData && profileData.profilePicture ? (
                      <img src={profileData.profilePicture} alt={profileData.firstName || 'Perfil'} className="welcome-avatar" />
                    ) : (
                      <div className="welcome-avatar placeholder">
                        {`${(profileData.firstName || 'Yogi').charAt(0)}${(profileData.lastName || '').charAt(0)}`.trim()}
                      </div>
                    )}
                    <h2>Olá {profileData.firstName || 'Yogi'}, que bom ter-te de volta!</h2>
                  </div>

                  {/* Monthly Content */}
                  <section className="monthly-content">
                    <div className="mantra-section" style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      marginBottom: '1.5rem'
                    }}>
                      <h3 style={{ color: '#81524f', marginBottom: '1rem' }}>🕉️ Mantra do Mês</h3>
                      {currentMantra ? (
                      <div className="mantra-content">
                          <h4 className="mantra-title" style={{ color: '#333', marginBottom: '0.5rem' }}>
                            {currentMantra.title}
                          </h4>
                          {currentMantra.description && (
                            <p style={{ color: '#666', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                              {currentMantra.description}
                            </p>
                          )}
                          {currentMantra.audio_url && (
                            <div style={{ marginTop: '1rem' }}>
                              <audio 
                                controls 
                                src={currentMantra.audio_url} 
                                style={{ 
                                  width: '100%', 
                                  borderRadius: '8px',
                                  outline: 'none'
                                }}
                              >
                                Seu navegador não suporta áudio.
                              </audio>
                        </div>
                          )}
                      </div>
                      ) : (
                        <p style={{ color: '#999', fontStyle: 'italic' }}>
                          Nenhum mantra disponível para este mês
                        </p>
                      )}
                    </div>

                    <div className="asana-section" style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ color: '#81524f', marginBottom: '1rem' }}>🧘 Asana do Mês</h3>
                      {currentAsana ? (
                      <div className="asana-content">
                          <h4 className="asana-title" style={{ color: '#333', marginBottom: '0.25rem' }}>
                            {currentAsana.title}
                          </h4>
                          {currentAsana.sanskrit_name && (
                            <p style={{ 
                              fontStyle: 'italic', 
                              color: '#81524f', 
                              fontSize: '0.9rem',
                              marginBottom: '0.75rem'
                            }}>
                              {currentAsana.sanskrit_name}
                            </p>
                          )}
                          {currentAsana.image_url && (
                            <div className="asana-image" style={{ 
                              marginBottom: '1rem',
                              borderRadius: '8px',
                              overflow: 'hidden'
                            }}>
                              <img 
                                src={currentAsana.image_url} 
                                alt={currentAsana.title}
                                style={{
                                  width: '100%',
                                  maxHeight: '300px',
                                  objectFit: 'cover'
                                }}
                              />
                        </div>
                          )}
                          {currentAsana.description && (
                            <p style={{ 
                              color: '#666', 
                              fontSize: '0.95rem', 
                              lineHeight: '1.6',
                              marginBottom: '1rem'
                            }}>
                              {currentAsana.description}
                            </p>
                          )}
                          {currentAsana.benefits && (
                            <div style={{ marginTop: '1rem' }}>
                              <strong style={{ color: '#81524f', display: 'block', marginBottom: '0.5rem' }}>
                                Benefícios:
                              </strong>
                              <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: '1.6' }}>
                                {currentAsana.benefits}
                              </p>
                      </div>
                          )}
                          {currentAsana.instructions && (
                            <div style={{ marginTop: '1rem' }}>
                              <strong style={{ color: '#81524f', display: 'block', marginBottom: '0.5rem' }}>
                                Como fazer:
                              </strong>
                              <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: '1.6' }}>
                                {currentAsana.instructions}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p style={{ color: '#999', fontStyle: 'italic' }}>
                          Nenhum asana disponível para este mês
                        </p>
                      )}
                    </div>
                  </section>

                  {/* News Widget */}
                  <section className="news-widget">
                    <h3>Novidades</h3>
                    <div className="news-preview">
                      {news.slice(0, 2).map((article) => (
                        <div key={article.id} className="news-preview-item">
                          <h4>{article.title}</h4>
                          <p>{article.content}</p>
                          <span className="news-date">{article.date}</span>
                        </div>
                      ))}
                    </div>
                    <button 
                      className="view-all-news-btn"
                      onClick={() => setCurrentView('news')}
                    >
                      Ver todas as novidades
                    </button>
                  </section>
                </div>
              )}

              {currentView === 'classes' && (
                <div className="classes-view">
                  <h2>Aulas Disponíveis</h2>
                  <div className="classes-grid">
                    {classes.map((classItem) => (
                      <div 
                        key={classItem.id} 
                        className="class-card"
                        onClick={() => {
                          setSelectedClassFilter(classItem);
                          setCurrentView('bookings');
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <h4>{classItem.name}</h4>
                        <p>{classItem.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {currentView === 'bookings' && (
                <div className="bookings-view">
                  <h2>Marcação de Aulas</h2>
                  
                  {/* Class Filter Banner */}
                  {selectedClassFilter && (
                    <div style={{
                      background: 'linear-gradient(135deg, #81524f 0%, #a67770 100%)',
                      color: 'white',
                      padding: '1rem 1.5rem',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <div>
                        <strong>🔍 Filtrado por:</strong> {selectedClassFilter.name}
                      </div>
                      <button
                        onClick={() => setSelectedClassFilter(null)}
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: '1px solid rgba(255,255,255,0.4)',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                      >
                        Mostrar Todas as Aulas
                      </button>
                    </div>
                  )}
                  
                  <div className="bookings-container">
                    {/* Calendar (Monthly) */}
                    <div className="calendar">
                      <div className="calendar-header">
                        <button className="nav-btn" onClick={() => setSelectedDate(toIsoDate(new Date(new Date(selectedDate).setMonth(new Date(selectedDate).getMonth() - 1))))}>‹</button>
                        <h3>
                          {new Date(selectedDate).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}
                        </h3>
                        <button className="nav-btn" onClick={() => setSelectedDate(toIsoDate(new Date(new Date(selectedDate).setMonth(new Date(selectedDate).getMonth() + 1))))}>›</button>
                      </div>
                      <div className="calendar-weekdays">
                        {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((w) => (
                          <div key={w} className="weekday">{w}</div>
                        ))}
                      </div>
                      <div className="calendar-grid">
                        {(() => {
                          const { days, month } = getMonthCalendarDays(selectedDate);
                          const todayIso = toIsoDate(new Date());
                          const selectedIso = selectedDate;
                          return days.map((d) => {
                            const iso = toIsoDate(d);
                            const isOtherMonth = d.getMonth() !== month;
                            const isToday = iso === todayIso;
                            const isSelected = iso === selectedIso;
                            // Filter sessions by selected class if filter is active
                            let sessionsForDay = getSessionsForDateIso(iso);
                            if (selectedClassFilter) {
                              sessionsForDay = sessionsForDay.filter(s => s.class_id === selectedClassFilter.id);
                            }
                            const isPast = iso < todayIso;
                            return (
                              <button
                                key={iso + Math.random()}
                                className={`calendar-cell ${isOtherMonth ? 'other' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isPast ? 'disabled' : ''}`}
                                onClick={() => { 
                                  if (!isPast && sessionsForDay.length > 0) {
                                    setSelectedDate(iso);
                                    setShowDaySessionsModal(true);
                                  }
                                }}
                              >
                                <span className="date-num">{d.getDate()}</span>
                                {sessionsForDay.length > 0 && (
                                  <div className="calendar-preview">
                                    {sessionsForDay.slice(0, 1).map((s) => {
                                      const isBooked = isSessionBooked(s.id);
                                      return (
                                        <div key={s.id} className={`preview-item ${isBooked ? 'booked' : ''}`}>
                                        <span className="time">{s.start_time.substring(0,5)}</span>
                                        <span className="name">{s.classes?.name || 'Aula'}</span>
                                          {isBooked && <span className="booking-indicator">✓</span>}
                                      </div>
                                      );
                                    })}
                                    {sessionsForDay.length > 1 && (
                                      <div className={`preview-more ${sessionsForDay.some(s => isSessionBooked(s.id)) ? 'has-booking' : ''}`}>
                                        +{sessionsForDay.length - 1}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>

                    {/* Day Sessions Modal */}
                    {showDaySessionsModal && (
                      <div className="modal-overlay" onClick={() => setShowDaySessionsModal(false)}>
                        <div className="modal-content day-modal" onClick={(e) => e.stopPropagation()}>
                          <button className="modal-close" onClick={() => setShowDaySessionsModal(false)}>✕</button>
                          
                          <div className="modal-header">
                            <h2>{new Date(selectedDate).toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h2>
                                </div>
                                
                          <div className="modal-body">
                            {(() => {
                              // Apply class filter if active
                              let filteredSessions = getSessionsForDateIso(selectedDate);
                              if (selectedClassFilter) {
                                filteredSessions = filteredSessions.filter(s => s.class_id === selectedClassFilter.id);
                              }
                              
                              return filteredSessions.length === 0 ? (
                              <p style={{ color: '#6c757d', fontStyle: 'italic' }}>Sem aulas neste dia.</p>
                            ) : (
                              <div className="day-sessions-list">
                                  {filteredSessions.map((session) => {
                                  const classInfo = classes.find(c => c.id === session.class_id);
                                  const isCancelled = session.is_cancelled || session.status === 'cancelled';
                                  const isBooked = isSessionBooked(session.id);
                                  const isPastSession = isSessionPast(session.session_date, session.start_time);

                                  return (
                                    <div 
                                      key={session.id} 
                                      className={`session-management-item ${isCancelled ? 'cancelled' : ''} ${isBooked ? 'user-booked' : ''} ${isPastSession ? 'disabled' : ''}`}
                                      style={{ 
                                        marginBottom: '1rem', 
                                        cursor: isPastSession ? 'not-allowed' : 'pointer',
                                        opacity: isPastSession ? 0.5 : 1
                                      }}
                                      onClick={() => {
                                        if (isPastSession) return; // Don't open modal for past sessions
                                        setShowDaySessionsModal(false);
                                        openClassModal(
                                    session.id,
                                          session.classes?.name || classInfo?.name,
                                    {
                                            description: session.classes?.description || classInfo?.description,
                                      teacher: session.notes,
                                            location: session.is_online ? 'Online (Zoom)' : session.location || 'Estúdio Bhāva',
                                      isOnline: session.is_online,
                                            zoomLink: session.zoom_link || 'https://zoom.us/j/123456789',
                                            sessionDate: session.session_date,
                                            startTime: session.start_time,
                                            endTime: session.end_time
                                          }
                                        );
                                      }}
                                    >
                                      <div className="session-info">
                                        <div className="session-header-row">
                                          <h4>{classInfo?.name || session.classes?.name || 'Aula'}</h4>
                                          <div className="session-badges">
                                            {isPastSession && <span className="badge" style={{background: '#999'}}>⏰ PASSADA</span>}
                                            {isBooked && <span className="badge booked-badge">✓ RESERVADA</span>}
                                            {isCancelled && <span className="badge cancelled-badge">❌ CANCELADA</span>}
                                            {session.is_online && <span className="badge online">ONLINE</span>}
                                            {!session.is_online && <span className="badge presencial">PRESENCIAL</span>}
                                            {session.notes && session.notes.includes('NEW') && <span className="badge">🆕 NEW</span>}
                              </div>
                          </div>
                                        <p><strong>Horário:</strong> {session.start_time.slice(0,5)} - {session.end_time.slice(0,5)}</p>
                                        <p><strong>Professor:</strong> {session.notes ? session.notes.replace(/ - (Presencial|Online|NEW).*$/, '') : 'N/A'}</p>
                                        <p><strong>Local:</strong> {session.is_online ? 'Online (Zoom)' : session.location || 'Estúdio Bhāva'}</p>
                                        {!isCancelled && (
                                          <p><strong>Vagas:</strong> {session.current_participants || 0} / {session.max_participants} inscritos</p>
                        )}
                      </div>
                    </div>
                                  );
                                })}
                              </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Class Details Modal */}
                    {showClassModal && (
                      <div className="modal-overlay" onClick={closeClassModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                          <button className="modal-close" onClick={closeClassModal}>✕</button>
                          
                          <div className="modal-header">
                            <h2>{classDetails.name}</h2>
                            <div className="class-badge">
                              {classDetails.isOnline ? <span className="online-badge">🔗 ONLINE</span> : <span className="in-person-badge">📍 PRESENCIAL</span>}
                            </div>
                          </div>

                          <div className="modal-body">
                            {/* Class Information */}
                            <div className="class-info-section">
                              <h3>Informações da Aula</h3>
                              <div className="info-grid">
                                <div className="info-item">
                                  <strong>Professor:</strong>
                                  <p>{classDetails.teacher}</p>
                                </div>
                                <div className="info-item">
                                  <strong>Local:</strong>
                                  <p>{classDetails.location}</p>
                                </div>
                                {classDetails.isOnline && (
                                  <div className="info-item">
                                    <strong>Link de Zoom:</strong>
                                    <a href={classDetails.zoomLink} target="_blank" rel="noopener noreferrer">{classDetails.zoomLink}</a>
                                  </div>
                                )}
                              </div>
                              <div className="info-item full-width">
                                <strong>Descrição:</strong>
                                <p>{classDetails.description}</p>
                              </div>
                            </div>

                            {/* Booked Students */}
                            <div className="booked-students-modal-section">
                              <h3>Alunos Já Inscritos ({classDetails.bookedStudents.length})</h3>
                              <div className="modal-students-avatars">
                                {classDetails.bookedStudents.map((student, index) => (
                                  <div key={index} className="modal-student-item">
                                    <div className="modal-student-avatar">
                                      {student.picture ? (
                                        <img 
                                          src={student.picture} 
                                          alt={student.name}
                                          className="student-picture"
                                        />
                                      ) : (
                                        <div className="student-avatar-placeholder">{student.initials}</div>
                                      )}
                                    </div>
                                    <span className="modal-student-name">{student.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="modal-footer">
                            <button className="cancel-btn" onClick={closeClassModal}>Fechar</button>
                            {(() => {
                              const isPastSession = isSessionPast(classDetails.sessionDate, classDetails.startTime);
                              
                              if (isPastSession) {
                                return (
                                  <button className="save-profile-btn" disabled style={{opacity: 0.5, cursor: 'not-allowed'}}>
                                    ⏰ Aula Passada
                                  </button>
                                );
                              }
                              
                              if (isSessionBooked(classDetails.id)) {
                                return (
                              <button className="cancel-booking-btn" onClick={handleCancelBooking}>
                                ❌ Cancelar Reserva
                              </button>
                                );
                              }
                              
                              return (
                              <button className="save-profile-btn" onClick={handleBookSession}>
                                ✓ Confirmar Reserva
                              </button>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {currentView === 'news' && (
                <div className="news-view">
                  <h2>Novidades</h2>
                  <div className="news-list">
                    {news.map((article) => (
                      <div 
                        key={article.id} 
                        className="news-item"
                        onClick={() => openNewsModal(article)}
                      >
                        <h4>{article.title}</h4>
                        <p>{article.content}</p>
                        <span className="news-date">{article.date}</span>
                      </div>
                    ))}
                  </div>

                  {/* News Detail Modal */}
                  {showNewsModal && (
                    <div className="modal-overlay" onClick={closeNewsModal}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeNewsModal}>✕</button>
                        
                        <div className="news-modal-image">
                          <img src={selectedNews.image} alt={selectedNews.title} />
                        </div>

                        <div className="news-modal-body">
                          <div className="news-modal-header">
                            <h2>{selectedNews.title}</h2>
                            <span className="news-modal-date">{selectedNews.date}</span>
                          </div>

                          <div className="news-modal-content">
                            <p>{selectedNews.content}</p>
                          </div>
                        </div>

                        <div className="modal-footer">
                          <button className="cancel-btn" onClick={closeNewsModal}>Fechar</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentView === 'profile' && (
                <div className="profile-view">
                  <h2>O meu perfil</h2>
                  
                  <div className="profile-content" style={{ marginTop: '1.5rem' }}>
                    {/* Profile Picture Section */}
                    <div className="profile-picture-section">
                      <h3>Foto de perfil</h3>
                      <div className="profile-picture-container">
                        {profileData.profilePicture ? (
                          <img 
                            src={profileData.profilePicture} 
                            alt="Profile" 
                            className="profile-picture"
                          />
                        ) : (
                          <div className="profile-picture-placeholder">
                            <User size={48} />
                            <span>Adicionar foto</span>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePictureUpload}
                          className="profile-picture-input"
                          id="profile-picture-input"
                        />
                        <label htmlFor="profile-picture-input" className="upload-button">
                          {profileData.profilePicture ? 'Alterar foto' : 'Escolher foto'}
                        </label>
                      </div>
                    </div>

                    {/* Personal Information */}
                    <div className="profile-form">
                      <h3>Informações pessoais</h3>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Primeiro nome:</label>
                          <input
                            type="text"
                            value={profileData.firstName}
                            onChange={(e) => handleProfileUpdate('firstName', e.target.value)}
                            placeholder="Digite o seu primeiro nome"
                          />
                        </div>
                        <div className="form-group">
                          <label>Último nome:</label>
                          <input
                            type="text"
                            value={profileData.lastName}
                            onChange={(e) => handleProfileUpdate('lastName', e.target.value)}
                            placeholder="Digite o seu último nome"
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Email:</label>
                        <input
                          type="email"
                          value={profileData.email}
                          readOnly
                          placeholder="O seu email (apenas leitura)"
                        />
                      </div>
                    </div>

                    {/* Subscription Information */}
                    <div className="subscription-info">
                      <h3>As minhas subscrições</h3>
                      
                      {userSubscriptions.length === 0 ? (
                        <div className="no-subscriptions-message">
                          <p>❌ Não tem subscrições ativas no momento.</p>
                          <p style={{ fontSize: '0.9em', color: '#666', marginTop: '0.5rem' }}>
                            💡 Contacte a administração para ativar uma subscrição e começar a reservar aulas!
                          </p>
                          </div>
                      ) : (
                        <div className="subscriptions-list">
                          {userSubscriptions.map((sub, index) => (
                            <div key={sub.subscription_id || index} className="subscription-card">
                              <div className="subscription-header">
                                <h4>{sub.plan_name}</h4>
                                <span className={`subscription-status ${sub.is_active ? 'active' : 'inactive'}`}>
                                  {sub.is_active ? '✓ Ativo' : '✕ Inativo'}
                                </span>
                        </div>
                              
                              <div className="subscription-details">
                                {/* Plan Type */}
                                <div className="detail-row">
                                  <span className="detail-label">Tipo:</span>
                                  <span className="detail-value">
                                    {sub.class_type === 'online' ? '🌐 Online' : 
                                     sub.class_type === 'presencial' ? '📍 Presencial' : 
                                     '🌐📍 Online e Presencial'}
                                  </span>
                          </div>

                                {/* Weekly Limit or Credits */}
                                {sub.weekly_limit !== null ? (
                                  <>
                                    <div className="detail-row">
                                      <span className="detail-label">Limite semanal:</span>
                                      <span className="detail-value">
                                        {sub.weekly_limit} aula{sub.weekly_limit !== 1 ? 's' : ''} por semana
                                      </span>
                        </div>
                                    <div className="detail-row highlight">
                                      <span className="detail-label">Esta semana:</span>
                                      <span className="detail-value strong">
                                        {sub.used_this_week || 0} / {sub.weekly_limit} aulas usadas
                                        {sub.remaining_this_week !== null && sub.remaining_this_week > 0 && (
                                          <span className="remaining-badge">{sub.remaining_this_week} disponíveis</span>
                                        )}
                                      </span>
                      </div>
                                  </>
                                ) : sub.total_credits !== null ? (
                                  <>
                                    <div className="detail-row">
                                      <span className="detail-label">Pack de créditos:</span>
                                      <span className="detail-value">
                                        {sub.total_credits} aulas
                                      </span>
                      </div>
                                    <div className="detail-row highlight">
                                      <span className="detail-label">Créditos restantes:</span>
                                      <span className="detail-value strong">
                                        {sub.remaining_credits || 0} / {sub.total_credits}
                                        {sub.remaining_credits > 0 && (
                                          <span className="remaining-badge">{sub.remaining_credits} disponíveis</span>
                                        )}
                                      </span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="detail-row highlight">
                                    <span className="detail-label">Limite:</span>
                                    <span className="detail-value strong">
                                      ♾️ Ilimitado
                                    </span>
                                  </div>
                                )}

                                {/* Validity Dates */}
                                {sub.end_date && (
                                  <div className="detail-row">
                                    <span className="detail-label">Válido até:</span>
                                    <span className="detail-value">
                                      {new Date(sub.end_date).toLocaleDateString('pt-PT', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                )}

                                {/* Status Message */}
                                <div className="status-message">
                                  {sub.status_message}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="subscription-help-text">
                        <p style={{ fontSize: '0.85em', color: '#666', marginTop: '1rem' }}>
                          ℹ️ As subscrições semanais renovam todas as segundas-feiras.
                          Os packs de créditos têm data de validade.
                        </p>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="profile-actions">
                      <button className="save-profile-btn" onClick={saveProfile}>
                        Guardar alterações
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Teacher Portal Views */}
          {userType === 'teacher' && (
            <>
              {currentView === 'home' && (
                <div className="homepage">
                  <div className="welcome-section">
                    {profileData && profileData.profilePicture ? (
                      <img src={profileData.profilePicture} alt={profileData.firstName || 'Perfil'} className="welcome-avatar" />
                    ) : (
                      <div className="welcome-avatar placeholder">
                        {`${(profileData.firstName || user?.name?.split(' ')[0] || 'Professor').charAt(0)}${(profileData.lastName || user?.name?.split(' ')[1] || '').charAt(0)}`.trim()}
                        </div>
                    )}
                    <h2>Olá {profileData.firstName || user?.name?.split(' ')[0] || 'Professor'}, que bom ter-te de volta!</h2>
                      </div>

                  {/* Monthly Content */}
                  <section className="monthly-content">
                    <div className="mantra-section" style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      marginBottom: '1.5rem'
                    }}>
                      <h3 style={{ color: '#81524f', marginBottom: '1rem' }}>🕉️ Mantra do Mês</h3>
                      {currentMantra ? (
                      <div className="mantra-content">
                          <h4 className="mantra-title" style={{ color: '#333', marginBottom: '0.5rem' }}>
                            {currentMantra.title}
                          </h4>
                          {currentMantra.description && (
                            <p style={{ color: '#666', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                              {currentMantra.description}
                            </p>
                          )}
                          {currentMantra.audio_url && (
                            <div style={{ marginTop: '1rem' }}>
                              <audio 
                                controls 
                                src={currentMantra.audio_url} 
                                style={{ 
                                  width: '100%', 
                                  borderRadius: '8px',
                                  outline: 'none'
                                }}
                              >
                                Seu navegador não suporta áudio.
                              </audio>
                        </div>
                          )}
                      </div>
                      ) : (
                        <p style={{ color: '#999', fontStyle: 'italic' }}>
                          Nenhum mantra disponível para este mês
                        </p>
                      )}
                    </div>

                    <div className="asana-section" style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ color: '#81524f', marginBottom: '1rem' }}>🧘 Asana do Mês</h3>
                      {currentAsana ? (
                        <div className="asana-content">
                          <h4 className="asana-title" style={{ color: '#333', marginBottom: '0.25rem' }}>
                            {currentAsana.title}
                          </h4>
                          {currentAsana.sanskrit_name && (
                            <p style={{ 
                              fontStyle: 'italic', 
                              color: '#81524f', 
                              fontSize: '0.9rem',
                              marginBottom: '0.75rem'
                            }}>
                              {currentAsana.sanskrit_name}
                            </p>
                          )}
                          {currentAsana.image_url && (
                            <div className="asana-image" style={{ 
                              marginBottom: '1rem',
                              borderRadius: '8px',
                              overflow: 'hidden'
                            }}>
                              <img 
                                src={currentAsana.image_url} 
                                alt={currentAsana.title}
                                style={{
                                  width: '100%',
                                  maxHeight: '300px',
                                  objectFit: 'cover'
                                }}
                              />
                  </div>
                          )}
                          {currentAsana.description && (
                            <p style={{ 
                              color: '#666', 
                              fontSize: '0.95rem', 
                              lineHeight: '1.6',
                              marginBottom: '1rem'
                            }}>
                              {currentAsana.description}
                            </p>
                          )}
                          {currentAsana.benefits && (
                            <div style={{ marginTop: '1rem' }}>
                              <strong style={{ color: '#81524f', display: 'block', marginBottom: '0.5rem' }}>
                                Benefícios:
                              </strong>
                              <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: '1.6' }}>
                                {currentAsana.benefits}
                              </p>
                </div>
              )}
                          {currentAsana.instructions && (
                            <div style={{ marginTop: '1rem' }}>
                              <strong style={{ color: '#81524f', display: 'block', marginBottom: '0.5rem' }}>
                                Como fazer:
                              </strong>
                              <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: '1.6' }}>
                                {currentAsana.instructions}
                              </p>
                </div>
               )}
                        </div>
                      ) : (
                        <p style={{ color: '#999', fontStyle: 'italic' }}>
                          Nenhum asana disponível para este mês
                        </p>
                      )}
                    </div>
                  </section>

                  {/* News Section */}
                  <section className="news-preview">
                    <h3>Últimas Notícias</h3>
                    {news.length > 0 ? (
                      <div className="news-cards-preview">
                        {news.slice(0, 3).map((newsItem) => (
                          <div key={newsItem.id} className="news-card-small">
                            <h4>{newsItem.title}</h4>
                            <p>{newsItem.content.substring(0, 100)}...</p>
                            <small>{new Date(newsItem.published_at).toLocaleDateString('pt-PT')}</small>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>Sem novidades no momento</p>
                    )}
                  </section>
                </div>
              )}

              {currentView === 'my-classes' && (
                <div className="bookings-view">
                  <h2>As Minhas Aulas 📚</h2>
                  <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                    Aulas onde você é o professor
                  </p>
                  <div className="bookings-container">
                    {/* Calendar (Monthly) */}
                    <div className="calendar">
                    <div className="calendar-header">
                        <button className="nav-btn" onClick={() => setSelectedDate(toIsoDate(new Date(new Date(selectedDate).setMonth(new Date(selectedDate).getMonth() - 1))))}>‹</button>
                        <h3>
                          {new Date(selectedDate).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}
                        </h3>
                        <button className="nav-btn" onClick={() => setSelectedDate(toIsoDate(new Date(new Date(selectedDate).setMonth(new Date(selectedDate).getMonth() + 1))))}>›</button>
                    </div>
                      <div className="calendar-weekdays">
                        {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((w) => (
                          <div key={w} className="weekday">{w}</div>
                        ))}
                    </div>
                      <div className="calendar-grid">
                        {(() => {
                          const myTeacherId = teachers.find(t => t.user_id === user.id)?.id;
                          const { days, month } = getMonthCalendarDays(selectedDate);
                          const todayIso = toIsoDate(new Date());
                          const selectedIso = selectedDate;
                          return days.map((d) => {
                            const iso = toIsoDate(d);
                            const isOtherMonth = d.getMonth() !== month;
                            const isToday = iso === todayIso;
                            const isSelected = iso === selectedIso;
                            // Show only sessions where teacher IS teaching
                            const sessionsForDay = getSessionsForDateIso(iso).filter(s => s.teacher_id === myTeacherId);
                            const isPast = iso < todayIso;
                            return (
                              <button
                                key={iso + Math.random()}
                                className={`calendar-cell ${isOtherMonth ? 'other' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isPast ? 'disabled' : ''}`}
                                onClick={() => { 
                                  if (sessionsForDay.length > 0) {
                                    setSelectedDate(iso);
                                    setShowDaySessionsModal(true);
                                  }
                                }}
                              >
                                <span className="date-num">{d.getDate()}</span>
                                {sessionsForDay.length > 0 && (
                                  <div className="calendar-preview">
                                    {sessionsForDay.slice(0, 1).map((s) => (
                                      <div key={s.id} className="preview-item">
                                        <span className="time">{s.start_time.substring(0,5)}</span>
                                        <span className="name">{s.classes?.name || 'Aula'}</span>
                  </div>
                                    ))}
                                    {sessionsForDay.length > 1 && (
                                      <div className="preview-more">+{sessionsForDay.length - 1}</div>
                                    )}
                </div>
              )}
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>

                    {/* Day Sessions Modal - Teacher's Classes */}
                    {showDaySessionsModal && (
                      <div className="modal-overlay" onClick={() => setShowDaySessionsModal(false)}>
                        <div className="modal-content day-modal" onClick={(e) => e.stopPropagation()}>
                          <button className="modal-close" onClick={() => setShowDaySessionsModal(false)}>✕</button>
                          
                          <div className="modal-header">
                            <h2>{new Date(selectedDate).toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h2>
                    </div>
                                
                          <div className="modal-body">
                            {(() => {
                              const myTeacherId = teachers.find(t => t.user_id === user.id)?.id;
                              const mySessions = getSessionsForDateIso(selectedDate).filter(s => s.teacher_id === myTeacherId);
                              
                              if (mySessions.length === 0) {
                                return <p style={{ color: '#6c757d', fontStyle: 'italic' }}>Sem aulas suas neste dia.</p>;
                              }
                              
                              return (
                                <div className="day-sessions-list">
                                  {mySessions.map((session) => {
                                    const classInfo = classes.find(c => c.id === session.class_id);
                                    const isCancelled = session.is_cancelled || session.status === 'cancelled';
                                    const bookingsForSession = sessionBookings[session.id] || [];

                                    return (
                                      <div 
                                        key={session.id} 
                                        className={`session-management-item ${isCancelled ? 'cancelled' : ''}`}
                                        style={{ marginBottom: '1rem' }}
                                      >
                                        <div className="session-info">
                                          <div className="session-header-row">
                                            <h4>{classInfo?.name || session.classes?.name || 'Aula'}</h4>
                                            <div className="session-badges">
                                              {isCancelled && <span className="badge cancelled-badge">❌ CANCELADA</span>}
                                              {session.is_online && <span className="badge online">ONLINE</span>}
                                              {!session.is_online && <span className="badge presencial">PRESENCIAL</span>}
                                            </div>
                                          </div>
                                          <p><strong>Horário:</strong> {session.start_time.slice(0,5)} - {session.end_time.slice(0,5)}</p>
                                          <p><strong>Local:</strong> {session.is_online ? 'Online (Zoom)' : session.location || 'Estúdio Bhāva'}</p>
                                          <p><strong>Alunos Inscritos:</strong> {bookingsForSession.length} / {session.max_participants}</p>
                                          
                                          {/* Enrolled Students */}
                                          {bookingsForSession.length > 0 && (
                                            <div style={{ marginTop: '0.75rem' }}>
                                              <strong style={{ fontSize: '0.9rem' }}>Alunos:</strong>
                                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                                                {bookingsForSession.map(booking => (
                                                  <span 
                                                    key={booking.id}
                                                    style={{
                                                      padding: '0.25rem 0.75rem',
                                                      backgroundColor: '#e9ecef',
                                                      borderRadius: '16px',
                                                      fontSize: '0.85rem'
                                                    }}
                                                  >
                                                    {booking.user_profile?.full_name || 'Aluno'}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {/* Cancel Button */}
                                          {!isCancelled && (
                                            <button
                                              onClick={async () => {
                                                if (!window.confirm('Tem certeza que deseja cancelar esta aula?')) return;
                                                
                                                try {
                                                  const { error } = await supabase
                                                    .from('class_sessions')
                                                    .update({ is_cancelled: true, status: 'cancelled' })
                                                    .eq('id', session.id);
                                                  
                                                  if (error) throw error;
                                                  
                                                  alert('Aula cancelada com sucesso!');
                                                  // Refresh data
                                                  const { data: updatedSessions } = await supabase
                                                    .from('class_sessions')
                                                    .select('*, classes(*)')
                                                    .order('session_date', { ascending: true });
                                                  setClassSessions(updatedSessions || []);
                                                  setShowDaySessionsModal(false);
                                                } catch (error) {
                                                  console.error('Error cancelling session:', error);
                                                  alert('Erro ao cancelar aula: ' + error.message);
                                                }
                                              }}
                                              style={{
                                                marginTop: '1rem',
                                                padding: '0.5rem 1rem',
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: 'bold'
                                              }}
                                            >
                                              Cancelar Esta Aula
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {currentView === 'book-classes' && (
                <div className="bookings-view">
                  <h2>Marcar Aulas (como aluno) 🧘</h2>
                  <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                    Aqui pode marcar aulas onde não é o professor
                  </p>
                  <div className="bookings-container">
                    {/* Calendar (Monthly) */}
                    <div className="calendar">
                      <div className="calendar-header">
                        <button className="nav-btn" onClick={() => setSelectedDate(toIsoDate(new Date(new Date(selectedDate).setMonth(new Date(selectedDate).getMonth() - 1))))}>‹</button>
                        <h3>
                          {new Date(selectedDate).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}
                        </h3>
                        <button className="nav-btn" onClick={() => setSelectedDate(toIsoDate(new Date(new Date(selectedDate).setMonth(new Date(selectedDate).getMonth() + 1))))}>›</button>
                      </div>
                      <div className="calendar-weekdays">
                        {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((w) => (
                          <div key={w} className="weekday">{w}</div>
                        ))}
                      </div>
                      <div className="calendar-grid">
                        {(() => {
                          const myTeacherId = teachers.find(t => t.user_id === user.id)?.id;
                          const { days, month } = getMonthCalendarDays(selectedDate);
                          const todayIso = toIsoDate(new Date());
                          const selectedIso = selectedDate;
                          return days.map((d) => {
                            const iso = toIsoDate(d);
                            const isOtherMonth = d.getMonth() !== month;
                            const isToday = iso === todayIso;
                            const isSelected = iso === selectedIso;
                            // Filter out sessions where teacher is teaching
                            const sessionsForDay = getSessionsForDateIso(iso).filter(s => s.teacher_id !== myTeacherId);
                            const isPast = iso < todayIso;
                            return (
                              <button
                                key={iso + Math.random()}
                                className={`calendar-cell ${isOtherMonth ? 'other' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isPast ? 'disabled' : ''}`}
                                onClick={() => { 
                                  if (!isPast && sessionsForDay.length > 0) {
                                    setSelectedDate(iso);
                                    setShowDaySessionsModal(true);
                                  }
                                }}
                              >
                                <span className="date-num">{d.getDate()}</span>
                                {sessionsForDay.length > 0 && (
                                  <div className="calendar-preview">
                                    {sessionsForDay.slice(0, 1).map((s) => (
                                      <div key={s.id} className="preview-item">
                                        <span className="time">{s.start_time.substring(0,5)}</span>
                                        <span className="name">{s.classes?.name || 'Aula'}</span>
                                      </div>
                                    ))}
                                    {sessionsForDay.length > 1 && (
                                      <div className="preview-more">+{sessionsForDay.length - 1}</div>
                                    )}
                                  </div>
                                )}
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>

                    {/* Day Sessions Modal */}
                    {showDaySessionsModal && (
                      <div className="modal-overlay" onClick={() => setShowDaySessionsModal(false)}>
                        <div className="modal-content day-modal" onClick={(e) => e.stopPropagation()}>
                          <button className="modal-close" onClick={() => setShowDaySessionsModal(false)}>✕</button>
                          
                          <div className="modal-header">
                            <h2>{new Date(selectedDate).toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h2>
                                </div>
                                
                          <div className="modal-body">
                            {(() => {
                              const myTeacherId = teachers.find(t => t.user_id === user.id)?.id;
                              const availableSessions = getSessionsForDateIso(selectedDate).filter(s => s.teacher_id !== myTeacherId);
                              
                              if (availableSessions.length === 0) {
                                return <p style={{ color: '#6c757d', fontStyle: 'italic' }}>Sem aulas disponíveis neste dia.</p>;
                              }
                              
                              return (
                                <div className="day-sessions-list">
                                  {availableSessions.map((session) => {
                                    const classInfo = classes.find(c => c.id === session.class_id);
                                    const isCancelled = session.is_cancelled || session.status === 'cancelled';
                                    const isBooked = isSessionBooked(session.id);
                                    const isPastSession = isSessionPast(session.session_date, session.start_time);

                                    return (
                                      <div 
                                        key={session.id} 
                                        className={`session-management-item ${isCancelled ? 'cancelled' : ''} ${isBooked ? 'user-booked' : ''} ${isPastSession ? 'disabled' : ''}`}
                                        style={{ 
                                          marginBottom: '1rem', 
                                          cursor: isPastSession ? 'not-allowed' : 'pointer',
                                          opacity: isPastSession ? 0.5 : 1
                                        }}
                                        onClick={() => {
                                          if (isPastSession) return; // Don't open modal for past sessions
                                          setShowDaySessionsModal(false);
                                          openClassModal(
                                      session.id,
                                            session.classes?.name || classInfo?.name,
                                      {
                                              description: session.classes?.description || classInfo?.description,
                                        teacher: session.notes,
                                              location: session.is_online ? 'Online (Zoom)' : session.location || 'Estúdio Bhāva',
                                        isOnline: session.is_online,
                                              zoomLink: session.zoom_link || 'https://zoom.us/j/123456789',
                                              sessionDate: session.session_date,
                                              startTime: session.start_time,
                                              endTime: session.end_time
                                            }
                                          );
                                        }}
                                      >
                                        <div className="session-info">
                                          <div className="session-header-row">
                                            <h4>{classInfo?.name || session.classes?.name || 'Aula'}</h4>
                                            <div className="session-badges">
                                              {isPastSession && <span className="badge" style={{background: '#999'}}>⏰ PASSADA</span>}
                                              {isBooked && <span className="badge booked-badge">✓ RESERVADA</span>}
                                              {isCancelled && <span className="badge cancelled-badge">❌ CANCELADA</span>}
                                              {session.is_online && <span className="badge online">ONLINE</span>}
                                              {!session.is_online && <span className="badge presencial">PRESENCIAL</span>}
                                              {session.notes && session.notes.includes('NEW') && <span className="badge">🆕 NEW</span>}
                                </div>
                            </div>
                                          <p><strong>Horário:</strong> {session.start_time.slice(0,5)} - {session.end_time.slice(0,5)}</p>
                                          <p><strong>Professor:</strong> {session.notes ? session.notes.replace(/ - (Presencial|Online|NEW).*$/, '') : 'N/A'}</p>
                                          <p><strong>Local:</strong> {session.is_online ? 'Online (Zoom)' : session.location || 'Estúdio Bhāva'}</p>
                                          {!isCancelled && (
                                            <p><strong>Vagas:</strong> {session.current_participants || 0} / {session.max_participants} inscritos</p>
                          )}
                        </div>
                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Class Details Modal - Same as student view */}
                    {showClassModal && (
                      <div className="modal-overlay" onClick={closeClassModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                          <button className="modal-close" onClick={closeClassModal}>✕</button>
                          
                          <div className="modal-header">
                            <h2>{classDetails.name}</h2>
                            <div className="class-badge">
                              {classDetails.isOnline ? <span className="online-badge">🔗 ONLINE</span> : <span className="in-person-badge">📍 PRESENCIAL</span>}
                            </div>
                          </div>

                          <div className="modal-body">
                            {/* Class Information */}
                            <div className="class-info-section">
                              <h3>Informações da Aula</h3>
                              <div className="info-grid">
                                <div className="info-item">
                                  <strong>Professor:</strong>
                                  <p>{classDetails.teacher}</p>
                                </div>
                                <div className="info-item">
                                  <strong>Local:</strong>
                                  <p>{classDetails.location}</p>
                                </div>
                                {classDetails.isOnline && (
                                  <div className="info-item">
                                    <strong>Link de Zoom:</strong>
                                    <a href={classDetails.zoomLink} target="_blank" rel="noopener noreferrer">{classDetails.zoomLink}</a>
                                  </div>
                                )}
                              </div>
                              <div className="info-item full-width">
                                <strong>Descrição:</strong>
                                <p>{classDetails.description}</p>
                              </div>
                            </div>

                            {/* Booked Students */}
                            <div className="booked-students-modal-section">
                              <h3>Alunos Já Inscritos ({classDetails.bookedStudents.length})</h3>
                              <div className="modal-students-avatars">
                                {classDetails.bookedStudents.map((student, index) => (
                                  <div key={index} className="modal-student-item">
                                    <div className="modal-student-avatar">
                                      {student.picture ? (
                                        <img 
                                          src={student.picture} 
                                          alt={student.name}
                                          className="student-picture"
                                        />
                                      ) : (
                                        <div className="student-avatar-placeholder">{student.initials}</div>
                                      )}
                                    </div>
                                    <span className="modal-student-name">{student.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Class Time Info */}
                            <div className="class-time-info">
                              <div className="time-item">
                                <strong>📅 Data:</strong>
                                <p>{new Date(classDetails.sessionDate).toLocaleDateString('pt-PT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                              </div>
                              <div className="time-item">
                                <strong>⏰ Horário:</strong>
                                <p>{classDetails.startTime?.slice(0,5)} - {classDetails.endTime?.slice(0,5)}</p>
                              </div>
                            </div>
                          </div>

                          <div className="modal-footer">
                            {(() => {
                              const sessionId = classDetails.id;
                              const isBooked = isSessionBooked(sessionId);
                              const sessionData = classSessions.find(s => s.id === sessionId);
                              const bookingsForSession = sessionBookings[sessionId] || [];
                              const isFull = bookingsForSession.length >= (sessionData?.max_participants || 0);
                              const isCancelled = sessionData?.is_cancelled || sessionData?.status === 'cancelled';

                              if (isCancelled) {
                                return (
                                  <button className="book-btn disabled" disabled>
                                    ❌ Aula Cancelada
                                  </button>
                                );
                              }

                              if (isBooked) {
                                return (
                                  <>
                                    <span style={{ color: '#28a745', fontWeight: 'bold', marginRight: '1rem' }}>✓ Já marcou esta aula</span>
                                    <button 
                                      onClick={async () => {
                                        try {
                                          const bookingToCancel = userBookings.find(b => b.class_session_id === sessionId && b.status === 'confirmed');
                                          if (!bookingToCancel) {
                                            alert('Marcação não encontrada.');
                                            return;
                                          }

                                          const { error } = await supabase
                                            .from('bookings')
                                            .update({ status: 'cancelled' })
                                            .eq('id', bookingToCancel.id);
                                          
                                          if (error) throw error;
                                          
                                          alert('Marcação cancelada!');
                                          // Refresh bookings using the proper fetch function
                                          await fetchUserBookings(user.id);
                                          closeClassModal();
                                        } catch (error) {
                                          console.error('Error cancelling booking:', error);
                                          alert('Erro ao cancelar marcação: ' + error.message);
                                        }
                                      }}
                                      className="cancel-booking-btn"
                                    >
                                      Cancelar Marcação
                                    </button>
                                  </>
                                );
                              }

                              if (isFull) {
                                return (
                                  <button className="book-btn disabled" disabled>
                                    Lotado
                                  </button>
                                );
                              }

                              return (
                                <button 
                                  onClick={async () => {
                                    try {
                                      const { error } = await supabase
                                        .from('bookings')
                                        .insert({
                                          user_id: user.id,
                                          class_session_id: sessionId,
                                          status: 'confirmed'
                                        });
                                      
                                      if (error) throw error;
                                      
                                      alert('Aula marcada com sucesso!');
                                      const { data: bookings } = await supabase
                                        .from('bookings')
                                        .select('*')
                                        .eq('user_id', user.id);
                                      setUserBookings(bookings || []);
                                      closeClassModal();
                                    } catch (error) {
                                      console.error('Error booking class:', error);
                                      alert('Erro ao marcar aula: ' + error.message);
                                    }
                                  }}
                                  className="book-btn"
                                >
                                  Marcar Aula
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {currentView === 'profile' && (
                <div className="profile-view">
                  <h2>O meu perfil</h2>
                  <div className="profile-container">
                    {/* Profile Picture */}
                    <div className="profile-picture-section">
                      {profileData.profilePicture ? (
                        <img src={profileData.profilePicture} alt="Perfil" className="profile-picture" />
                      ) : (
                        <div className="profile-picture-placeholder">
                          {profileData.firstName?.charAt(0) || 'P'}{profileData.lastName?.charAt(0) || ''}
                        </div>
                      )}
                      <div className="upload-section">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setProfileData({...profileData, profilePicture: reader.result});
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          style={{ display: 'none' }}
                          id="profile-picture-input"
                        />
                        <label htmlFor="profile-picture-input" className="upload-button">
                          {profileData.profilePicture ? 'Alterar foto' : 'Escolher foto'}
                        </label>
                      </div>
                    </div>

                    {/* Personal Information */}
                    <div className="profile-form">
                      <h3>Informações pessoais</h3>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Primeiro nome:</label>
                          <input
                            type="text"
                            value={profileData.firstName}
                            onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                            placeholder="Digite o seu primeiro nome"
                          />
                        </div>
                        <div className="form-group">
                          <label>Último nome:</label>
                          <input
                            type="text"
                            value={profileData.lastName}
                            onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                            placeholder="Digite o seu último nome"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Email:</label>
                        <input
                          type="email"
                          value={profileData.email}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                        <small>O email não pode ser alterado</small>
                      </div>

                      <button className="save-profile-btn">Guardar alterações</button>
                    </div>
                  </div>
                </div>
              )}

              {currentView === 'news' && (
                <div className="news-view">
                  <h2>Novidades</h2>
                  {news.length > 0 ? (
                    <div className="news-grid">
                      {news.map((newsItem) => (
                        <div key={newsItem.id} className="news-card" onClick={() => {
                          setSelectedNews(newsItem);
                          setShowNewsModal(true);
                        }}>
                          <h3>{newsItem.title}</h3>
                          <p>{newsItem.content.substring(0, 150)}...</p>
                          <small>{new Date(newsItem.published_at).toLocaleDateString('pt-PT')}</small>
                      </div>
                    ))}
                  </div>
                  ) : (
                    <p>Sem novidades no momento.</p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Admin Portal Views */}
          {userType === 'admin' && (
            <>
              {currentView === 'home' && (
                <div className="admin-dashboard">
                  <h2 style={{ color: '#81524f' }}>O meu Bhāva</h2>
                  
                  <div className="dashboard-stats-container">
                    {/* Yogis Count */}
                    <div className="stat-card">
                      <h4>Yogis</h4>
                      <p className="stat-number">{dashboardStats.totalYogis}</p>
                    </div>

                    {/* Most Attended Class */}
                    <div className="stat-card">
                      <h4>Aula mais frequentada: {dashboardStats.mostAttendedClass.name}</h4>
                      <p className="stat-count">{dashboardStats.mostAttendedClass.count} participações</p>
                    </div>

                    {/* Least Attended Class */}
                    <div className="stat-card">
                      <h4>Aula menos frequentada: {dashboardStats.leastAttendedClass.name}</h4>
                      <p className="stat-count">{dashboardStats.leastAttendedClass.count} participações</p>
                    </div>

                    {/* Top 3 Most Assiduous Yogis */}
                    <div className="stat-card stat-card-large">
                      <h4>Top 3 alunos mais assíduos</h4>
                      <div className="top-yogis-list">
                        {dashboardStats.top3Yogis.map((yogi, index) => (
                          <div key={index} className="top-yogi-item">
                            <span className="yogi-rank">{index + 1}º</span>
                            <span className="yogi-name">{yogi.name}</span>
                            <span className="yogi-count">{yogi.count} aulas</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Birthdays */}
                    <div className="stat-card stat-card-large">
                      <h4>Aniversários</h4>
                      <div className="birthdays-list">
                        {dashboardStats.birthdays.length > 0 ? (
                          dashboardStats.birthdays.map((birthday, index) => {
                            const dateStr = birthday.date.toLocaleDateString('pt-PT', { day: 'numeric', month: 'long' });
                            return (
                              <div 
                                key={index} 
                                className={`birthday-item ${birthday.isToday ? 'birthday-today' : 'birthday-upcoming'}`}
                              >
                                <span className="birthday-name">{birthday.name}</span>
                                <span className="birthday-date">{dateStr}</span>
                              </div>
                            );
                          })
                        ) : (
                          <p className="no-birthdays">Sem aniversários nos próximos dias</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentView === 'classes' && (
                <div className="admin-classes">
                  <h2>Gerir Aulas</h2>
                  <div className="class-management">
                    <button className="add-button" onClick={openAddClassModal}>Adicionar Nova Aula</button>
                    <div className="classes-list">
                      {classes.map((classItem) => {
                        // Get teachers assigned to this class type
                        const assignedTeachers = teacherClassAssignments.filter(
                          assignment => assignment.class_id === classItem.id
                        );

                        return (
                        <div key={classItem.id} className="class-management-item">
                          <h4>{classItem.name}</h4>
                          <p>{classItem.description}</p>
                          
                          {/* Show assigned teachers */}
                          {assignedTeachers.length > 0 && (
                            <div style={{ 
                              marginTop: '0.75rem', 
                              padding: '0.5rem', 
                              backgroundColor: '#f8f9fa', 
                              borderRadius: '4px' 
                            }}>
                              <strong style={{ fontSize: '0.9rem', color: '#495057' }}>
                                👨‍🏫 Professores Atribuídos:
                              </strong>
                              <ul style={{ 
                                margin: '0.5rem 0 0 0', 
                                paddingLeft: '1.25rem', 
                                fontSize: '0.85rem' 
                              }}>
                                {assignedTeachers.map(assignment => (
                                  <li key={assignment.id} style={{ marginBottom: '0.25rem' }}>
                                    {assignment.teacher?.user_profile?.full_name || 'N/A'}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <div className="class-actions" style={{
                            display: 'flex',
                            gap: '0.5rem',
                            flexWrap: 'wrap',
                            marginTop: '1rem'
                          }}>
                            <button 
                              onClick={() => openTeacherAssignmentModal(classItem)}
                              aria-label="Gerir Professores"
                              title="Gerir Professores"
                              style={{ 
                                backgroundColor: '#81524f', 
                                color: 'white',
                                padding: '0.75rem 1rem',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                minWidth: '44px',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.2s',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6a403d'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#81524f'}
                            >
                              <span style={{ fontSize: '1.2rem' }}>👥</span>
                              <span className="button-text-desktop">Professores</span>
                            </button>
                            <button 
                              onClick={() => openEditClassModal(classItem)}
                              aria-label="Editar Aula"
                              title="Editar Aula"
                              style={{
                                padding: '0.75rem 1rem',
                                border: '1px solid #81524f',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                backgroundColor: 'white',
                                color: '#81524f',
                                minWidth: '44px',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.2s',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#81524f';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.color = '#81524f';
                              }}
                            >
                              <span style={{ fontSize: '1.2rem' }}>✏️</span>
                              <span className="button-text-desktop">Editar</span>
                            </button>
                            <button 
                              onClick={() => handleDeleteClass(classItem)}
                              aria-label="Remover Aula"
                              title="Remover Aula"
                              style={{
                                padding: '0.75rem 1rem',
                                border: '1px solid #dc3545',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                backgroundColor: 'white',
                                color: '#dc3545',
                                minWidth: '44px',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.2s',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#dc3545';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.color = '#dc3545';
                              }}
                            >
                              <span style={{ fontSize: '1.2rem' }}>🗑️</span>
                              <span className="button-text-desktop">Remover</span>
                            </button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Add Class Modal */}
                  {showAddClassModal && (
                    <div className="modal-overlay" onClick={closeAddClassModal}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeAddClassModal}>✕</button>
                        
                        <div className="modal-header">
                          <h2>Adicionar Nova Aula</h2>
                        </div>

                        <div className="modal-body">
                          <div className="form-group">
                            <label>Nome da Aula *</label>
                            <input
                              type="text"
                              value={newClassForm.name}
                              onChange={(e) => setNewClassForm({...newClassForm, name: e.target.value})}
                              placeholder="Ex: Yôga, Pilates, etc."
                            />
                          </div>

                          <div className="form-group">
                            <label>Professor</label>
                            <input
                              type="text"
                              value={newClassForm.teacher}
                              onChange={(e) => setNewClassForm({...newClassForm, teacher: e.target.value})}
                              placeholder="Ex: Inês Carreira"
                            />
                          </div>

                          <div className="form-group">
                            <label>Descrição *</label>
                            <textarea
                              value={newClassForm.description}
                              onChange={(e) => setNewClassForm({...newClassForm, description: e.target.value})}
                              placeholder="Descreva a aula..."
                              rows="4"
                              style={{
                                padding: '0.75rem',
                                border: '2px solid #e9ecef',
                                borderRadius: '6px',
                                fontSize: '1rem',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                width: '100%'
                              }}
                            />
                          </div>
                        </div>

                        <div className="modal-footer">
                          <button className="cancel-btn" onClick={closeAddClassModal}>Cancelar</button>
                          <button className="save-profile-btn" onClick={handleAddClass}>Guardar Aula</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Edit Class Modal */}
                  {showEditClassModal && selectedClassToEdit && (
                    <div className="modal-overlay" onClick={closeEditClassModal}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeEditClassModal}>✕</button>
                        
                        <div className="modal-header">
                          <h2>Editar Aula</h2>
                        </div>

                        <div className="modal-body">
                          <div className="form-group">
                            <label>Nome da Aula *</label>
                            <input
                              type="text"
                              value={newClassForm.name}
                              onChange={(e) => setNewClassForm({...newClassForm, name: e.target.value})}
                              placeholder="Ex: Yôga, Pilates, etc."
                            />
                          </div>

                          <div className="form-group">
                            <label>Professor</label>
                            <input
                              type="text"
                              value={newClassForm.teacher}
                              onChange={(e) => setNewClassForm({...newClassForm, teacher: e.target.value})}
                              placeholder="Ex: Inês Carreira"
                            />
                          </div>

                          <div className="form-group">
                            <label>Descrição *</label>
                            <textarea
                              value={newClassForm.description}
                              onChange={(e) => setNewClassForm({...newClassForm, description: e.target.value})}
                              placeholder="Descreva a aula..."
                              rows="4"
                              style={{
                                padding: '0.75rem',
                                border: '2px solid #e9ecef',
                                borderRadius: '6px',
                                fontSize: '1rem',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                width: '100%'
                              }}
                            />
                          </div>
                        </div>

                        <div className="modal-footer">
                          <button className="cancel-btn" onClick={closeEditClassModal}>Cancelar</button>
                          <button className="save-profile-btn" onClick={handleEditClass}>Guardar Alterações</button>
                        </div>
                      </div>
                </div>
              )}

                  {/* Teacher Assignment Modal */}
                  {showTeacherAssignmentModal && selectedClassForTeachers && (() => {
                    // Get all teachers (users with user_type='teacher')
                    const allTeachers = users.filter(u => u.user_type === 'teacher');
                    
                    // Get teachers assigned to this class type
                    const assignedTeachers = teacherClassAssignments.filter(
                      assignment => assignment.class_id === selectedClassForTeachers.id
                    );

                    // Get teacher IDs that are already assigned
                    const assignedTeacherUserIds = assignedTeachers
                      .map(assignment => assignment.teacher?.user_id)
                      .filter(Boolean);

                    // Get unassigned teachers
                    const unassignedTeachers = allTeachers.filter(
                      teacher => !assignedTeacherUserIds.includes(teacher.id)
                    );

                    return (
                      <div className="modal-overlay" onClick={closeTeacherAssignmentModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                          <button className="modal-close" onClick={closeTeacherAssignmentModal}>✕</button>
                          
                          <div className="modal-header">
                            <h2>👥 Gerir Professores</h2>
                            <p style={{ fontSize: '0.95rem', color: '#666', marginTop: '0.5rem' }}>
                              Tipo de Aula: <strong>{selectedClassForTeachers.name}</strong>
                            </p>
                          </div>

                          <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {/* Currently Assigned Teachers */}
                            <div style={{ marginBottom: '2rem' }}>
                              <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#81524f' }}>
                                ✅ Professores Atribuídos ({assignedTeachers.length})
                              </h3>
                              {assignedTeachers.length === 0 ? (
                                <p style={{ color: '#666', fontStyle: 'italic', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                  Nenhum professor atribuído a este tipo de aula.
                                </p>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  {assignedTeachers.map(assignment => (
                                    <div 
                                      key={assignment.id} 
                                      style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        padding: '0.75rem',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '4px',
                                        border: '1px solid #dee2e6'
                                      }}
                                    >
                                      <div>
                                        <strong>{assignment.teacher?.user_profile?.full_name || 'N/A'}</strong>
                                        <br />
                                        <small style={{ color: '#666' }}>
                                          {assignment.teacher?.user_profile?.email || 'N/A'}
                                        </small>
                                      </div>
                                      <button
                                        onClick={() => handleRemoveTeacherFromClass(assignment.id)}
                                        style={{
                                          padding: '0.5rem 1rem',
                                          backgroundColor: '#dc3545',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '0.9rem'
                                        }}
                                      >
                                        Remover
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Available Teachers to Assign */}
                            <div>
                              <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#81524f' }}>
                                ➕ Adicionar Professor ({unassignedTeachers.length} disponível)
                              </h3>
                              {unassignedTeachers.length === 0 ? (
                                <p style={{ color: '#666', fontStyle: 'italic', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                  Todos os professores já estão atribuídos a este tipo de aula, ou não existem professores disponíveis.
                                </p>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  {unassignedTeachers.map(teacher => {
                                    // Find the teacher's ID in the teachers table
                                    const teacherRecord = teachers.find(t => t.user_id === teacher.id);
                                    if (!teacherRecord) return null;

                                    return (
                                      <div 
                                        key={teacher.id} 
                                        style={{ 
                                          display: 'flex', 
                                          justifyContent: 'space-between', 
                                          alignItems: 'center',
                                          padding: '0.75rem',
                                          backgroundColor: '#f8f9fa',
                                          borderRadius: '4px',
                                          border: '1px solid #dee2e6'
                                        }}
                                      >
                                        <div>
                                          <strong>{teacher.full_name}</strong>
                                          <br />
                                          <small style={{ color: '#666' }}>{teacher.email}</small>
                                        </div>
                                        <button
                                          onClick={() => handleAssignTeacherToClass(teacherRecord.id, selectedClassForTeachers.id)}
                                          style={{
                                            padding: '0.5rem 1rem',
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem'
                                          }}
                                        >
                                          Atribuir
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="modal-footer">
                            <button className="cancel-btn" onClick={closeTeacherAssignmentModal}>Fechar</button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                </div>
              )}

              {/* Add/Edit Session Modal - For Weekly Template & Calendar */}
              {(showAddSessionModal || showEditSessionModal) && (() => {
                const isEditMode = showEditSessionModal;
                const title = isEditMode ? 'Editar Sessão' : 'Adicionar Sessão';
                
                // Filter teachers based on selected class type
                const selectedClass = sessionForm.class_id;
                const qualifiedTeachers = teachers.filter(teacher => {
                  // Check if this teacher is assigned to the selected class type
                  const isAssigned = teacherClassAssignments.some(
                    assignment => assignment.teacher_id === teacher.id && 
                                  assignment.class_id === selectedClass &&
                                  assignment.is_active
                  );
                  return isAssigned;
                });

                return (
                  <div className="modal-overlay" onClick={isEditMode ? closeEditSessionModal : closeAddSessionModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
                      <button className="modal-close" onClick={isEditMode ? closeEditSessionModal : closeAddSessionModal}>✕</button>
                      
                      <div className="modal-header">
                        <h2>{title}</h2>
                        {sessionForm.is_template && (
                          <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                            📋 Adicionando ao plano semanal (template repetido)
                          </p>
                        )}
                      </div>

                      <div className="modal-body" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                        {/* Class Type Selection */}
                        <div className="form-group">
                          <label>Tipo de Aula *</label>
                          <select
                            value={sessionForm.class_id}
                            onChange={(e) => {
                              setSessionForm({
                                ...sessionForm, 
                                class_id: e.target.value,
                                teacher_id: '' // Reset teacher when class changes
                              });
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              fontSize: '1rem',
                              border: '1px solid #ddd',
                              borderRadius: '4px'
                            }}
                          >
                            <option value="">Selecione um tipo de aula</option>
                            {classes.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Teacher Selection - Filtered by Class Type */}
                        <div className="form-group">
                          <label>Professor *</label>
                          {!sessionForm.class_id ? (
                            <p style={{ 
                              padding: '0.75rem', 
                              backgroundColor: '#fff3cd', 
                              border: '1px solid #ffc107',
                              borderRadius: '4px',
                              color: '#856404',
                              fontSize: '0.9rem'
                            }}>
                              ⚠️ Primeiro selecione um tipo de aula
                            </p>
                          ) : qualifiedTeachers.length === 0 ? (
                            <p style={{ 
                              padding: '0.75rem', 
                              backgroundColor: '#f8d7da', 
                              border: '1px solid #f5c6cb',
                              borderRadius: '4px',
                              color: '#721c24',
                              fontSize: '0.9rem'
                            }}>
                              ❌ Nenhum professor atribuído a este tipo de aula.<br/>
                              <small>Atribua professores em "Gerir Aulas" primeiro.</small>
                            </p>
                          ) : (
                            <select
                              value={sessionForm.teacher_id}
                              onChange={(e) => setSessionForm({...sessionForm, teacher_id: e.target.value})}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            >
                              <option value="">Inês Carreira (padrão)</option>
                              {qualifiedTeachers.map(teacher => {
                                // Use user_profile from teacher object (fetched from DB)
                                // Ensure we NEVER show the teacher ID
                                const fullName = teacher.user_profile?.full_name;
                                const bio = teacher.bio;
                                const teacherName = fullName || bio || 'Professor Sem Nome';
                                
                                // Debug: Log if we're missing teacher name data
                                if (!fullName && !bio) {
                                  console.warn('Teacher missing name data:', teacher.id, teacher);
                                }
                                
                                return (
                                  <option key={teacher.id} value={teacher.id}>
                                    {teacherName}
                                  </option>
                                );
                              })}
                            </select>
                          )}
                          <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                            👨‍🏫 Apenas professores atribuídos a este tipo de aula aparecem aqui
                          </small>
                        </div>

                        {/* Day of Week (for templates) or Date (for regular sessions) */}
                        {sessionForm.is_template ? (
                          <div className="form-group">
                            <label>Dia da Semana *</label>
                            <select
                              value={sessionForm.day_of_week}
                              onChange={(e) => setSessionForm({...sessionForm, day_of_week: parseInt(e.target.value)})}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            >
                              <option value="1">Segunda-feira</option>
                              <option value="2">Terça-feira</option>
                              <option value="3">Quarta-feira</option>
                              <option value="4">Quinta-feira</option>
                              <option value="5">Sexta-feira</option>
                              <option value="6">Sábado</option>
                              <option value="7">Domingo</option>
                            </select>
                          </div>
                        ) : (
                          <div className="form-group">
                            <label>Data *</label>
                            <input
                              type="date"
                              value={sessionForm.session_date}
                              onChange={(e) => setSessionForm({...sessionForm, session_date: e.target.value})}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            />
                          </div>
                        )}

                        {/* Time Range */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div className="form-group">
                            <label>Hora de Início *</label>
                            <input
                              type="time"
                              value={sessionForm.start_time}
                              onChange={(e) => setSessionForm({...sessionForm, start_time: e.target.value})}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            />
                          </div>
                          <div className="form-group">
                            <label>Hora de Fim *</label>
                            <input
                              type="time"
                              value={sessionForm.end_time}
                              onChange={(e) => setSessionForm({...sessionForm, end_time: e.target.value})}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            />
                          </div>
                        </div>

                        {/* Max Participants */}
                        <div className="form-group">
                          <label>Máximo de Participantes</label>
                          <input
                            type="number"
                            value={sessionForm.max_participants}
                            onChange={(e) => setSessionForm({...sessionForm, max_participants: parseInt(e.target.value) || 10})}
                            min="1"
                            max="50"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              fontSize: '1rem',
                              border: '1px solid #ddd',
                              borderRadius: '4px'
                            }}
                          />
                        </div>

                        {/* Online/Location */}
                        <div className="form-group">
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={sessionForm.is_online}
                              onChange={(e) => setSessionForm({...sessionForm, is_online: e.target.checked})}
                            />
                            Aula Online
                          </label>
                        </div>

                        {sessionForm.is_online ? (
                          <div className="form-group">
                            <label>Link Zoom</label>
                            <input
                              type="url"
                              value={sessionForm.zoom_link}
                              onChange={(e) => setSessionForm({...sessionForm, zoom_link: e.target.value})}
                              placeholder="https://zoom.us/..."
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            />
                          </div>
                        ) : (
                          <div className="form-group">
                            <label>Local</label>
                            <input
                              type="text"
                              value={sessionForm.location}
                              onChange={(e) => setSessionForm({...sessionForm, location: e.target.value})}
                              placeholder="Ex: Estúdio Bhāva"
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            />
                          </div>
                        )}

                        {/* Notes */}
                        <div className="form-group">
                          <label>Notas</label>
                          <textarea
                            value={sessionForm.notes}
                            onChange={(e) => setSessionForm({...sessionForm, notes: e.target.value})}
                            placeholder="Notas adicionais..."
                            rows="3"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              fontSize: '1rem',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontFamily: 'inherit',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                      </div>

                      <div className="modal-footer">
                        <button 
                          className="cancel-btn" 
                          onClick={isEditMode ? closeEditSessionModal : closeAddSessionModal}
                        >
                          Cancelar
                        </button>
                        <button 
                          className="save-profile-btn" 
                          onClick={isEditMode ? handleUpdateSession : handleAddSession}
                        >
                          {isEditMode ? 'Guardar Alterações' : 'Adicionar Sessão'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {currentView === 'schedule' && (
                <div className="schedule-management">
                  <div className="schedule-header">
                    <h2>Horários e Calendário</h2>
                  </div>

                  {/* Tab Navigation */}
                  <div className="tabs-container">
                    <button
                      className={`tab-button ${activeScheduleTab === 'template' ? 'active' : ''}`}
                      onClick={() => setActiveScheduleTab('template')}
                    >
                      📋 Plano Semanal
                    </button>
                    <button
                      className={`tab-button ${activeScheduleTab === 'calendar' ? 'active' : ''}`}
                      onClick={() => setActiveScheduleTab('calendar')}
                    >
                      📅 Calendário
                    </button>
                  </div>

                  {/* TAB 1: WEEKLY TEMPLATE */}
                  {activeScheduleTab === 'template' && (
                    <div className="template-tab">
                      <div className="tab-actions">
                        <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                          Este é o plano semanal que se repete automaticamente. As alterações aqui afetam todas as semanas futuras.
                        </p>
                        <button className="add-button" onClick={() => openAddSessionModal(true)}>
                          + Adicionar ao Template
                        </button>
                      </div>

                      <div className="weekly-template-view">
                        {['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado', 'Domingo'].map((dayName, dayIndex) => {
                          const dayOfWeek = dayIndex + 1; // 1=Monday
                          const daySessions = weeklyTemplates
                            .filter(s => s.day_of_week === dayOfWeek)
                            .sort((a, b) => a.start_time.localeCompare(b.start_time));

                          return (
                            <div key={dayName} className="template-day">
                              <h3 className="day-name">{dayName}</h3>
                              {daySessions.length === 0 ? (
                                <p className="no-sessions">Sem aulas programadas</p>
                              ) : (
                                <div className="day-sessions-list">
                                  {daySessions.map((session) => {
                                    const classInfo = classes.find(c => c.id === session.class_id);
                                    // Use teacher data from the session join first, fallback to teachers array
                                    const teacherInfo = session.teachers || teachers.find(t => t.id === session.teacher_id);
                                    const teacherName = teacherInfo?.user_profile?.full_name || teacherInfo?.bio || 'Professor';
                                    
                                    return (
                                      <div key={session.id} className="template-session-card">
                                        <div className="session-time-info">
                                          <span className="session-time">{session.start_time.slice(0,5)} - {session.end_time.slice(0,5)}</span>
                                          <span className="session-class-name">{classInfo?.name || 'Aula'}</span>
                                          {session.is_online && <span className="badge-online">ONLINE</span>}
                                        </div>
                                        <div className="session-teacher-info">
                                          <span>{teacherName}</span>
                                        </div>
                                        {session.notes && <div className="session-notes">{session.notes}</div>}
                                        <div className="session-actions-inline">
                                          <button 
                                            className="btn-edit-small"
                                            onClick={() => openEditSessionModal(session)}
                                          >
                                            Editar
                                          </button>
                                          <button 
                                            className="btn-delete-small"
                                            onClick={() => handleDeleteSession(session)}
                                          >
                                            Remover
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* TAB 2: CALENDAR GRID VIEW */}
                  {activeScheduleTab === 'calendar' && (
                    <div className="calendar-tab">
                      {/* Calendar Controls */}
                      <div className="calendar-controls">
                        <button className="calendar-nav-btn" onClick={goToPreviousMonth}>
                          ◀ Anterior
                        </button>
                        <h3 className="calendar-month-title">
                          {calendarMonth.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}
                        </h3>
                        <button className="calendar-nav-btn" onClick={goToNextMonth}>
                          Próximo ▶
                        </button>
                        <button className="calendar-today-btn" onClick={goToToday}>
                          Hoje
                        </button>
                      </div>

                      {/* Legend */}
                      <div className="calendar-legend">
                        <span className="legend-item"><span className="legend-icon">✅</span> Auto-gerada</span>
                        <span className="legend-item"><span className="legend-icon">⭐</span> Manual</span>
                        <span className="legend-item"><span className="legend-icon">❌</span> Cancelada</span>
                        <span className="legend-item"><span className="legend-icon" style={{fontSize: '1.2rem'}}>📍</span> Clique num dia para ver/editar aulas</span>
                      </div>

                      {/* Calendar Grid */}
                      <div className="calendar-grid-container">
                        {/* Day headers */}
                        <div className="calendar-weekdays">
                          {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((day) => (
                            <div key={day} className="calendar-weekday">{day}</div>
                          ))}
                        </div>

                        {/* Calendar days */}
                        <div className="calendar-days-grid">
                          {getMonthCalendarDays(calendarMonth).days.map((day, index) => {
                            const isCurrentMonth = day && day.getMonth() === calendarMonth.getMonth();
                            const isToday = day && toIsoDate(day) === toIsoDate(new Date());
                            const dayIso = day ? toIsoDate(day) : '';
                            const daySessions = day ? getSessionsForDateIso(dayIso) : [];
                            const hasClasses = daySessions.length > 0;

                            return (
                              <div
                                key={index}
                                className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${hasClasses ? 'has-classes' : ''}`}
                                onClick={() => day && isCurrentMonth && handleDayClick(day)}
                              >
                                {day && (
                                  <>
                                    <div className="calendar-day-number">{day.getDate()}</div>
                                    {hasClasses && (
                                      <div className="calendar-day-sessions">
                                        {daySessions.slice(0, 3).map((session) => {
                                          const classInfo = classes.find(c => c.id === session.class_id);
                                          const isManual = !session.template_id;
                                          const isCancelled = session.is_cancelled || session.status === 'cancelled';
                                          
                                          return (
                                            <div
                                              key={session.id}
                                              className={`calendar-session-dot ${isCancelled ? 'cancelled' : ''}`}
                                              title={`${session.start_time.slice(0,5)} ${classInfo?.name || 'Aula'}`}
                                            >
                                              <span className="session-time">{session.start_time.slice(0,5)}</span>
                                              <span className="session-name">{classInfo?.name || 'Aula'}</span>
                                              {isManual && <span className="session-badge">⭐</span>}
                                              {isCancelled && <span className="session-badge">❌</span>}
                                            </div>
                                          );
                                        })}
                                        {daySessions.length > 3 && (
                                          <div className="calendar-more-sessions">
                                            +{daySessions.length - 3} mais
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day Details Modal */}
                  {showDayModal && calendarSelectedDate && (
                    <div className="modal-overlay" onClick={closeDayModal}>
                      <div className="modal-content day-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeDayModal}>✕</button>
                        
                        <div className="modal-header">
                          <h2>{calendarSelectedDate.toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h2>
                        </div>

                        <div className="modal-body">
                          <button 
                            className="add-button" 
                            onClick={() => addSessionForDate(calendarSelectedDate)}
                            style={{ marginBottom: '1.5rem' }}
                          >
                            + Adicionar Aula Neste Dia
                          </button>

                          {getSessionsForDateIso(toIsoDate(calendarSelectedDate)).length === 0 ? (
                            <p style={{ color: '#6c757d', fontStyle: 'italic' }}>Sem aulas neste dia.</p>
                          ) : (
                            <div className="day-sessions-list">
                              {getSessionsForDateIso(toIsoDate(calendarSelectedDate)).map((session) => {
                                const classInfo = classes.find(c => c.id === session.class_id);
                                const teacherInfo = teachers.find(t => t.id === session.teacher_id);
                                const isManual = !session.template_id;
                                const isCancelled = session.is_cancelled || session.status === 'cancelled';

                                return (
                                  <div 
                                    key={session.id} 
                                    className={`session-management-item ${isCancelled ? 'cancelled' : ''}`}
                                    style={{ marginBottom: '1rem' }}
                                  >
                                    <div className="session-info">
                                      <div className="session-header-row">
                                        <h4>{classInfo?.name || 'Aula'}</h4>
                                        <div className="session-badges">
                                          {isManual && <span className="badge manual">⭐ AVULSA</span>}
                                          {!isManual && !isCancelled && <span className="badge auto">✅ AUTO</span>}
                                          {isCancelled && <span className="badge cancelled-badge">❌ CANCELADA</span>}
                                          {session.is_online && <span className="badge online">ONLINE</span>}
                                        </div>
                                      </div>
                                      <p><strong>Horário:</strong> {session.start_time.slice(0,5)} - {session.end_time.slice(0,5)}</p>
                                      <p><strong>Professor:</strong> {teacherInfo?.bio || 'N/A'}</p>
                                      <p><strong>Local:</strong> {session.location || 'N/A'}</p>
                                      {session.notes && <p><strong>Notas:</strong> {session.notes}</p>}
                                      
                                      {/* Bookings Section */}
                                      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #dee2e6' }}>
                                        <p style={{ marginBottom: '0.5rem' }}>
                                          <strong>👥 Yogis Inscritos:</strong> {session.current_participants || 0} / {session.max_participants}
                                        </p>
                                        {sessionBookings[session.id] && sessionBookings[session.id].length > 0 && (
                                          <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', fontSize: '0.9rem', color: '#495057' }}>
                                            {sessionBookings[session.id].map((booking, idx) => (
                                              <li key={booking.id} style={{ marginBottom: '0.25rem' }}>
                                                {booking.user_profiles?.full_name || booking.user_profiles?.email || 'Yogi'}
                                                {booking.payment_status === 'paid' && <span style={{ marginLeft: '0.5rem', color: '#28a745' }}>✓</span>}
                                                {booking.payment_status === 'pending' && <span style={{ marginLeft: '0.5rem', color: '#ffc107' }}>⏳</span>}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                        {(!sessionBookings[session.id] || sessionBookings[session.id].length === 0) && (
                                          <p style={{ fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic', marginTop: '0.5rem' }}>
                                            Ainda sem inscrições
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="session-actions">
                                      {!isCancelled ? (
                                        <>
                                          <button 
                                            className="edit-button" 
                                            onClick={() => { closeDayModal(); openEditSessionModal(session); }}
                                          >
                                            Editar
                                          </button>
                                          <button 
                                            className="cancel-button" 
                                            onClick={() => { handleCancelSession(session); }}
                                          >
                                            Cancelar
                                          </button>
                                          {isManual && (
                                            <button 
                                              className="delete-button" 
                                              onClick={() => { handleDeleteSession(session); closeDayModal(); }}
                                            >
                                              Remover
                                            </button>
                                          )}
                                        </>
                                      ) : (
                                        <button 
                                          className="reactivate-button" 
                                          onClick={() => { handleReactivateSession(session); }}
                                        >
                                          Reativar
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Add Session Modal */}
                  {showAddSessionModal && (
                    <div className="modal-overlay" onClick={closeAddSessionModal}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeAddSessionModal}>✕</button>
                        
                        <div className="modal-header">
                          <h2>{sessionForm.is_template ? 'Adicionar ao Template Semanal' : 'Adicionar Aula Avulsa'}</h2>
                        </div>

                        <div className="modal-body">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Tipo de Aula *</label>
                              <select
                                value={sessionForm.class_id}
                                onChange={(e) => setSessionForm({...sessionForm, class_id: e.target.value})}
                              >
                                <option value="">Selecione...</option>
                                {classes.map((classItem) => (
                                  <option key={classItem.id} value={classItem.id}>
                                    {classItem.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="form-group">
                              <label>Professor *</label>
                              <select
                                value={sessionForm.teacher_id}
                                onChange={(e) => setSessionForm({...sessionForm, teacher_id: e.target.value})}
                              >
                                <option value="">Selecione...</option>
                                {teachers.map((teacher) => (
                                  <option key={teacher.id} value={teacher.id}>
                                    {teacher.bio || teacher.id}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="form-row">
                            {sessionForm.is_template ? (
                              <div className="form-group">
                                <label>Dia da Semana *</label>
                                <select
                                  value={sessionForm.day_of_week}
                                  onChange={(e) => setSessionForm({...sessionForm, day_of_week: parseInt(e.target.value)})}
                                >
                                  <option value={1}>Segunda-feira</option>
                                  <option value={2}>Terça-feira</option>
                                  <option value={3}>Quarta-feira</option>
                                  <option value={4}>Quinta-feira</option>
                                  <option value={5}>Sexta-feira</option>
                                  <option value={6}>Sábado</option>
                                  <option value={7}>Domingo</option>
                                </select>
                              </div>
                            ) : (
                              <div className="form-group">
                                <label>Data *</label>
                                <input
                                  type="date"
                                  value={sessionForm.session_date}
                                  onChange={(e) => setSessionForm({...sessionForm, session_date: e.target.value})}
                                />
                              </div>
                            )}

                            <div className="form-group">
                              <label>Hora Início *</label>
                              <input
                                type="time"
                                value={sessionForm.start_time}
                                onChange={(e) => setSessionForm({...sessionForm, start_time: e.target.value})}
                              />
                            </div>

                            <div className="form-group">
                              <label>Hora Fim *</label>
                              <input
                                type="time"
                                value={sessionForm.end_time}
                                onChange={(e) => setSessionForm({...sessionForm, end_time: e.target.value})}
                              />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Máx. Participantes</label>
                              <input
                                type="number"
                                value={sessionForm.max_participants}
                                onChange={(e) => setSessionForm({...sessionForm, max_participants: parseInt(e.target.value) || 15})}
                                min="1"
                                max="50"
                              />
                            </div>

                            <div className="form-group">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={sessionForm.is_online}
                                  onChange={(e) => setSessionForm({...sessionForm, is_online: e.target.checked})}
                                  style={{ marginRight: '0.5rem' }}
                                />
                                Aula Online
                              </label>
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Local</label>
                            <input
                              type="text"
                              value={sessionForm.location}
                              onChange={(e) => setSessionForm({...sessionForm, location: e.target.value})}
                              placeholder="Ex: Estúdio Bhāva"
                            />
                          </div>

                          <div className="form-group">
                            <label>Link Zoom (se online)</label>
                            <input
                              type="url"
                              value={sessionForm.zoom_link}
                              onChange={(e) => setSessionForm({...sessionForm, zoom_link: e.target.value})}
                              placeholder="https://zoom.us/j/..."
                            />
                          </div>

                          <div className="form-group">
                            <label>Notas</label>
                            <textarea
                              value={sessionForm.notes}
                              onChange={(e) => setSessionForm({...sessionForm, notes: e.target.value})}
                              placeholder="Informações adicionais..."
                              rows="3"
                              style={{
                                padding: '0.75rem',
                                border: '2px solid #e9ecef',
                                borderRadius: '6px',
                                fontSize: '1rem',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                width: '100%'
                              }}
                            />
                          </div>
                        </div>

                        <div className="modal-footer">
                          <button className="cancel-btn" onClick={closeAddSessionModal}>Cancelar</button>
                          <button className="save-profile-btn" onClick={handleAddSession}>
                            {sessionForm.is_template ? 'Guardar no Template' : 'Guardar Aula'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {currentView === 'content' && (
                <div className="content-management">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div>
                      <h2>Gerir Conteúdo Mensal</h2>
                      <p style={{ color: '#666', marginBottom: 0 }}>
                        Gerencie os mantras e asanas mensais que aparecem para todos os yogis.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setCalendarType('all');
                        setShowCalendarModal(true);
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#81524f',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      📅 Vista de Calendário
                    </button>
                  </div>
                  
                  <div className="content-sections" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '2rem',
                    marginTop: '2rem'
                  }}>
                    {/* MANTRA SECTION */}
                    <div className="mantra-card" style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <h3>🕉️ Mantra do Mês</h3>
                      <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                        {getMonthName(new Date().getMonth() + 1)} {new Date().getFullYear()}
                      </p>
                      
                      {currentMantra ? (
                        <div className="current-content">
                          <h4>{currentMantra.title}</h4>
                          {currentMantra.description && (
                            <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                              {currentMantra.description}
                            </p>
                          )}
                          {currentMantra.audio_url && (
                            <audio controls src={currentMantra.audio_url} style={{ width: '100%', marginTop: '1rem' }}>
                              Seu navegador não suporta áudio.
                            </audio>
                          )}
                        </div>
                      ) : (
                        <p style={{
                          padding: '2rem',
                          textAlign: 'center',
                          color: '#999',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          margin: '1rem 0'
                        }}>
                          Nenhum mantra configurado para este mês
                        </p>
                      )}
                      
                      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {!currentMantra ? (
                          <button 
                            className="add-button"
                            style={{ flex: 1, minWidth: '120px' }}
                            onClick={openAddMantraModal}
                          >
                            ➕ Adicionar
                          </button>
                        ) : (
                          <>
                            <button 
                              className="edit-button"
                              style={{ flex: 1, minWidth: '110px' }}
                              onClick={() => openEditMantraModal(currentMantra)}
                            >
                              ✏️ Editar
                            </button>
                            <button 
                              className="delete-button"
                              style={{ padding: '0.75rem 1rem', background: '#dc3545', color: 'white', border: 'none', borderRadius: '8px' }}
                              onClick={() => handleDeleteMantra(currentMantra)}
                            >
                              🗑️
                            </button>
                          </>
                        )}
                        <button 
                          className="history-button"
                          style={{ padding: '0.75rem 1rem' }}
                          onClick={() => setShowMantraHistoryModal(true)}
                          title={`${mantras.length} mantras no histórico`}
                        >
                          📜
                        </button>
                      </div>
                    </div>

                    {/* ASANA SECTION */}
                    <div className="asana-card" style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <h3>🧘 Asana do Mês</h3>
                      <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                        {getMonthName(new Date().getMonth() + 1)} {new Date().getFullYear()}
                      </p>
                      
                      {currentAsana ? (
                        <div className="current-content">
                          <h4>{currentAsana.title}</h4>
                          {currentAsana.sanskrit_name && (
                            <p style={{ fontStyle: 'italic', color: '#81524f', marginTop: '0.25rem' }}>
                              {currentAsana.sanskrit_name}
                            </p>
                          )}
                          {currentAsana.image_url && (
                            <img 
                              src={currentAsana.image_url} 
                              alt={currentAsana.title}
                              style={{
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                marginTop: '1rem'
                              }}
                            />
                          )}
                          {currentAsana.description && (
                            <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '1rem' }}>
                              {currentAsana.description}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p style={{
                          padding: '2rem',
                          textAlign: 'center',
                          color: '#999',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          margin: '1rem 0'
                        }}>
                          Nenhum asana configurado para este mês
                        </p>
                      )}
                      
                      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {!currentAsana ? (
                          <button 
                            className="add-button"
                            style={{ flex: 1, minWidth: '120px' }}
                            onClick={openAddAsanaModal}
                          >
                            ➕ Adicionar
                          </button>
                        ) : (
                          <>
                            <button 
                              className="edit-button"
                              style={{ flex: 1, minWidth: '110px' }}
                              onClick={() => openEditAsanaModal(currentAsana)}
                            >
                              ✏️ Editar
                            </button>
                            <button 
                              className="delete-button"
                              style={{ padding: '0.75rem 1rem', background: '#dc3545', color: 'white', border: 'none', borderRadius: '8px' }}
                              onClick={() => handleDeleteAsana(currentAsana)}
                            >
                              🗑️
                            </button>
                          </>
                        )}
                        <button 
                          className="history-button"
                          style={{ padding: '0.75rem 1rem' }}
                          onClick={() => setShowAsanaHistoryModal(true)}
                          title={`${asanas.length} asanas no histórico`}
                        >
                          📜
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ADD/EDIT MANTRA MODAL */}
                  {showMantraModal && (
                    <div className="modal-overlay" onClick={closeMantraModal}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <button className="modal-close" onClick={closeMantraModal}>✕</button>
                        
                        <div className="modal-header">
                          <h2>{isEditingMantra ? 'Editar Mantra' : 'Adicionar Mantra'}</h2>
                        </div>

                        <div className="modal-body">
                          <div className="form-group">
                            <label>Título *</label>
                            <input
                              type="text"
                              value={mantraForm.title}
                              onChange={(e) => setMantraForm({...mantraForm, title: e.target.value})}
                              placeholder="Ex: Om Namah Shivaya"
                            />
                          </div>

                          <div className="form-group">
                            <label>Descrição</label>
                            <textarea
                              value={mantraForm.description}
                              onChange={(e) => setMantraForm({...mantraForm, description: e.target.value})}
                              placeholder="Significado e contexto do mantra..."
                              rows="4"
                            />
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Mês *</label>
                              <select
                                value={mantraForm.month}
                                onChange={(e) => setMantraForm({...mantraForm, month: parseInt(e.target.value)})}
                              >
                                {[...Array(12)].map((_, i) => (
                                  <option key={i} value={i + 1}>{getMonthName(i + 1)}</option>
                                ))}
                              </select>
                            </div>

                            <div className="form-group">
                              <label>Ano *</label>
                              <select
                                value={mantraForm.year}
                                onChange={(e) => setMantraForm({...mantraForm, year: parseInt(e.target.value)})}
                              >
                                {[2024, 2025, 2026, 2027].map(year => (
                                  <option key={year} value={year}>{year}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                              <div className="form-group">
                            <label>Áudio</label>
                            
                            {/* File Upload Option */}
                            <div style={{ marginBottom: '1rem' }}>
                              <label style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#666' }}>
                                📁 Upload de Arquivo (MP3, WAV, M4A - máx 10MB)
                              </label>
                              <input
                                type="file"
                                accept="audio/*"
                                onChange={handleMantraFileChange}
                                style={{ marginTop: '0.5rem' }}
                              />
                            </div>

                            {/* Recording Option */}
                            <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                              <label style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#666', marginBottom: '0.5rem', display: 'block' }}>
                                🎙️ Gravar Áudio
                              </label>
                              
                              {!isRecording && !recordedAudio && (
                                <button
                                  type="button"
                                  onClick={startRecording}
                                  style={{
                                    padding: '0.75rem 1.5rem',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                  }}
                                >
                                  <span style={{ fontSize: '1.5rem' }}>⏺</span>
                                  Iniciar Gravação
                                </button>
                              )}

                              {isRecording && (
                                <div>
                                  <p style={{ color: '#dc3545', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                    🔴 Gravando...
                                  </p>
                                  <button
                                    type="button"
                                    onClick={stopRecording}
                                    style={{
                                      padding: '0.75rem 1.5rem',
                                      background: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      fontSize: '1rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem'
                                    }}
                                  >
                                    <span style={{ fontSize: '1.5rem' }}>⏹</span>
                                    Parar Gravação
                                  </button>
                              </div>
                              )}

                              {recordedAudio && (
                                <div>
                                  <p style={{ color: '#28a745', marginBottom: '0.5rem' }}>
                                    ✓ Gravação concluída
                                  </p>
                                  <audio controls src={recordedAudio} style={{ width: '100%', marginBottom: '0.5rem' }} />
                                  <button
                                    type="button"
                                    onClick={clearRecording}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#6c757d',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    🗑️ Limpar Gravação
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Current Audio Display */}
                            {mantraForm.audioUrl && !mantraForm.audioFile && !recordedAudio && (
                              <div style={{ marginTop: '0.5rem' }}>
                                <label style={{ fontSize: '0.9rem', color: '#666' }}>Áudio Atual:</label>
                                <audio controls src={mantraForm.audioUrl} style={{ width: '100%', marginTop: '0.5rem' }}>
                                  Áudio atual
                                </audio>
                              </div>
                            )}
                            
                            {mantraForm.audioFile && !recordedAudio && (
                              <p style={{ marginTop: '0.5rem', color: '#28a745', fontSize: '0.9rem' }}>
                                ✓ Novo áudio selecionado: {mantraForm.audioFile.name}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="modal-footer">
                          <button className="cancel-btn" onClick={closeMantraModal}>Cancelar</button>
                          <button className="save-profile-btn" onClick={handleSaveMantra}>
                            {isEditingMantra ? 'Guardar Alterações' : 'Adicionar Mantra'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ADD/EDIT ASANA MODAL */}
                  {showAsanaModal && (
                    <div className="modal-overlay" onClick={closeAsanaModal}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <button className="modal-close" onClick={closeAsanaModal}>✕</button>
                        
                        <div className="modal-header">
                          <h2>{isEditingAsana ? 'Editar Asana' : 'Adicionar Asana'}</h2>
                        </div>

                        <div className="modal-body">
                              <div className="form-group">
                            <label>Título *</label>
                                <input
                              type="text"
                              value={asanaForm.title}
                              onChange={(e) => setAsanaForm({...asanaForm, title: e.target.value})}
                              placeholder="Ex: Postura do Guerreiro"
                                />
                              </div>

                            <div className="form-group">
                            <label>Nome em Sânscrito</label>
                              <input
                              type="text"
                              value={asanaForm.sanskritName}
                              onChange={(e) => setAsanaForm({...asanaForm, sanskritName: e.target.value})}
                              placeholder="Ex: Virabhadrasana"
                              />
                            </div>

                            <div className="form-group">
                            <label>Descrição</label>
                            <textarea
                              value={asanaForm.description}
                              onChange={(e) => setAsanaForm({...asanaForm, description: e.target.value})}
                              placeholder="Descrição geral do asana..."
                              rows="3"
                            />
                          </div>

                            <div className="form-group">
                            <label>Benefícios</label>
                            <textarea
                              value={asanaForm.benefits}
                              onChange={(e) => setAsanaForm({...asanaForm, benefits: e.target.value})}
                              placeholder="Benefícios físicos e mentais..."
                              rows="3"
                              />
                            </div>

                            <div className="form-group">
                            <label>Instruções</label>
                            <textarea
                              value={asanaForm.instructions}
                              onChange={(e) => setAsanaForm({...asanaForm, instructions: e.target.value})}
                              placeholder="Como realizar o asana passo a passo..."
                              rows="4"
                            />
                            </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Mês *</label>
                              <select
                                value={asanaForm.month}
                                onChange={(e) => setAsanaForm({...asanaForm, month: parseInt(e.target.value)})}
                              >
                                {[...Array(12)].map((_, i) => (
                                  <option key={i} value={i + 1}>{getMonthName(i + 1)}</option>
                                ))}
                              </select>
                          </div>

                          <div className="form-group">
                              <label>Ano *</label>
                              <select
                                value={asanaForm.year}
                                onChange={(e) => setAsanaForm({...asanaForm, year: parseInt(e.target.value)})}
                              >
                                {[2024, 2025, 2026, 2027].map(year => (
                                  <option key={year} value={year}>{year}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Foto (JPG, PNG - máx 5MB)</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleAsanaFileChange}
                            />
                            {(asanaForm.imageUrl || asanaForm.imageFile) && (
                              <div style={{ marginTop: '0.5rem' }}>
                                <img 
                                  src={asanaForm.imageFile ? URL.createObjectURL(asanaForm.imageFile) : asanaForm.imageUrl}
                                  alt="Preview"
                                  style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                                />
                              </div>
                            )}
                            {asanaForm.imageFile && (
                              <p style={{ marginTop: '0.5rem', color: '#28a745', fontSize: '0.9rem' }}>
                                ✓ Nova foto selecionada: {asanaForm.imageFile.name}
                              </p>
                            )}
                          </div>
                          </div>

                        <div className="modal-footer">
                          <button className="cancel-btn" onClick={closeAsanaModal}>Cancelar</button>
                          <button className="save-profile-btn" onClick={handleSaveAsana}>
                            {isEditingAsana ? 'Guardar Alterações' : 'Adicionar Asana'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* MANTRA HISTORY MODAL */}
                  {showMantraHistoryModal && (
                    <div className="modal-overlay" onClick={() => setShowMantraHistoryModal(false)}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <button className="modal-close" onClick={() => setShowMantraHistoryModal(false)}>✕</button>
                        
                        <div className="modal-header">
                          <h2>📜 Histórico de Mantras</h2>
                        </div>

                        <div className="modal-body">
                          {mantras.length === 0 ? (
                            <p>Nenhum mantra cadastrado</p>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                              {mantras.map(mantra => (
                                <div key={mantra.id} style={{ 
                                  padding: '1rem', 
                                  border: '1px solid #ddd', 
                                  borderRadius: '8px',
                                  backgroundColor: mantra.month === new Date().getMonth() + 1 && mantra.year === new Date().getFullYear() ? '#f0f8ff' : 'white'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                    <div style={{ flex: 1 }}>
                                      <h4>{mantra.title}</h4>
                                      <p style={{ color: '#666', fontSize: '0.9rem' }}>
                                        {getMonthName(mantra.month)} {mantra.year}
                                      </p>
                                      {mantra.description && <p>{mantra.description}</p>}
                                      {mantra.audio_url && (
                                        <audio controls src={mantra.audio_url} style={{ width: '100%', marginTop: '0.5rem' }} />
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                                      <button 
                                        onClick={() => {
                                          setShowMantraHistoryModal(false);
                                          openEditMantraModal(mantra);
                                        }}
                                        style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer' }}
                                        title="Editar"
                                      >
                                        ✏️
                                      </button>
                                      <button 
                                        onClick={() => handleDeleteMantra(mantra)}
                                        style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer' }}
                                        title="Remover"
                                      >
                                        🗑️
                                      </button>
                          </div>
                        </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ASANA HISTORY MODAL */}
                  {showAsanaHistoryModal && (
                    <div className="modal-overlay" onClick={() => setShowAsanaHistoryModal(false)}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <button className="modal-close" onClick={() => setShowAsanaHistoryModal(false)}>✕</button>
                        
                        <div className="modal-header">
                          <h2>📜 Histórico de Asanas</h2>
                        </div>

                        <div className="modal-body">
                          {asanas.length === 0 ? (
                            <p>Nenhum asana cadastrado</p>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                              {asanas.map(asana => (
                                <div key={asana.id} style={{ 
                                  padding: '1rem', 
                                  border: '1px solid #ddd', 
                                  borderRadius: '8px',
                                  backgroundColor: asana.month === new Date().getMonth() + 1 && asana.year === new Date().getFullYear() ? '#f0f8ff' : 'white'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                    <div style={{ flex: 1 }}>
                                      <h4>{asana.title}</h4>
                                      {asana.sanskrit_name && (
                                        <p style={{ fontStyle: 'italic', color: '#666' }}>{asana.sanskrit_name}</p>
                                      )}
                                      <p style={{ color: '#666', fontSize: '0.9rem' }}>
                                        {getMonthName(asana.month)} {asana.year}
                                      </p>
                                      {asana.image_url && (
                                        <img 
                                          src={asana.image_url} 
                                          alt={asana.title}
                                          style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px', marginTop: '0.5rem' }}
                                        />
                                      )}
                                      {asana.description && <p style={{ marginTop: '0.5rem' }}>{asana.description}</p>}
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                                      <button 
                                        onClick={() => {
                                          setShowAsanaHistoryModal(false);
                                          openEditAsanaModal(asana);
                                        }}
                                        style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer' }}
                                        title="Editar"
                                      >
                                        ✏️
                                      </button>
                                      <button 
                                        onClick={() => handleDeleteAsana(asana)}
                                        style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer' }}
                                        title="Remover"
                                      >
                                        🗑️
                          </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CALENDAR VIEW MODAL - Multi-month view and scheduling */}
                  {showCalendarModal && (
                    <div className="modal-overlay" onClick={() => setShowCalendarModal(false)}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <button className="modal-close" onClick={() => setShowCalendarModal(false)}>✕</button>
                        
                        <div className="modal-header">
                          <h2>📅 Vista de Calendário - {new Date().getFullYear()}</h2>
                          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                            Planeie o conteúdo para todo o ano. Clique em qualquer mês para adicionar ou editar.
                          </p>
                        </div>

                        <div className="modal-body">
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                            gap: '1rem' 
                          }}>
                            {[...Array(12)].map((_, index) => {
                              const month = index + 1;
                              const currentYear = new Date().getFullYear();
                              const mantra = mantras.find(m => m.month === month && m.year === currentYear);
                              const asana = asanas.find(a => a.month === month && a.year === currentYear);
                              const isCurrentMonth = month === new Date().getMonth() + 1;

                              return (
                                <div
                                  key={month}
                                  style={{
                                    padding: '1rem',
                                    border: isCurrentMonth ? '2px solid #81524f' : '1px solid #ddd',
                                    borderRadius: '8px',
                                    backgroundColor: isCurrentMonth ? '#fff8f0' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#81524f' }}>
                                    {getMonthName(month)}
                                    {isCurrentMonth && <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}>🔵</span>}
                                  </h4>
                                  
                                  <div style={{ fontSize: '0.9rem' }}>
                                    <div style={{ marginBottom: '0.5rem' }}>
                                      <strong>🕉️ Mantra:</strong>
                                      {mantra ? (
                                        <div>
                                          <p style={{ margin: '0.25rem 0', color: '#28a745' }}>✓ {mantra.title}</p>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowCalendarModal(false);
                                              openEditMantraModal(mantra);
                                            }}
                                            style={{
                                              padding: '0.25rem 0.5rem',
                                              fontSize: '0.8rem',
                                              background: '#81524f',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              cursor: 'pointer',
                                              marginTop: '0.25rem'
                                            }}
                                          >
                                            ✏️ Editar
                                          </button>
                                        </div>
                                      ) : (
                                        <div>
                                          <p style={{ margin: '0.25rem 0', color: '#999' }}>Não configurado</p>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowCalendarModal(false);
                                              setMantraForm({
                                                ...mantraForm,
                                                month: month,
                                                year: currentYear
                                              });
                                              openAddMantraModal();
                                            }}
                                            style={{
                                              padding: '0.25rem 0.5rem',
                                              fontSize: '0.8rem',
                                              background: '#28a745',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              cursor: 'pointer',
                                              marginTop: '0.25rem'
                                            }}
                                          >
                                            ➕ Adicionar
                                          </button>
                </div>
              )}
                                    </div>

                                    <div>
                                      <strong>🧘 Asana:</strong>
                                      {asana ? (
                                        <div>
                                          <p style={{ margin: '0.25rem 0', color: '#28a745' }}>✓ {asana.title}</p>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowCalendarModal(false);
                                              openEditAsanaModal(asana);
                                            }}
                                            style={{
                                              padding: '0.25rem 0.5rem',
                                              fontSize: '0.8rem',
                                              background: '#81524f',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              cursor: 'pointer',
                                              marginTop: '0.25rem'
                                            }}
                                          >
                                            ✏️ Editar
                                          </button>
                    </div>
                                      ) : (
                                        <div>
                                          <p style={{ margin: '0.25rem 0', color: '#999' }}>Não configurado</p>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowCalendarModal(false);
                                              setAsanaForm({
                                                ...asanaForm,
                                                month: month,
                                                year: currentYear
                                              });
                                              openAddAsanaModal();
                                            }}
                                            style={{
                                              padding: '0.25rem 0.5rem',
                                              fontSize: '0.8rem',
                                              background: '#28a745',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              cursor: 'pointer',
                                              marginTop: '0.25rem'
                                            }}
                                          >
                                            ➕ Adicionar
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <div style={{
                            marginTop: '2rem',
                            padding: '1rem',
                            background: '#e3f2fd',
                            borderRadius: '8px',
                            fontSize: '0.9rem'
                          }}>
                            <p style={{ margin: 0 }}>
                              <strong>💡 Dica:</strong> Use esta vista para planear o conteúdo de todo o ano. 
                              O mês atual está destacado com 🔵. Clique em "Adicionar" para agendar conteúdo para meses futuros.
                            </p>
                          </div>
                    </div>
                  </div>
                    </div>
                  )}
                </div>
              )}

              {currentView === 'news' && (
                <div className="admin-news">
                  <h2>Gerir Notícias</h2>
                  <div className="news-management">
                    <button className="add-button">Adicionar Notícia</button>
                    <div className="news-list">
                      {news.map((article) => (
                        <div key={article.id} className="news-management-item">
                          <h4>{article.title}</h4>
                          <p>{article.content}</p>
                          <div className="news-actions">
                            <button>Editar</button>
                            <button>Remover</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {currentView === 'users' && (() => {
                // Capture current logged-in user's email to prevent self-deletion
                const currentUserEmail = user?.email;
                
                return (
                <div className="users-management">
                  <div className="users-header">
                    <h2>Gerir Utilizadores</h2>
                    <button className="add-button" onClick={openAddUserModal}>
                      + Adicionar Utilizador
                    </button>
                  </div>
                  <div className="users-list">
                    {users.length === 0 ? (
                      <p>Nenhum utilizador encontrado.</p>
                    ) : (
                        users.map((listUser) => (
                          <div key={listUser.id} className="user-card">
                          <div className="user-info">
                              <h4>{listUser.full_name || 'Sem nome'}</h4>
                              <p className="user-email">{listUser.email}</p>
                              {listUser.birth_date && (
                              <p className="user-birth-date">
                                  Data de nascimento: {new Date(listUser.birth_date).toLocaleDateString('pt-PT')}
                              </p>
                            )}
                            <p className="user-type">
                              Tipo: {
                                  listUser.user_type === 'student' ? 'Aluno' :
                                  listUser.user_type === 'teacher' ? 'Professor' :
                                  listUser.user_type === 'admin' ? 'Admin' :
                                  listUser.user_type
                              }
                            </p>
                              <p style={{ fontSize: '0.85rem', color: '#81524f', marginTop: '0.5rem' }}>
                                💡 Ver subscrições no menu "Subscrições"
                              </p>
                          </div>
                          <div className="user-actions">
                            <button 
                              className="edit-button" 
                                onClick={() => openEditUserModal(listUser)}
                            >
                              Editar
                            </button>
                              {/* Don't allow users to delete themselves */}
                              {listUser.email !== currentUserEmail && (
                            <button 
                              className="delete-button" 
                                  onClick={() => handleDeleteUser(listUser)}
                            >
                              Remover
                            </button>
                              )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add User Modal */}
                  {showAddUserModal && (
                    <div className="modal-overlay" onClick={closeAddUserModal}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeAddUserModal}>✕</button>
                        
                        <div className="modal-header">
                          <h2>Adicionar Novo Utilizador</h2>
                        </div>

                        <div className="modal-body">
                          <div className="form-group">
                            <label>Email *</label>
                            <input
                              type="email"
                              value={newUserForm.email}
                              onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                              placeholder="exemplo@email.com"
                            />
                          </div>

                          <div className="form-group">
                            <label>Nome Próprio *</label>
                            <input
                              type="text"
                              value={newUserForm.full_name}
                              onChange={(e) => setNewUserForm({...newUserForm, full_name: e.target.value})}
                              placeholder="Ex: Maria Silva"
                            />
                          </div>

                          <div className="form-group">
                            <label>Data de Nascimento</label>
                            <input
                              type="date"
                              value={newUserForm.birth_date}
                              onChange={(e) => setNewUserForm({...newUserForm, birth_date: e.target.value})}
                            />
                          </div>

                          <div className="form-group">
                            <label>Tipo de Perfil *</label>
                            <select
                              value={newUserForm.user_type}
                              onChange={(e) => setNewUserForm({...newUserForm, user_type: e.target.value})}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: 'white'
                              }}
                            >
                              <option value="student">Aluno</option>
                              <option value="teacher">Professor</option>
                            </select>
                            <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                              {newUserForm.user_type === 'teacher' 
                                ? '👨‍🏫 Professores podem gerir as suas aulas e marcar aulas como alunos' 
                                : '🧘 Alunos podem marcar e gerir as suas reservas'}
                            </small>
                          </div>

                          <div className="form-help-text" style={{ 
                            padding: '1rem', 
                            backgroundColor: '#f0f8ff', 
                            borderLeft: '4px solid #81524f', 
                            borderRadius: '4px',
                            marginTop: '1rem'
                          }}>
                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                              💡 <strong>Nota:</strong> Para atribuir subscrições a este utilizador, 
                              utilize o menu <strong>"Subscrições"</strong> após criar o utilizador.
                            </p>
                          </div>
                        </div>

                        <div className="modal-footer">
                          <button 
                            className="cancel-btn" 
                            onClick={closeAddUserModal}
                            disabled={isUserOperationLoading}
                          >
                            Cancelar
                          </button>
                          <button 
                            className="save-profile-btn" 
                            onClick={handleAddUser}
                            disabled={isUserOperationLoading}
                            style={{
                              opacity: isUserOperationLoading ? 0.6 : 1,
                              cursor: isUserOperationLoading ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {isUserOperationLoading ? '⏳ A adicionar...' : 'Adicionar Utilizador'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Edit User Modal */}
                  {showEditUserModal && selectedUser && (
                    <div className="modal-overlay" onClick={closeEditUserModal}>
                      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeEditUserModal}>✕</button>
                        
                        <div className="modal-header">
                          <h2>Editar Utilizador</h2>
                        </div>

                        <div className="modal-body">
                          <div className="form-group">
                            <label>Email *</label>
                            <input
                              type="email"
                              value={newUserForm.email}
                              onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                              placeholder="exemplo@email.com"
                            />
                          </div>

                          <div className="form-group">
                            <label>Nome Próprio *</label>
                            <input
                              type="text"
                              value={newUserForm.full_name}
                              onChange={(e) => setNewUserForm({...newUserForm, full_name: e.target.value})}
                              placeholder="Ex: Maria Silva"
                            />
                          </div>

                          <div className="form-group">
                            <label>Data de Nascimento</label>
                            <input
                              type="date"
                              value={newUserForm.birth_date || ''}
                              onChange={(e) => setNewUserForm({...newUserForm, birth_date: e.target.value})}
                            />
                          </div>

                          <div className="form-group">
                            <label>Tipo de Perfil *</label>
                            <select
                              value={newUserForm.user_type || 'student'}
                              onChange={(e) => setNewUserForm({...newUserForm, user_type: e.target.value})}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: 'white'
                              }}
                            >
                              <option value="student">Aluno</option>
                              <option value="teacher">Professor</option>
                            </select>
                            <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                              {newUserForm.user_type === 'teacher' 
                                ? '👨‍🏫 Professores podem gerir as suas aulas e marcar aulas como alunos' 
                                : '🧘 Alunos podem marcar e gerir as suas reservas'}
                            </small>
                          </div>

                          <div className="form-help-text" style={{ 
                            padding: '1rem', 
                            backgroundColor: '#f0f8ff', 
                            borderLeft: '4px solid #81524f', 
                            borderRadius: '4px',
                            marginTop: '1rem'
                          }}>
                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                              💡 <strong>Nota:</strong> Para gerir subscrições deste utilizador, 
                              utilize o menu <strong>"Subscrições"</strong>.
                            </p>
                          </div>
                        </div>

                        <div className="modal-footer">
                          <button 
                            className="cancel-btn" 
                            onClick={closeEditUserModal}
                            disabled={isUserOperationLoading}
                          >
                            Cancelar
                          </button>
                          <button 
                            className="save-profile-btn" 
                            onClick={handleEditUser}
                            disabled={isUserOperationLoading}
                            style={{
                              opacity: isUserOperationLoading ? 0.6 : 1,
                              cursor: isUserOperationLoading ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {isUserOperationLoading ? '⏳ A guardar...' : 'Guardar Alterações'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
                );
              })()}

              {currentView === 'subscriptions' && (
                <div className="subscriptions-management">
                  <div className="subscriptions-header">
                    <h2>Gerir Subscrições</h2>
                    <button className="add-button" onClick={() => setShowAssignSubscriptionModal(true)}>
                      + Atribuir Subscrição
                    </button>
                  </div>

                  {/* Summary Stats */}
                  <div className="subscription-stats">
                    <div className="stat-card">
                      <h4>Total Subscrições Ativas</h4>
                      <p className="stat-number">{allSubscriptions.filter(s => s.is_active).length}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Subscrições Inativas</h4>
                      <p className="stat-number">{allSubscriptions.filter(s => !s.is_active).length}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Total de Utilizadores</h4>
                      <p className="stat-number">{[...new Set(allSubscriptions.map(s => s.user_id))].length}</p>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="subscription-filters">
                    <div className="filter-group">
                      <label>Filtrar por Plano:</label>
                            <select
                        value={subscriptionFilterPlan} 
                        onChange={(e) => setSubscriptionFilterPlan(e.target.value)}
                        className="filter-select"
                      >
                        <option value="">Todos os planos</option>
                        {subscriptionPlans.map(plan => (
                          <option key={plan.id} value={plan.id}>
                            {plan.display_name}
                                </option>
                              ))}
                            </select>
                          </div>

                    <div className="filter-group">
                      <label>Filtrar por Utilizador:</label>
                      <input
                        type="text"
                        placeholder="Pesquisar por nome ou email..."
                        value={subscriptionFilterUser}
                        onChange={(e) => setSubscriptionFilterUser(e.target.value)}
                        className="filter-input"
                      />
                        </div>

                    {(subscriptionFilterPlan || subscriptionFilterUser) && (
                      <button 
                        className="clear-filters-btn"
                        onClick={() => {
                          setSubscriptionFilterPlan('');
                          setSubscriptionFilterUser('');
                        }}
                      >
                        ✕ Limpar Filtros
                      </button>
                    )}
                  </div>

                  {/* Subscriptions Table */}
                  <div className="subscriptions-table-container">
                    {(() => {
                      // Apply filters
                      let filteredSubscriptions = allSubscriptions;

                      // Filter by plan
                      if (subscriptionFilterPlan) {
                        filteredSubscriptions = filteredSubscriptions.filter(
                          sub => sub.subscription_plan_id === subscriptionFilterPlan
                        );
                      }

                      // Filter by user (name or email)
                      if (subscriptionFilterUser) {
                        const searchTerm = subscriptionFilterUser.toLowerCase();
                        filteredSubscriptions = filteredSubscriptions.filter(sub => 
                          sub.user_profiles?.full_name?.toLowerCase().includes(searchTerm) ||
                          sub.user_profiles?.email?.toLowerCase().includes(searchTerm)
                        );
                      }

                      return filteredSubscriptions.length === 0 ? (
                        <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                          {subscriptionFilterPlan || subscriptionFilterUser ? 
                            'Nenhuma subscrição encontrada com os filtros selecionados.' : 
                            'Nenhuma subscrição encontrada.'}
                        </p>
                      ) : (
                        <table className="subscriptions-table">
                        <thead>
                          <tr>
                            <th>Utilizador</th>
                            <th>Plano</th>
                            <th>Tipo</th>
                            <th>Limite/Créditos</th>
                            <th>Data Início</th>
                            <th>Data Fim</th>
                            <th>Status</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSubscriptions.map((sub) => (
                            <tr key={sub.id} className={!sub.is_active ? 'inactive-row' : ''}>
                              <td>
                                <div className="user-cell">
                                  <strong>{sub.user_profiles?.full_name || 'N/A'}</strong>
                                  <small>{sub.user_profiles?.email || 'N/A'}</small>
                                </div>
                              </td>
                              <td>
                                <span className="plan-name">{sub.subscription_plans?.display_name || 'N/A'}</span>
                              </td>
                              <td>
                                <span className="plan-type">
                                  {sub.subscription_plans?.class_type === 'online' ? '🌐 Online' :
                                   sub.subscription_plans?.class_type === 'presencial' ? '📍 Presencial' :
                                   '🌐📍 Ambos'}
                                </span>
                              </td>
                              <td>
                                {sub.subscription_plans?.weekly_limit !== null ? (
                                  <span>{sub.subscription_plans.weekly_limit}x/semana</span>
                                ) : sub.total_credits !== null ? (
                                  <span className="credits-badge">
                                    {sub.remaining_credits}/{sub.total_credits} créditos
                                  </span>
                                ) : (
                                  <span>♾️ Ilimitado</span>
                                )}
                              </td>
                              <td>
                                {sub.start_date ? new Date(sub.start_date).toLocaleDateString('pt-PT') : 'N/A'}
                              </td>
                              <td>
                                {sub.end_date ? (
                                  <span className={new Date(sub.end_date) < new Date() ? 'expired-date' : ''}>
                                    {new Date(sub.end_date).toLocaleDateString('pt-PT')}
                                  </span>
                                ) : (
                                  <span>Sem limite</span>
                                )}
                              </td>
                              <td>
                                <span className={`status-badge ${sub.is_active ? 'status-active' : 'status-inactive'}`}>
                                  {sub.is_active ? '✓ Ativo' : '✕ Inativo'}
                                </span>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="action-btn toggle-btn"
                                    onClick={() => handleToggleSubscriptionStatus(sub.id, sub.is_active)}
                                    title={sub.is_active ? 'Desativar' : 'Ativar'}
                                  >
                                    {sub.is_active ? '⏸️' : '▶️'}
                                  </button>
                                  {sub.is_active && (
                                    <button
                                      className="action-btn cancel-btn"
                                      onClick={() => handleCancelSubscription(sub.id)}
                                      title="Cancelar"
                                    >
                                      ❌
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      );
                    })()}
                  </div>

                  {/* Assign Subscription Modal */}
                  {showAssignSubscriptionModal && (
                    <div className="modal-overlay" onClick={() => setShowAssignSubscriptionModal(false)}>
                      <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setShowAssignSubscriptionModal(false)}>✕</button>
                        
                        <div className="modal-header">
                          <h2>Atribuir Subscrição</h2>
                        </div>

                        <div className="modal-body">
                          <form onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            handleAssignSubscription(
                              formData.get('userId'),
                              formData.get('planId'),
                              formData.get('startDate'),
                              formData.get('endDate') || null
                            );
                          }}>
                            {/* User Selection */}
                            <div className="form-group">
                              <label>Utilizador *</label>
                              <select name="userId" required>
                                <option value="">Selecione um utilizador...</option>
                                {users.filter(u => u.user_type === 'student').map(user => (
                                  <option key={user.id} value={user.id}>
                                    {user.full_name} ({user.email})
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Plan Selection - Grouped */}
                            <div className="form-group">
                              <label>Plano de Subscrição *</label>
                              <select name="planId" required>
                                <option value="">Selecione um plano...</option>
                                
                                <optgroup label="Yoga">
                                  {subscriptionPlans.filter(p => p.plan_group === 'yoga').map(plan => (
                                    <option key={plan.id} value={plan.id}>
                                      {plan.display_name}
                                    </option>
                                  ))}
                                </optgroup>
                                
                                <optgroup label="Meditação">
                                  {subscriptionPlans.filter(p => p.plan_group === 'meditacao').map(plan => (
                                    <option key={plan.id} value={plan.id}>
                                      {plan.display_name}
                                    </option>
                                  ))}
                                </optgroup>
                                
                                <optgroup label="Packs">
                                  {subscriptionPlans.filter(p => p.plan_group === 'pack').map(plan => (
                                    <option key={plan.id} value={plan.id}>
                                      {plan.display_name}
                                    </option>
                                  ))}
                                </optgroup>
                                
                                <optgroup label="Gravações">
                                  {subscriptionPlans.filter(p => p.plan_group === 'gravacoes').map(plan => (
                                    <option key={plan.id} value={plan.id}>
                                      {plan.display_name}
                                    </option>
                                  ))}
                                </optgroup>
                              </select>
                            </div>

                            {/* Start Date */}
                            <div className="form-group">
                              <label>Data de Início *</label>
                              <input
                                type="date"
                                name="startDate"
                                defaultValue={new Date().toISOString().split('T')[0]}
                                required
                              />
                            </div>

                            {/* End Date (optional) */}
                            <div className="form-group">
                              <label>Data de Fim (opcional)</label>
                              <input
                                type="date"
                                name="endDate"
                              />
                              <small style={{ color: '#666', fontSize: '0.85rem' }}>
                                💡 Para packs, a data será calculada automaticamente. Deixe em branco para subscrições sem fim.
                              </small>
                            </div>

                            {/* Submit Buttons */}
                        <div className="modal-footer">
                              <button type="submit" className="save-profile-btn">
                                Atribuir Subscrição
                              </button>
                              <button 
                                type="button" 
                                className="cancel-btn" 
                                onClick={() => setShowAssignSubscriptionModal(false)}
                              >
                                Cancelar
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentView === 'reports' && (
                <div className="reports-view">
                  <h2>Relatórios e Analytics</h2>
                  <div className="reports-grid">
                    <div className="report-card">
                      <h4>Reservas por Mês</h4>
                      <p>Gráfico de reservas</p>
                    </div>
                    <div className="report-card">
                      <h4>Aulas Mais Populares</h4>
                      <p>Yôga: 45%, Pilates: 30%</p>
                    </div>
                  </div>
                </div>
              )}

              {currentView === 'bookings' && (
                <div className="bookings-view">
                  <h2>Calendário de Aulas</h2>
                  <div className="bookings-container">
                    {/* Calendar (Monthly) */}
                    <div className="calendar">
                      <div className="calendar-header">
                        <button className="nav-btn" onClick={() => setSelectedDate(toIsoDate(new Date(new Date(selectedDate).setMonth(new Date(selectedDate).getMonth() - 1))))}>‹</button>
                        <h3>
                          {new Date(selectedDate).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}
                        </h3>
                        <button className="nav-btn" onClick={() => setSelectedDate(toIsoDate(new Date(new Date(selectedDate).setMonth(new Date(selectedDate).getMonth() + 1))))}>›</button>
                      </div>
                      <div className="calendar-weekdays">
                        {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((w) => (
                          <div key={w} className="weekday">{w}</div>
                        ))}
                      </div>
                      <div className="calendar-grid">
                        {(() => {
                          const { days, month } = getMonthCalendarDays(selectedDate);
                          const todayIso = toIsoDate(new Date());
                          const selectedIso = selectedDate;
                          return days.map((d) => {
                            const iso = toIsoDate(d);
                            const isOtherMonth = d.getMonth() !== month;
                            const isToday = iso === todayIso;
                            const isSelected = iso === selectedIso;
                            const sessionsForDay = getSessionsForDateIso(iso);
                            const isPast = iso < todayIso;
                            return (
                              <button
                                key={iso + Math.random()}
                                className={`calendar-cell ${isOtherMonth ? 'other' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isPast ? 'disabled' : ''}`}
                                onClick={() => { if (!isPast) setSelectedDate(iso); }}
                              >
                                <span className="date-num">{d.getDate()}</span>
                                {sessionsForDay.length > 0 && (
                                  <div className="calendar-preview">
                                    {sessionsForDay.slice(0, 1).map((s) => (
                                      <div key={s.id} className="preview-item">
                                        <span className="time">{s.start_time.substring(0,5)}</span>
                                        <span className="name">{s.classes?.name || 'Aula'}</span>
                                      </div>
                                    ))}
                                    {sessionsForDay.length > 1 && (
                                      <div className="preview-more">+{sessionsForDay.length - 1}</div>
                                    )}
                                  </div>
                                )}
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>

                    {/* Sessions for Selected Day */}
                    <div className="daily-sessions">
                      <div className="day-sessions">
                        {getSessionsForDateIso(selectedDate).length > 0 ? (
                          getSessionsForDateIso(selectedDate).map(session => (
                              <div key={session.id} className="session-card">
                                <div className="session-time">
                                  {`${session.start_time.substring(0,5)}-${session.end_time.substring(0,5)}  `}
                                </div>
                                <div className="session-class">
                                  <div className="class-name-row">
                                    <strong>{session.classes?.name || 'Aula'}</strong>
                                    {session.notes && session.notes.includes('NEW') && (
                                      <span className="new-badge">NEW</span>
                                    )}
                                  </div>
                                  <div className="session-teacher">
                                    {session.notes ? session.notes.replace(/ - (Presencial|Online|NEW).*$/, '') : ''}
                                  </div>
                                </div>
                                <div className="session-details">
                                  <span className="duration">{session.classes?.duration_minutes} min</span>
                                  {session.is_online && <span className="online">ONLINE</span>}
                                  {!session.is_online && <span className="presencial">PRESENCIAL</span>}
                                </div>
                                
                                <button 
                                  className="book-session-btn"
                                  onClick={() => openClassModal(
                                    session.id,
                                    session.classes?.name,
                                    {
                                      description: session.classes?.description,
                                      teacher: session.notes,
                                      location: session.is_online ? 'Online (Zoom)' : 'Sala 1',
                                      isOnline: session.is_online,
                                      zoomLink: 'https://zoom.us/j/123456789'
                                    }
                                  )}
                                >
                                  Ver Inscritos
                                </button>
                              </div>
                            ))
                        ) : (
                          <div className="no-sessions">
                            <p>Sem aulas para esta data</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Class Details Modal */}
                    {showClassModal && (
                      <div className="modal-overlay" onClick={closeClassModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                          <button className="modal-close" onClick={closeClassModal}>✕</button>
                          
                          <div className="modal-header">
                            <h2>{classDetails.name}</h2>
                            <div className="class-badge">
                              {classDetails.isOnline ? <span className="online-badge">🔗 ONLINE</span> : <span className="in-person-badge">📍 PRESENCIAL</span>}
                            </div>
                          </div>

                          <div className="modal-body">
                            {/* Class Information */}
                            <div className="class-info-section">
                              <h3>Informações da Aula</h3>
                              <div className="info-grid">
                                <div className="info-item">
                                  <strong>Professor:</strong>
                                  <p>{classDetails.teacher}</p>
                                </div>
                                <div className="info-item">
                                  <strong>Local:</strong>
                                  <p>{classDetails.location}</p>
                                </div>
                                {classDetails.isOnline && (
                                  <div className="info-item">
                                    <strong>Link de Zoom:</strong>
                                    <a href={classDetails.zoomLink} target="_blank" rel="noopener noreferrer">{classDetails.zoomLink}</a>
                                  </div>
                                )}
                              </div>
                              <div className="info-item full-width">
                                <strong>Descrição:</strong>
                                <p>{classDetails.description}</p>
                              </div>
                            </div>

                            {/* Booked Students */}
                            <div className="booked-students-modal-section">
                              <h3>Yogis Inscritos ({classDetails.bookedStudents.length})</h3>
                              <div className="modal-students-avatars">
                                {classDetails.bookedStudents.length > 0 ? (
                                  classDetails.bookedStudents.map((student, index) => (
                                    <div key={index} className="modal-student-item">
                                      <div className="modal-student-avatar">
                                        {student.picture ? (
                                          <img 
                                            src={student.picture} 
                                            alt={student.name}
                                            className="student-picture"
                                          />
                                        ) : (
                                          <div className="student-avatar-placeholder">{student.initials}</div>
                                        )}
                                      </div>
                                      <span className="modal-student-name">{student.name}</span>
                                    </div>
                                  ))
                                ) : (
                                  <p style={{ color: '#6c757d', fontStyle: 'italic' }}>Nenhum yogi inscrito ainda</p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="modal-footer">
                            <button className="cancel-btn" onClick={closeClassModal}>Fechar</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;